import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { getCountries, getCountryCallingCode, getExampleNumber, AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js/min';
import phoneExamples from 'libphonenumber-js/examples.mobile.json';
import { z } from 'zod';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// src/utils/index.ts

// src/constants/reservationIssue.ts
var RESERVATION_ISSUE_CATEGORY = {
  /** Pre-start withdrawal after acknowledgement — auto-releases the assignment. */
  CANNOT_DRIVE: "cannot_drive",
  /** Passenger never appeared at/after the effective pickup — routes to the operator. */
  NO_SHOW: "no_show",
  /** Safety or incident report. */
  INCIDENT: "incident",
  /** General operational report. */
  OTHER: "other"
};
var RESERVATION_ISSUE_CATEGORIES = Object.values(
  RESERVATION_ISSUE_CATEGORY
);
var RESERVATION_ISSUE_SEVERITY = {
  URGENT: "urgent",
  HIGH: "high",
  NORMAL: "normal"
};
var RESERVATION_ISSUE_STATUS = {
  OPEN: "open",
  ACKNOWLEDGED: "acknowledged",
  RESOLVED: "resolved"
};

// src/constants/index.ts
var COMPANY_ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  DRIVER: "driver"
};
var RESERVATION_STATUS = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  ASSIGNED: "assigned",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  CANCELLED: "cancelled"
};
var PICKUP_MODE = {
  EXPLICIT: "explicit",
  FLIGHT_TRACKED: "flight_tracked"
};
var NOT_OVERDUE_ELIGIBLE_STATUSES = [
  RESERVATION_STATUS.IN_PROGRESS,
  RESERVATION_STATUS.COMPLETED,
  RESERVATION_STATUS.CANCELLED
];
var OVERDUE_ELIGIBLE_STATUSES = Object.values(
  RESERVATION_STATUS
).filter((status) => !NOT_OVERDUE_ELIGIBLE_STATUSES.includes(status));
new Set(
  OVERDUE_ELIGIBLE_STATUSES
);
OVERDUE_ELIGIBLE_STATUSES.join(",");
var COMPANY_DECLINED_CANCELLATION_REASON = "company_declined";
var RESERVATION_CANCELLATION_OUTCOME = {
  CANCELLED: "cancelled",
  DECLINED: "declined"
};
function getReservationStage(input) {
  if (input.status === RESERVATION_STATUS.ASSIGNED) {
    return input.driverAcceptedAt ? "driver_confirmed" : "awaiting_driver";
  }
  return input.status;
}
var COMPLETION_OUTCOME = {
  NORMAL: "normal",
  NO_SHOW: "no_show",
  INCIDENT: "incident"
};
var PAYMENT_STATUS = {
  UNPAID: "unpaid",
  PAID: "paid",
  PARTIAL: "partial"
};
var PAYMENT_METHODS = {
  CASH: "cash",
  VENMO: "venmo",
  ZELLE: "zelle",
  PAYPAL: "paypal",
  CHECK: "check",
  BANK_TRANSFER: "bank_transfer",
  CREDIT_CARD: "credit_card",
  OTHER: "other"};
var PAYMENT_REQUIREMENT = {
  PAY_AT_PICKUP: "pay_at_pickup",
  ONLINE_UPFRONT: "online_upfront"
};
var RIDE_TYPES = {
  POINT_TO_POINT: "point-to-point",
  HOURLY: "hourly",
  CUSTOM: "custom"
};
var RIDE_SUB_TYPES = {
  ARRIVAL: "arrival",
  DEPARTURE: "departure"
};
var DOCUMENT_TYPES = {
  DRIVER_LICENSE: "driver_license",
  INSURANCE: "insurance",
  VEHICLE_REGISTRATION: "vehicle_registration",
  OTHER: "other"
};
var DOCUMENT_ACCESS_STATUS = {
  PENDING: "pending",
  GRANTED: "granted",
  DENIED: "denied",
  REVOKED: "revoked"
};
var DOCUMENT_REVIEW_STATUS = {
  APPROVED: "approved",
  REJECTED: "rejected"
};
var DRIVER_APPROVAL_STATUS = {
  PENDING_DOCUMENTS: "pending_documents",
  DOCUMENTS_RECEIVED: "documents_received",
  APPROVED: "approved",
  REJECTED: "rejected",
  MORE_INFO_NEEDED: "more_info_needed"
};
var FLEET_SIZES = {
  ONE: "1",
  TWO_TO_FIVE: "2-5",
  SIX_TO_TEN: "6-10",
  ELEVEN_TO_TWENTYFIVE: "11-25",
  TWENTYSIX_PLUS: "26+"
};
var FLIGHT_STATUS = {
  SCHEDULED: "scheduled",
  DELAYED: "delayed",
  DEPARTED: "departed"};
var AVAILABILITY_STATUS = {
  AVAILABLE: "available",
  UNAVAILABLE: "unavailable",
  BUSY: "busy"
};
var ERROR_CODES = {
  RESOURCE_ALREADY_EXISTS: "RESOURCE_ALREADY_EXISTS"};
var CONFLICT_REASONS = {
  /**
   * The write lost an optimistic-concurrency check: the reservation changed
   * (a different version, a different assignment generation, or an
   * `expectedUpdatedAt` that no longer matches) between read and write. The
   * client's remedy is to reload and reapply, never to retry blindly.
   */
  RESERVATION_VERSION_CONFLICT: "reservation_version_conflict"};
var HTTP_STATUS = {
  CONFLICT: 409};
var INQUIRY_STATUS = {
  NEW: "new",
  CONTACTED: "contacted",
  CONVERTED: "converted",
  CLOSED: "closed"
};
var DEFAULT_BRANDING_PRIMARY_COLOR = "#18794B";
var KNOWN_DEFAULT_BRANDING_COLORS = [
  DEFAULT_BRANDING_PRIMARY_COLOR,
  // Retired bootstrap blue — the business onboarding path's original default.
  "#007bff",
  // Retired solo blue — what the solo-driver and driver-plan provisioning
  // paths wrote before they were routed through the canonical default.
  "#2563eb"
];
new Set(
  KNOWN_DEFAULT_BRANDING_COLORS.map((hex) => hex.toLowerCase())
);
var INQUIRY_PLAN_OPTIONS = {
  BUSINESS: "business",
  BUSINESS_PLUS: "business_plus",
  BUSINESS_ULTIMATE: "business_ultimate",
  HELP_ME_CHOOSE: "help_me_choose"
};
var INQUIRY_SOURCE = {
  REGISTRATION: "registration_inquiry",
  HOMEPAGE_DEMO: "homepage_demo"
};

// src/utils/effectivePickup.ts
function resolveEffectivePickupAt(input) {
  return input.pickupMode === PICKUP_MODE.FLIGHT_TRACKED ? input.derivedPickupAt ?? input.pickupDatetime : input.pickupDatetime;
}
function effectivePickupDate(input) {
  const value = resolveEffectivePickupAt(input);
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
function effectivePickupInstant(ride) {
  return ride.effectivePickupAt ?? ride.pickupDatetime;
}
function toIsoOrNull(value) {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
function deriveFlightTrackedPickupAt(flight) {
  const actual = toIsoOrNull(flight.actualArrival);
  if (actual) return actual;
  return toIsoOrNull(flight.estimatedArrival) ?? toIsoOrNull(flight.scheduledArrival);
}

// src/utils/phoneCountryNames.ts
var PHONE_COUNTRY_NAMES = {
  AC: "Ascension Island",
  AD: "Andorra",
  AE: "United Arab Emirates",
  AF: "Afghanistan",
  AG: "Antigua & Barbuda",
  AI: "Anguilla",
  AL: "Albania",
  AM: "Armenia",
  AO: "Angola",
  AR: "Argentina",
  AS: "American Samoa",
  AT: "Austria",
  AU: "Australia",
  AW: "Aruba",
  AX: "\xC5land Islands",
  AZ: "Azerbaijan",
  BA: "Bosnia & Herzegovina",
  BB: "Barbados",
  BD: "Bangladesh",
  BE: "Belgium",
  BF: "Burkina Faso",
  BG: "Bulgaria",
  BH: "Bahrain",
  BI: "Burundi",
  BJ: "Benin",
  BL: "St. Barth\xE9lemy",
  BM: "Bermuda",
  BN: "Brunei",
  BO: "Bolivia",
  BQ: "Caribbean Netherlands",
  BR: "Brazil",
  BS: "Bahamas",
  BT: "Bhutan",
  BW: "Botswana",
  BY: "Belarus",
  BZ: "Belize",
  CA: "Canada",
  CC: "Cocos (Keeling) Islands",
  CD: "Congo - Kinshasa",
  CF: "Central African Republic",
  CG: "Congo - Brazzaville",
  CH: "Switzerland",
  CI: "C\xF4te d\u2019Ivoire",
  CK: "Cook Islands",
  CL: "Chile",
  CM: "Cameroon",
  CN: "China",
  CO: "Colombia",
  CR: "Costa Rica",
  CU: "Cuba",
  CV: "Cape Verde",
  CW: "Cura\xE7ao",
  CX: "Christmas Island",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DJ: "Djibouti",
  DK: "Denmark",
  DM: "Dominica",
  DO: "Dominican Republic",
  DZ: "Algeria",
  EC: "Ecuador",
  EE: "Estonia",
  EG: "Egypt",
  EH: "Western Sahara",
  ER: "Eritrea",
  ES: "Spain",
  ET: "Ethiopia",
  FI: "Finland",
  FJ: "Fiji",
  FK: "Falkland Islands",
  FM: "Micronesia",
  FO: "Faroe Islands",
  FR: "France",
  GA: "Gabon",
  GB: "United Kingdom",
  GD: "Grenada",
  GE: "Georgia",
  GF: "French Guiana",
  GG: "Guernsey",
  GH: "Ghana",
  GI: "Gibraltar",
  GL: "Greenland",
  GM: "Gambia",
  GN: "Guinea",
  GP: "Guadeloupe",
  GQ: "Equatorial Guinea",
  GR: "Greece",
  GT: "Guatemala",
  GU: "Guam",
  GW: "Guinea-Bissau",
  GY: "Guyana",
  HK: "Hong Kong SAR China",
  HN: "Honduras",
  HR: "Croatia",
  HT: "Haiti",
  HU: "Hungary",
  ID: "Indonesia",
  IE: "Ireland",
  IL: "Israel",
  IM: "Isle of Man",
  IN: "India",
  IO: "British Indian Ocean Territory",
  IQ: "Iraq",
  IR: "Iran",
  IS: "Iceland",
  IT: "Italy",
  JE: "Jersey",
  JM: "Jamaica",
  JO: "Jordan",
  JP: "Japan",
  KE: "Kenya",
  KG: "Kyrgyzstan",
  KH: "Cambodia",
  KI: "Kiribati",
  KM: "Comoros",
  KN: "St. Kitts & Nevis",
  KP: "North Korea",
  KR: "South Korea",
  KW: "Kuwait",
  KY: "Cayman Islands",
  KZ: "Kazakhstan",
  LA: "Laos",
  LB: "Lebanon",
  LC: "St. Lucia",
  LI: "Liechtenstein",
  LK: "Sri Lanka",
  LR: "Liberia",
  LS: "Lesotho",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  LY: "Libya",
  MA: "Morocco",
  MC: "Monaco",
  MD: "Moldova",
  ME: "Montenegro",
  MF: "St. Martin",
  MG: "Madagascar",
  MH: "Marshall Islands",
  MK: "North Macedonia",
  ML: "Mali",
  MM: "Myanmar (Burma)",
  MN: "Mongolia",
  MO: "Macao SAR China",
  MP: "Northern Mariana Islands",
  MQ: "Martinique",
  MR: "Mauritania",
  MS: "Montserrat",
  MT: "Malta",
  MU: "Mauritius",
  MV: "Maldives",
  MW: "Malawi",
  MX: "Mexico",
  MY: "Malaysia",
  MZ: "Mozambique",
  NA: "Namibia",
  NC: "New Caledonia",
  NE: "Niger",
  NF: "Norfolk Island",
  NG: "Nigeria",
  NI: "Nicaragua",
  NL: "Netherlands",
  NO: "Norway",
  NP: "Nepal",
  NR: "Nauru",
  NU: "Niue",
  NZ: "New Zealand",
  OM: "Oman",
  PA: "Panama",
  PE: "Peru",
  PF: "French Polynesia",
  PG: "Papua New Guinea",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Poland",
  PM: "St. Pierre & Miquelon",
  PR: "Puerto Rico",
  PS: "Palestinian Territories",
  PT: "Portugal",
  PW: "Palau",
  PY: "Paraguay",
  QA: "Qatar",
  RE: "R\xE9union",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russia",
  RW: "Rwanda",
  SA: "Saudi Arabia",
  SB: "Solomon Islands",
  SC: "Seychelles",
  SD: "Sudan",
  SE: "Sweden",
  SG: "Singapore",
  SH: "St. Helena",
  SI: "Slovenia",
  SJ: "Svalbard & Jan Mayen",
  SK: "Slovakia",
  SL: "Sierra Leone",
  SM: "San Marino",
  SN: "Senegal",
  SO: "Somalia",
  SR: "Suriname",
  SS: "South Sudan",
  ST: "S\xE3o Tom\xE9 & Pr\xEDncipe",
  SV: "El Salvador",
  SX: "Sint Maarten",
  SY: "Syria",
  SZ: "Eswatini",
  TA: "Tristan da Cunha",
  TC: "Turks & Caicos Islands",
  TD: "Chad",
  TG: "Togo",
  TH: "Thailand",
  TJ: "Tajikistan",
  TK: "Tokelau",
  TL: "Timor-Leste",
  TM: "Turkmenistan",
  TN: "Tunisia",
  TO: "Tonga",
  TR: "T\xFCrkiye",
  TT: "Trinidad & Tobago",
  TV: "Tuvalu",
  TW: "Taiwan",
  TZ: "Tanzania",
  UA: "Ukraine",
  UG: "Uganda",
  US: "United States",
  UY: "Uruguay",
  UZ: "Uzbekistan",
  VA: "Vatican City",
  VC: "St. Vincent & Grenadines",
  VE: "Venezuela",
  VG: "British Virgin Islands",
  VI: "U.S. Virgin Islands",
  VN: "Vietnam",
  VU: "Vanuatu",
  WF: "Wallis & Futuna",
  WS: "Samoa",
  XK: "Kosovo",
  YE: "Yemen",
  YT: "Mayotte",
  ZA: "South Africa",
  ZM: "Zambia",
  ZW: "Zimbabwe"
};
var PHONE_COUNTRY_SEARCH_ALIASES = {
  AE: ["UAE"],
  CD: ["Democratic Republic of the Congo", "Democratic Republic of Congo", "DRC", "Zaire"],
  CG: ["Republic of the Congo", "Republic of Congo"],
  CI: ["Ivory Coast"],
  CV: ["Cabo Verde"],
  CZ: ["Czech Republic"],
  GB: ["UK", "Great Britain", "England", "Scotland", "Wales", "Northern Ireland"],
  KP: ["DPRK", "Democratic People's Republic of Korea"],
  KR: ["Republic of Korea"],
  MO: ["Macau"],
  NL: ["Holland"],
  PS: ["Palestine"],
  SX: ["Saint Maarten"],
  SZ: ["Swaziland"],
  TL: ["East Timor"],
  TR: ["Turkey"],
  US: ["USA", "United States of America", "America"],
  VA: ["Holy See"]
};
var DIACRITIC_FOLD_GROUPS = [
  ["a", "\xE0\xE1\xE2\xE3\xE4\xE5\u0101\u0103\u0105\u1EA1\u1EA3\u1EA5\u1EA7\u1EA9\u1EAB\u1EAD\u1EAF\u1EB1\u1EB3\u1EB5\u1EB7"],
  ["c", "\xE7\u0107\u0109\u010B\u010D"],
  ["d", "\u010F\u0111\xF0"],
  ["e", "\xE8\xE9\xEA\xEB\u0113\u0115\u0117\u0119\u011B\u1EB9\u1EBB\u1EBD\u1EBF\u1EC1\u1EC3\u1EC5\u1EC7"],
  ["g", "\u011D\u011F\u0121\u0123"],
  ["h", "\u0125\u0127"],
  ["i", "\xEC\xED\xEE\xEF\u0129\u012B\u012D\u012F\u0131\u1ECB\u1EC9"],
  ["j", "\u0135"],
  ["k", "\u0137"],
  ["l", "\u013A\u013C\u013E\u0140\u0142"],
  ["n", "\xF1\u0144\u0146\u0148\u014B"],
  ["o", "\xF2\xF3\xF4\xF5\xF6\xF8\u014D\u014F\u0151\u1ECD\u1ECF\u1ED1\u1ED3\u1ED5\u1ED7\u1ED9\u1EDB\u1EDD\u1EDF\u1EE1\u1EE3"],
  ["r", "\u0155\u0157\u0159"],
  ["s", "\u015B\u015D\u015F\u0161\u0219"],
  ["t", "\u0163\u0165\u0167\u021B"],
  ["u", "\xF9\xFA\xFB\xFC\u0169\u016B\u016D\u016F\u0171\u0173\u1EE5\u1EE7\u1EE9\u1EEB\u1EED\u1EEF\u1EF1"],
  ["w", "\u0175"],
  ["y", "\xFD\xFF\u0177\u1EF3\u1EF7\u1EF9\u1EF5"],
  ["z", "\u017A\u017C\u017E"],
  ["ae", "\xE6"],
  ["oe", "\u0153"],
  ["ss", "\xDF"],
  ["th", "\xFE"]
];
var DIACRITIC_FOLD_MAP = Object.fromEntries(
  DIACRITIC_FOLD_GROUPS.flatMap(
    ([plain, accented]) => Array.from(accented, (character) => [character, plain])
  )
);
function foldCountrySearchText(value) {
  let folded = "";
  for (const character of value.toLowerCase()) {
    const plain = DIACRITIC_FOLD_MAP[character] ?? character;
    for (const letter of plain) {
      if (letter >= "a" && letter <= "z" || letter >= "0" && letter <= "9") {
        folded += letter;
      }
    }
  }
  return folded.split("saint").join("st");
}
var PHONE_PUNCTUATION = ` 	
\r+()-./\\,'"`;
function isPhonePunctuationOnly(value) {
  for (const character of value) {
    if (!PHONE_PUNCTUATION.includes(character)) return false;
  }
  return true;
}
function stripLeadingArticle(value) {
  return value.replace(/^the\s+/i, "");
}

// src/utils/zonedTime.ts
var DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
var TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?$/;
var OFFSET_PROBE_MS = 36 * 60 * 60 * 1e3;
var NONEXISTENT_WALL_TIME_MESSAGE = "That time doesn't exist on this date \u2014 the clocks jump forward an hour. Pick another time.";
function parseWallDate(date) {
  if (!DATE_RE.test(date)) return null;
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));
  const probe = new Date(Date.UTC(y, m - 1, d));
  const real = probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
  return real ? { y, m, d } : null;
}
function parseWallTimeParts(time) {
  if (!TIME_RE.test(time)) return null;
  return {
    h: Number(time.slice(0, 2)),
    min: Number(time.slice(3, 5)),
    s: time.length > 5 ? Number(time.slice(6, 8)) : 0,
    ms: time.length > 8 ? Number(time.slice(9).padEnd(3, "0")) : 0
  };
}
function toInstant(instant) {
  const d = typeof instant === "string" ? new Date(instant) : instant;
  return Number.isFinite(d.getTime()) ? d : null;
}
function zoneOffsetMinutes(instant, timeZone) {
  const parts = readZonedParts(instant, timeZone);
  if (!parts) return null;
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return Math.round((asUtc - instant.getTime()) / 6e4);
}
var zonedPartsFormatters = /* @__PURE__ */ new Map();
function zonedPartsFormatter(timeZone) {
  let formatter = zonedPartsFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23"
    });
    zonedPartsFormatters.set(timeZone, formatter);
  }
  return formatter;
}
function readZonedParts(instant, timeZone) {
  if (!timeZone || !Number.isFinite(instant.getTime())) return null;
  try {
    const parts = zonedPartsFormatter(timeZone).formatToParts(instant);
    const read = (type) => Number(parts.find((p) => p.type === type)?.value ?? "NaN");
    const fields = {
      year: read("year"),
      month: read("month"),
      day: read("day"),
      hour: read("hour"),
      minute: read("minute"),
      second: read("second")
    };
    return Object.values(fields).some(Number.isNaN) ? null : fields;
  } catch {
    return null;
  }
}
function instantToWallTime(instant, timeZone) {
  const at = toInstant(instant);
  if (!at) return null;
  const parts = readZonedParts(at, timeZone);
  if (!parts) return null;
  const pad = (n, width = 2) => String(n).padStart(width, "0");
  return {
    date: `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}`,
    time: `${pad(parts.hour)}:${pad(parts.minute)}`
  };
}
function wallTimeToInstant(date, time, timeZone, options = {}) {
  const { nonexistent = "reject", ambiguous = "earlier" } = options;
  const day = parseWallDate(date);
  const parts = parseWallTimeParts(time);
  if (!day || !parts) return null;
  const naiveUtcMs = Date.UTC(day.y, day.m - 1, day.d, parts.h, parts.min, parts.s, parts.ms);
  const offsets = /* @__PURE__ */ new Set();
  for (const probeMs of [naiveUtcMs - OFFSET_PROBE_MS, naiveUtcMs, naiveUtcMs + OFFSET_PROBE_MS]) {
    const offset = zoneOffsetMinutes(new Date(probeMs), timeZone);
    if (offset !== null) offsets.add(offset);
  }
  if (offsets.size === 0) return null;
  const candidates = [...offsets].map((offset) => new Date(naiveUtcMs - offset * 6e4)).sort((a, b) => a.getTime() - b.getTime());
  const exact = candidates.filter((candidate) => {
    const rendered = instantToWallTime(candidate, timeZone);
    return rendered?.date === date && rendered.time === time.slice(0, 5);
  });
  const chosen = ambiguous === "later" ? exact.at(-1) : exact.at(0);
  if (chosen) return chosen;
  return nonexistent === "shiftForward" ? candidates.at(-1) ?? null : null;
}
function isNonexistentWallTime(date, time, timeZone) {
  return wallTimeToInstant(date, time, timeZone) === null && wallTimeToInstant(date, time, timeZone, { nonexistent: "shiftForward" }) !== null;
}
function nextCalendarDate(year, month, day) {
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
}
function startOfDayInZone(date, timeZone) {
  return wallTimeToInstant(date, "00:00:00.000", timeZone, { nonexistent: "shiftForward" });
}
function endOfDayInZone(date, timeZone) {
  const day = parseWallDate(date);
  if (!day) return null;
  const nextStart = startOfDayInZone(nextCalendarDate(day.y, day.m, day.d), timeZone);
  return nextStart ? new Date(nextStart.getTime() - 1) : null;
}
var displayFormatters = /* @__PURE__ */ new Map();
function formatInZone(instant, timeZone, options) {
  try {
    if (!timeZone) throw new RangeError(`Invalid time zone specified: ${timeZone}`);
    const key = `${timeZone}\0${JSON.stringify(options)}`;
    let formatter = displayFormatters.get(key);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat("en-US", { timeZone, ...options });
      displayFormatters.set(key, formatter);
    }
    return formatter.format(new Date(instant));
  } catch {
    return typeof instant === "string" ? instant : instant.toISOString();
  }
}
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(128, "Password must be at most 128 characters");

// src/data/airports.ts
var AIRPORTS = [
  // ── United States: Large/Medium Hubs ────────────────────────────────────────
  { iata: "ATL", name: "Hartsfield\u2013Jackson Atlanta", lat: 33.6407, lng: -84.4277, isUS: true },
  { iata: "LAX", name: "Los Angeles International", lat: 33.9416, lng: -118.4085, isUS: true },
  { iata: "ORD", name: "Chicago O'Hare International", lat: 41.9742, lng: -87.9073, isUS: true },
  { iata: "DFW", name: "Dallas/Fort Worth International", lat: 32.8998, lng: -97.0403, bbox: [-97.116, 32.807, -96.999, 32.99], isUS: true },
  { iata: "DEN", name: "Denver International", lat: 39.8561, lng: -104.6737, bbox: [-104.887, 39.812, -104.526, 39.936], isUS: true },
  { iata: "JFK", name: "New York John F. Kennedy", lat: 40.6413, lng: -73.7781, isUS: true },
  { iata: "SFO", name: "San Francisco International", lat: 37.6213, lng: -122.379, isUS: true },
  { iata: "SEA", name: "Seattle-Tacoma International", lat: 47.4502, lng: -122.3088, isUS: true },
  { iata: "LAS", name: "Harry Reid International (Las Vegas)", lat: 36.084, lng: -115.1537, isUS: true },
  { iata: "MCO", name: "Orlando International", lat: 28.4312, lng: -81.3081, isUS: true },
  { iata: "EWR", name: "Newark Liberty International", lat: 40.6895, lng: -74.1745, isUS: true },
  { iata: "PHX", name: "Phoenix Sky Harbor International", lat: 33.4373, lng: -112.0078, isUS: true },
  { iata: "IAH", name: "Houston George Bush Intercontinental", lat: 29.9902, lng: -95.3368, isUS: true },
  { iata: "CLT", name: "Charlotte Douglas International", lat: 35.2144, lng: -80.9473, isUS: true },
  { iata: "MIA", name: "Miami International", lat: 25.7959, lng: -80.287, isUS: true },
  { iata: "BOS", name: "Boston Logan International", lat: 42.3656, lng: -71.0096, isUS: true },
  { iata: "MSP", name: "Minneapolis-Saint Paul International", lat: 44.8848, lng: -93.2223, isUS: true },
  { iata: "DTW", name: "Detroit Metropolitan Wayne County", lat: 42.2162, lng: -83.3554, isUS: true },
  { iata: "FLL", name: "Fort Lauderdale-Hollywood International", lat: 26.0726, lng: -80.1527, isUS: true },
  { iata: "PHL", name: "Philadelphia International", lat: 39.8721, lng: -75.2437, isUS: true },
  { iata: "LGA", name: "New York LaGuardia", lat: 40.7769, lng: -73.874, isUS: true },
  { iata: "BWI", name: "Baltimore/Washington International", lat: 39.1754, lng: -76.6682, isUS: true },
  { iata: "SLC", name: "Salt Lake City International", lat: 40.7899, lng: -111.9791, isUS: true },
  { iata: "IAD", name: "Washington Dulles International", lat: 38.9531, lng: -77.4565, isUS: true },
  { iata: "DCA", name: "Ronald Reagan Washington National", lat: 38.8512, lng: -77.0402, isUS: true },
  { iata: "MDW", name: "Chicago Midway International", lat: 41.7868, lng: -87.7522, isUS: true },
  { iata: "TPA", name: "Tampa International", lat: 27.9755, lng: -82.5332, isUS: true },
  { iata: "PDX", name: "Portland International", lat: 45.5898, lng: -122.5951, isUS: true },
  { iata: "BNA", name: "Nashville International", lat: 36.1245, lng: -86.6782, isUS: true },
  { iata: "AUS", name: "Austin-Bergstrom International", lat: 30.1975, lng: -97.6664, isUS: true },
  { iata: "MSY", name: "Louis Armstrong New Orleans International", lat: 29.9934, lng: -90.258, isUS: true },
  { iata: "RDU", name: "Raleigh-Durham International", lat: 35.8776, lng: -78.7875, isUS: true },
  { iata: "SAN", name: "San Diego International", lat: 32.7338, lng: -117.1933, isUS: true },
  { iata: "HNL", name: "Daniel K. Inouye International (Honolulu)", lat: 21.3187, lng: -157.9224, isUS: true },
  { iata: "STL", name: "St. Louis Lambert International", lat: 38.7487, lng: -90.37, isUS: true },
  { iata: "DAL", name: "Dallas Love Field", lat: 32.8471, lng: -96.8517, isUS: true },
  { iata: "HOU", name: "William P. Hobby Airport (Houston)", lat: 29.6454, lng: -95.2789, isUS: true },
  { iata: "SAT", name: "San Antonio International", lat: 29.5337, lng: -98.4698, isUS: true },
  { iata: "OAK", name: "Oakland Metropolitan International", lat: 37.7213, lng: -122.2208, isUS: true },
  { iata: "SJC", name: "San Jos\xE9 Mineta International", lat: 37.3626, lng: -121.929, isUS: true },
  { iata: "SMF", name: "Sacramento International", lat: 38.6954, lng: -121.5908, isUS: true },
  { iata: "MCI", name: "Kansas City International", lat: 39.2976, lng: -94.7139, isUS: true },
  { iata: "ABQ", name: "Albuquerque International Sunport", lat: 35.0402, lng: -106.609, isUS: true },
  { iata: "CLE", name: "Cleveland Hopkins International", lat: 41.4117, lng: -81.8498, isUS: true },
  { iata: "CMH", name: "John Glenn Columbus International", lat: 39.998, lng: -82.8919, isUS: true },
  { iata: "IND", name: "Indianapolis International", lat: 39.7173, lng: -86.2944, isUS: true },
  { iata: "PIT", name: "Pittsburgh International", lat: 40.4915, lng: -80.2329, isUS: true },
  { iata: "CVG", name: "Cincinnati/Northern Kentucky International", lat: 39.0485, lng: -84.6678, isUS: true },
  { iata: "MKE", name: "Milwaukee Mitchell International", lat: 42.9472, lng: -87.8966, isUS: true },
  { iata: "MEM", name: "Memphis International", lat: 35.0421, lng: -89.9767, isUS: true },
  { iata: "OMA", name: "Eppley Airfield (Omaha)", lat: 41.3032, lng: -95.894, isUS: true },
  { iata: "BOI", name: "Boise Airport", lat: 43.5644, lng: -116.2228, isUS: true },
  { iata: "SDF", name: "Louisville Muhammad Ali International", lat: 38.1744, lng: -85.736, isUS: true },
  { iata: "LEX", name: "Blue Grass Airport (Lexington)", lat: 38.0365, lng: -84.606, isUS: true },
  { iata: "GRR", name: "Gerald R. Ford International (Grand Rapids)", lat: 42.8808, lng: -85.5228, isUS: true },
  { iata: "TYS", name: "McGhee Tyson Airport (Knoxville)", lat: 35.811, lng: -83.994, isUS: true },
  { iata: "OGG", name: "Kahului Airport (Maui)", lat: 20.8986, lng: -156.4305, isUS: true },
  { iata: "KOA", name: "Ellison Onizuka Kona International", lat: 19.7388, lng: -156.0456, isUS: true },
  { iata: "LIH", name: "Lihue Airport (Kauai)", lat: 21.976, lng: -159.3388, isUS: true },
  { iata: "ITO", name: "Hilo International", lat: 19.7213, lng: -155.0484, isUS: true },
  { iata: "ANC", name: "Ted Stevens Anchorage International", lat: 61.1741, lng: -149.9963, isUS: true },
  { iata: "FAI", name: "Fairbanks International", lat: 64.8151, lng: -147.8562, isUS: true },
  { iata: "JNU", name: "Juneau International", lat: 58.355, lng: -134.5763, isUS: true },
  { iata: "KTN", name: "Ketchikan International", lat: 55.3556, lng: -131.7137, isUS: true },
  // ── United States: Small Hubs / Large Non-Hub ───────────────────────────────
  { iata: "PVD", name: "T.F. Green Providence", lat: 41.7237, lng: -71.4282, isUS: true },
  { iata: "BUF", name: "Buffalo Niagara International", lat: 42.9405, lng: -78.7322, isUS: true },
  { iata: "ALB", name: "Albany International", lat: 42.7483, lng: -73.8017, isUS: true },
  { iata: "BDL", name: "Bradley International (Hartford)", lat: 41.9389, lng: -72.6832, isUS: true },
  { iata: "MHT", name: "Manchester-Boston Regional", lat: 42.9326, lng: -71.4357, isUS: true },
  { iata: "PWM", name: "Portland International Jetport (Maine)", lat: 43.6462, lng: -70.3093, isUS: true },
  { iata: "BGR", name: "Bangor International", lat: 44.8074, lng: -68.8281, isUS: true },
  { iata: "SYR", name: "Syracuse Hancock International", lat: 43.1112, lng: -76.1066, isUS: true },
  { iata: "ROC", name: "Greater Rochester International", lat: 43.1189, lng: -77.6724, isUS: true },
  { iata: "HPN", name: "Westchester County Airport", lat: 41.067, lng: -73.7076, isUS: true },
  { iata: "ISP", name: "Long Island MacArthur Airport", lat: 40.7952, lng: -73.1002, isUS: true },
  { iata: "ORF", name: "Norfolk International", lat: 36.8976, lng: -76.0133, isUS: true },
  { iata: "RIC", name: "Richmond International", lat: 37.5052, lng: -77.3197, isUS: true },
  { iata: "GSO", name: "Piedmont Triad International (Greensboro)", lat: 36.0978, lng: -79.9373, isUS: true },
  { iata: "GSP", name: "Greenville-Spartanburg International", lat: 34.8957, lng: -82.2189, isUS: true },
  { iata: "CAE", name: "Columbia Metropolitan", lat: 33.9389, lng: -81.1195, isUS: true },
  { iata: "CHS", name: "Charleston International", lat: 32.8987, lng: -80.0405, isUS: true },
  { iata: "SAV", name: "Savannah/Hilton Head International", lat: 32.1277, lng: -81.2021, isUS: true },
  { iata: "JAX", name: "Jacksonville International", lat: 30.4941, lng: -81.6879, isUS: true },
  { iata: "RSW", name: "Southwest Florida International (Fort Myers)", lat: 26.5362, lng: -81.7552, isUS: true },
  { iata: "PBI", name: "Palm Beach International", lat: 26.6832, lng: -80.0956, isUS: true },
  { iata: "SRQ", name: "Sarasota Bradenton International", lat: 27.3954, lng: -82.5543, isUS: true },
  { iata: "TUS", name: "Tucson International", lat: 32.1161, lng: -110.941, isUS: true },
  { iata: "ELP", name: "El Paso International", lat: 31.8072, lng: -106.3779, isUS: true },
  { iata: "LBB", name: "Lubbock Preston Smith International", lat: 33.6636, lng: -101.8228, isUS: true },
  { iata: "MAF", name: "Midland International Air & Space Port", lat: 31.9425, lng: -102.2019, isUS: true },
  { iata: "TUL", name: "Tulsa International", lat: 36.1984, lng: -95.8881, isUS: true },
  { iata: "OKC", name: "Will Rogers World Airport (Oklahoma City)", lat: 35.3931, lng: -97.6007, isUS: true },
  { iata: "XNA", name: "Northwest Arkansas National", lat: 36.2819, lng: -94.3068, isUS: true },
  { iata: "LIT", name: "Bill and Hillary Clinton National (Little Rock)", lat: 34.7294, lng: -92.2243, isUS: true },
  { iata: "SHV", name: "Shreveport Regional", lat: 32.4466, lng: -93.8256, isUS: true },
  { iata: "BTR", name: "Baton Rouge Metropolitan", lat: 30.5332, lng: -91.1496, isUS: true },
  { iata: "MSN", name: "Dane County Regional (Madison)", lat: 43.1399, lng: -89.3375, isUS: true },
  { iata: "DSM", name: "Des Moines International", lat: 41.534, lng: -93.6631, isUS: true },
  { iata: "MLI", name: "Quad City International", lat: 41.4485, lng: -90.5075, isUS: true },
  { iata: "CID", name: "The Eastern Iowa Airport (Cedar Rapids)", lat: 41.8842, lng: -91.7108, isUS: true },
  { iata: "FAR", name: "Hector International (Fargo)", lat: 46.9207, lng: -96.8158, isUS: true },
  { iata: "FSD", name: "Sioux Falls Regional (Joe Foss Field)", lat: 43.582, lng: -96.7419, isUS: true },
  { iata: "BIS", name: "Bismarck Municipal", lat: 46.7724, lng: -100.7463, isUS: true },
  { iata: "RAP", name: "Rapid City Regional", lat: 44.0453, lng: -103.0574, isUS: true },
  { iata: "ICT", name: "Dwight D. Eisenhower National (Wichita)", lat: 37.6499, lng: -97.4331, isUS: true },
  { iata: "SNA", name: "John Wayne Airport (Orange County)", lat: 33.6757, lng: -117.8682, isUS: true },
  { iata: "BUR", name: "Hollywood Burbank Airport", lat: 34.2006, lng: -118.3585, isUS: true },
  { iata: "LGB", name: "Long Beach Airport", lat: 33.8117, lng: -118.1516, isUS: true },
  { iata: "ONT", name: "Ontario International", lat: 34.056, lng: -117.6012, isUS: true },
  { iata: "PSP", name: "Palm Springs International", lat: 33.8297, lng: -116.5067, isUS: true },
  { iata: "FAT", name: "Fresno Yosemite International", lat: 36.7762, lng: -119.7182, isUS: true },
  { iata: "SBA", name: "Santa Barbara Municipal", lat: 34.4263, lng: -119.8403, isUS: true },
  { iata: "MRY", name: "Monterey Regional", lat: 36.587, lng: -121.8428, isUS: true },
  { iata: "RNO", name: "Reno-Tahoe International", lat: 39.4991, lng: -119.7681, isUS: true },
  { iata: "GEG", name: "Spokane International", lat: 47.6199, lng: -117.5338, isUS: true },
  { iata: "MFR", name: "Rogue Valley International (Medford)", lat: 42.3742, lng: -122.873, isUS: true },
  { iata: "EUG", name: "Eugene Airport", lat: 44.1246, lng: -123.2119, isUS: true },
  { iata: "RDD", name: "Redding Municipal", lat: 40.509, lng: -122.2932, isUS: true },
  { iata: "ACV", name: "California Redwood Coast\u2013Humboldt County", lat: 40.9782, lng: -124.1087, isUS: true },
  { iata: "GJT", name: "Grand Junction Regional", lat: 39.1224, lng: -108.5268, isUS: true },
  { iata: "COS", name: "Colorado Springs Airport", lat: 38.8058, lng: -104.7008, isUS: true },
  { iata: "MSO", name: "Missoula Montana Airport", lat: 46.9163, lng: -114.0906, isUS: true },
  { iata: "HLN", name: "Helena Regional", lat: 46.6068, lng: -111.9829, isUS: true },
  { iata: "GTF", name: "Great Falls International", lat: 47.482, lng: -111.3711, isUS: true },
  { iata: "BIL", name: "Billings Logan International", lat: 45.8077, lng: -108.5428, isUS: true },
  { iata: "CPR", name: "Casper/Natrona County International", lat: 42.908, lng: -106.4636, isUS: true },
  { iata: "PIH", name: "Pocatello Regional", lat: 42.9098, lng: -112.5958, isUS: true },
  { iata: "GFK", name: "Grand Forks International", lat: 47.9493, lng: -97.1761, isUS: true },
  { iata: "SJU", name: "Luis Mu\xF1oz Mar\xEDn International (San Juan, PR)", lat: 18.4394, lng: -66.0018, isUS: true },
  { iata: "BQN", name: "Rafael Hern\xE1ndez Airport (Aguadilla, PR)", lat: 18.4949, lng: -67.1289, isUS: true },
  { iata: "STT", name: "Cyril E. King Airport (St. Thomas, USVI)", lat: 18.3373, lng: -64.9733, isUS: true },
  { iata: "GUM", name: "Antonio B. Won Pat International (Guam)", lat: 13.4834, lng: 144.7963, isUS: true },
  // ── Canada ──────────────────────────────────────────────────────────────────
  { iata: "YVR", name: "Vancouver International", lat: 49.1967, lng: -123.1815, isUS: false },
  { iata: "YYZ", name: "Toronto Pearson International", lat: 43.6777, lng: -79.6248, isUS: false },
  { iata: "YUL", name: "Montr\xE9al-Trudeau International", lat: 45.4706, lng: -73.7408, isUS: false },
  { iata: "YYC", name: "Calgary International", lat: 51.1314, lng: -114.013, isUS: false },
  { iata: "YEG", name: "Edmonton International", lat: 53.3097, lng: -113.5797, isUS: false },
  { iata: "YOW", name: "Ottawa Macdonald\u2013Cartier International", lat: 45.3225, lng: -75.6692, isUS: false },
  { iata: "YHZ", name: "Halifax Stanfield International", lat: 44.8808, lng: -63.5086, isUS: false },
  // ── Mexico & Central America ─────────────────────────────────────────────────
  { iata: "MEX", name: "Mexico City International", lat: 19.4363, lng: -99.0721, isUS: false },
  { iata: "CUN", name: "Canc\xFAn International", lat: 21.0365, lng: -86.8771, isUS: false },
  { iata: "GDL", name: "Guadalajara Miguel Hidalgo International", lat: 20.5218, lng: -103.311, isUS: false },
  { iata: "MTY", name: "Monterrey International", lat: 25.7785, lng: -100.107, isUS: false },
  { iata: "PVR", name: "Puerto Vallarta International", lat: 20.6801, lng: -105.254, isUS: false },
  { iata: "SJD", name: "Los Cabos International", lat: 23.1518, lng: -109.721, isUS: false },
  { iata: "MZT", name: "General Rafael Buelna (Mazatl\xE1n)", lat: 23.1614, lng: -106.2661, isUS: false },
  { iata: "TIJ", name: "General Abelardo L. Rodr\xEDguez (Tijuana)", lat: 32.5411, lng: -116.97, isUS: false },
  { iata: "BJX", name: "Baj\xEDo International (Le\xF3n)", lat: 20.9935, lng: -101.4809, isUS: false },
  { iata: "GUA", name: "La Aurora International (Guatemala City)", lat: 14.5833, lng: -90.5275, isUS: false },
  { iata: "SJO", name: "Juan Santamar\xEDa International (San Jos\xE9, CR)", lat: 9.9939, lng: -84.2088, isUS: false },
  { iata: "PTY", name: "Tocumen International (Panama City)", lat: 9.0714, lng: -79.3835, isUS: false },
  // ── Caribbean ────────────────────────────────────────────────────────────────
  { iata: "MBJ", name: "Sangster International (Montego Bay)", lat: 18.5037, lng: -77.9134, isUS: false },
  { iata: "KIN", name: "Norman Manley International (Kingston)", lat: 17.9357, lng: -76.7875, isUS: false },
  { iata: "SDQ", name: "Las Am\xE9ricas International (Santo Domingo)", lat: 18.4297, lng: -69.6689, isUS: false },
  { iata: "PUJ", name: "Punta Cana International", lat: 18.5674, lng: -68.3638, isUS: false },
  { iata: "NAS", name: "Lynden Pindling International (Nassau)", lat: 25.0389, lng: -77.4662, isUS: false },
  { iata: "GCM", name: "Owen Roberts International (Grand Cayman)", lat: 19.2928, lng: -81.3577, isUS: false },
  { iata: "BGI", name: "Grantley Adams International (Bridgetown)", lat: 13.0746, lng: -59.4925, isUS: false },
  { iata: "SXM", name: "Princess Juliana International (St. Maarten)", lat: 18.041, lng: -63.1089, isUS: false },
  // ── South America ────────────────────────────────────────────────────────────
  { iata: "GRU", name: "S\xE3o Paulo\u2013Guarulhos International", lat: -23.4356, lng: -46.4731, isUS: false },
  { iata: "GIG", name: "Rio de Janeiro\u2013Gale\xE3o International", lat: -22.8099, lng: -43.2505, isUS: false },
  { iata: "BSB", name: "Presidente Juscelino Kubitschek (Bras\xEDlia)", lat: -15.8711, lng: -47.9186, isUS: false },
  { iata: "EZE", name: "Ministro Pistarini International (Buenos Aires)", lat: -34.8222, lng: -58.5358, isUS: false },
  { iata: "SCL", name: "Arturo Merino Ben\xEDtez International (Santiago)", lat: -33.393, lng: -70.7936, isUS: false },
  { iata: "BOG", name: "El Dorado International (Bogot\xE1)", lat: 4.7016, lng: -74.1469, isUS: false },
  { iata: "LIM", name: "Jorge Ch\xE1vez International (Lima)", lat: -12.0219, lng: -77.1143, isUS: false },
  { iata: "MVD", name: "Carrasco International (Montevideo)", lat: -34.8384, lng: -56.0308, isUS: false },
  { iata: "UIO", name: "Mariscal Sucre International (Quito)", lat: -0.1292, lng: -78.3575, isUS: false },
  // ── Europe ───────────────────────────────────────────────────────────────────
  { iata: "LHR", name: "London Heathrow", lat: 51.4775, lng: -0.4614, isUS: false },
  { iata: "LGW", name: "London Gatwick", lat: 51.1537, lng: -0.1821, isUS: false },
  { iata: "STN", name: "London Stansted", lat: 51.885, lng: 0.235, isUS: false },
  { iata: "MAN", name: "Manchester Airport", lat: 53.3537, lng: -2.275, isUS: false },
  { iata: "EDI", name: "Edinburgh Airport", lat: 55.95, lng: -3.3725, isUS: false },
  { iata: "CDG", name: "Paris Charles de Gaulle", lat: 49.0097, lng: 2.5479, isUS: false },
  { iata: "ORY", name: "Paris Orly", lat: 48.7233, lng: 2.3794, isUS: false },
  { iata: "AMS", name: "Amsterdam Schiphol", lat: 52.3086, lng: 4.7639, isUS: false },
  { iata: "FRA", name: "Frankfurt am Main", lat: 50.0333, lng: 8.5706, isUS: false },
  { iata: "MUC", name: "Munich International", lat: 48.3537, lng: 11.775, isUS: false },
  { iata: "BER", name: "Berlin Brandenburg International", lat: 52.3667, lng: 13.5033, isUS: false },
  { iata: "HAM", name: "Hamburg Airport", lat: 53.6304, lng: 9.9882, isUS: false },
  { iata: "DUS", name: "D\xFCsseldorf International", lat: 51.2895, lng: 6.7668, isUS: false },
  { iata: "ZRH", name: "Z\xFCrich Airport", lat: 47.4647, lng: 8.5492, isUS: false },
  { iata: "GVA", name: "Geneva Airport", lat: 46.237, lng: 6.1089, isUS: false },
  { iata: "VIE", name: "Vienna International", lat: 48.1102, lng: 16.5697, isUS: false },
  { iata: "BRU", name: "Brussels Airport", lat: 50.9014, lng: 4.4844, isUS: false },
  { iata: "CPH", name: "Copenhagen Airport", lat: 55.6181, lng: 12.656, isUS: false },
  { iata: "ARN", name: "Stockholm Arlanda", lat: 59.6519, lng: 17.9186, isUS: false },
  { iata: "OSL", name: "Oslo Gardermoen", lat: 60.1939, lng: 11.1, isUS: false },
  { iata: "HEL", name: "Helsinki-Vantaa", lat: 60.3172, lng: 24.9633, isUS: false },
  { iata: "LIS", name: "Lisbon Humberto Delgado", lat: 38.7813, lng: -9.1359, isUS: false },
  { iata: "MAD", name: "Adolfo Su\xE1rez Madrid\u2013Barajas", lat: 40.4719, lng: -3.5626, isUS: false },
  { iata: "BCN", name: "Barcelona\u2013El Prat", lat: 41.2971, lng: 2.0785, isUS: false },
  { iata: "FCO", name: "Rome Fiumicino", lat: 41.8003, lng: 12.2389, isUS: false },
  { iata: "MXP", name: "Milan Malpensa", lat: 45.6306, lng: 8.7281, isUS: false },
  { iata: "VCE", name: "Venice Marco Polo", lat: 45.5052, lng: 12.3519, isUS: false },
  { iata: "ATH", name: "Athens Eleftherios Venizelos", lat: 37.9364, lng: 23.9445, isUS: false },
  { iata: "PRG", name: "V\xE1clav Havel Prague", lat: 50.1008, lng: 14.26, isUS: false },
  { iata: "BUD", name: "Budapest Ferenc Liszt International", lat: 47.4298, lng: 19.2611, isUS: false },
  { iata: "WAW", name: "Warsaw Chopin", lat: 52.1657, lng: 20.9671, isUS: false },
  // ── Middle East ──────────────────────────────────────────────────────────────
  { iata: "DXB", name: "Dubai International", lat: 25.2528, lng: 55.3644, isUS: false },
  { iata: "DWC", name: "Al Maktoum International (Dubai)", lat: 24.8963, lng: 55.1612, isUS: false },
  { iata: "DOH", name: "Hamad International (Doha)", lat: 25.2731, lng: 51.6081, isUS: false },
  { iata: "AUH", name: "Abu Dhabi International", lat: 24.433, lng: 54.6511, isUS: false },
  { iata: "IST", name: "Istanbul Airport", lat: 41.2608, lng: 28.7418, isUS: false },
  { iata: "SAW", name: "Istanbul Sabiha G\xF6k\xE7en", lat: 40.8985, lng: 29.3092, isUS: false },
  { iata: "TLV", name: "Ben Gurion International (Tel Aviv)", lat: 32.0055, lng: 34.8854, isUS: false },
  { iata: "AMM", name: "Queen Alia International (Amman)", lat: 31.7226, lng: 35.9932, isUS: false },
  { iata: "CAI", name: "Cairo International", lat: 30.1219, lng: 31.4056, isUS: false },
  // ── Africa ───────────────────────────────────────────────────────────────────
  { iata: "JNB", name: "O.R. Tambo International (Johannesburg)", lat: -26.1392, lng: 28.246, isUS: false },
  { iata: "CPT", name: "Cape Town International", lat: -33.9648, lng: 18.6017, isUS: false },
  { iata: "NBO", name: "Jomo Kenyatta International (Nairobi)", lat: -1.3192, lng: 36.9275, isUS: false },
  { iata: "ADD", name: "Addis Ababa Bole International", lat: 8.9779, lng: 38.7993, isUS: false },
  { iata: "LOS", name: "Murtala Muhammed International (Lagos)", lat: 6.5774, lng: 3.3214, isUS: false },
  { iata: "CMN", name: "Mohammed V International (Casablanca)", lat: 33.3675, lng: -7.5897, isUS: false },
  // ── Asia–Pacific ─────────────────────────────────────────────────────────────
  { iata: "BOM", name: "Chhatrapati Shivaji Maharaj International (Mumbai)", lat: 19.0896, lng: 72.8656, isUS: false },
  { iata: "DEL", name: "Indira Gandhi International (New Delhi)", lat: 28.5562, lng: 77.1, isUS: false },
  { iata: "BLR", name: "Kempegowda International (Bengaluru)", lat: 13.1986, lng: 77.7066, isUS: false },
  { iata: "HYD", name: "Rajiv Gandhi International (Hyderabad)", lat: 17.2313, lng: 78.4298, isUS: false },
  { iata: "MAA", name: "Chennai International", lat: 12.99, lng: 80.1693, isUS: false },
  { iata: "CCU", name: "Netaji Subhas Chandra Bose International (Kolkata)", lat: 22.6545, lng: 88.4467, isUS: false },
  { iata: "SIN", name: "Singapore Changi", lat: 1.3644, lng: 103.9915, isUS: false },
  { iata: "KUL", name: "Kuala Lumpur International", lat: 2.7456, lng: 101.7099, isUS: false },
  { iata: "BKK", name: "Suvarnabhumi (Bangkok)", lat: 13.6811, lng: 100.7473, isUS: false },
  { iata: "DMK", name: "Don Mueang International (Bangkok)", lat: 13.9126, lng: 100.607, isUS: false },
  { iata: "CGK", name: "Soekarno\u2013Hatta International (Jakarta)", lat: -6.1256, lng: 106.6559, isUS: false },
  { iata: "DPS", name: "Ngurah Rai International (Bali)", lat: -8.7482, lng: 115.1675, isUS: false },
  { iata: "MNL", name: "Ninoy Aquino International (Manila)", lat: 14.5086, lng: 121.0197, isUS: false },
  { iata: "HAN", name: "Noi Bai International (Hanoi)", lat: 21.2212, lng: 105.8072, isUS: false },
  { iata: "SGN", name: "Tan Son Nhat International (Ho Chi Minh City)", lat: 10.8188, lng: 106.6519, isUS: false },
  { iata: "HKG", name: "Hong Kong International", lat: 22.308, lng: 113.9185, isUS: false },
  { iata: "TPE", name: "Taiwan Taoyuan International", lat: 25.0777, lng: 121.2327, isUS: false },
  { iata: "NRT", name: "Tokyo Narita International", lat: 35.7648, lng: 140.3864, isUS: false },
  { iata: "HND", name: "Tokyo Haneda International", lat: 35.5494, lng: 139.7798, isUS: false },
  { iata: "KIX", name: "Kansai International (Osaka)", lat: 34.4347, lng: 135.244, isUS: false },
  { iata: "NGO", name: "Chubu Centrair International (Nagoya)", lat: 34.8583, lng: 136.8049, isUS: false },
  { iata: "CTS", name: "New Chitose Airport (Sapporo)", lat: 42.7752, lng: 141.6922, isUS: false },
  { iata: "FUK", name: "Fukuoka Airport", lat: 33.5853, lng: 130.4511, isUS: false },
  { iata: "OKA", name: "Naha Airport (Okinawa)", lat: 26.1958, lng: 127.6461, isUS: false },
  { iata: "ICN", name: "Incheon International (Seoul)", lat: 37.4602, lng: 126.4407, isUS: false },
  { iata: "GMP", name: "Gimpo International (Seoul)", lat: 37.5583, lng: 126.7914, isUS: false },
  { iata: "PEK", name: "Beijing Capital International", lat: 40.0799, lng: 116.6031, isUS: false },
  { iata: "PKX", name: "Beijing Daxing International", lat: 39.5098, lng: 116.4112, isUS: false },
  { iata: "PVG", name: "Shanghai Pudong International", lat: 31.1443, lng: 121.8083, isUS: false },
  { iata: "SHA", name: "Shanghai Hongqiao International", lat: 31.1981, lng: 121.3365, isUS: false },
  { iata: "CAN", name: "Guangzhou Baiyun International", lat: 23.3924, lng: 113.299, isUS: false },
  { iata: "CTU", name: "Chengdu Tianfu International", lat: 30.3118, lng: 104.4447, isUS: false },
  // ── Oceania ──────────────────────────────────────────────────────────────────
  { iata: "SYD", name: "Sydney Kingsford Smith", lat: -33.9461, lng: 151.1772, isUS: false },
  { iata: "MEL", name: "Melbourne Airport", lat: -37.669, lng: 144.841, isUS: false },
  { iata: "BNE", name: "Brisbane Airport", lat: -27.3842, lng: 153.1175, isUS: false },
  { iata: "PER", name: "Perth Airport", lat: -31.9403, lng: 115.9669, isUS: false },
  { iata: "ADL", name: "Adelaide Airport", lat: -34.945, lng: 138.53, isUS: false },
  { iata: "OOL", name: "Gold Coast Airport", lat: -28.1644, lng: 153.505, isUS: false },
  { iata: "CNS", name: "Cairns Airport", lat: -16.8858, lng: 145.755, isUS: false },
  { iata: "AKL", name: "Auckland Airport", lat: -37.0082, lng: 174.7917, isUS: false },
  { iata: "WLG", name: "Wellington International", lat: -41.3272, lng: 174.8052, isUS: false },
  { iata: "CHC", name: "Christchurch International", lat: -43.4894, lng: 172.5319, isUS: false },
  { iata: "ZQN", name: "Queenstown Airport", lat: -45.0211, lng: 168.7389, isUS: false }
];
var AIRPORT_IATA_SET = new Set(AIRPORTS.map((a) => a.iata));
function isKnownAirportIata(code) {
  return AIRPORT_IATA_SET.has(code);
}
new Map(AIRPORTS.map((a) => [a.iata, a]));

// src/validation/index.ts
var uuidSchema = z.guid();
var emailSchema = z.email("Please enter a valid email address").max(255, "Email must be 255 characters or less");
var phoneSchema = z.string("Please enter a phone number").refine((phone) => phone.trim().length > 0, "Please enter a phone number").regex(/^[\d\s\-+()]+$/, "Phone number can only contain digits, spaces, and + - ( )").min(10, "Phone number must be at least 10 digits").max(20, "Phone number must be 20 characters or less");
var timezoneSchema = z.string().refine(
  (tz) => {
    try {
      Intl.DateTimeFormat(void 0, { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid timezone" }
);
var slugSchema = z.string().min(2, "Slug must be at least 2 characters").max(100, "Slug must be 100 characters or less").regex(/^[a-z0-9-]+$/, "Slug can only contain lowercase letters, numbers, and hyphens").refine((slug) => !slug.startsWith("-") && !slug.endsWith("-"), {
  message: "Slug cannot start or end with a hyphen"
}).refine((slug) => !slug.includes("--"), {
  message: "Slug cannot contain consecutive hyphens"
}).refine((slug) => !isReservedSlug(slug), {
  message: "This URL is reserved and cannot be used"
});
var hexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Invalid hex color format");
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).max(1e3).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20)
});
var RESERVED_SYSTEM_CANCELLATION_REASONS = [
  "quote_expired",
  COMPANY_DECLINED_CANCELLATION_REASON
];
var RESERVED_SYSTEM_CANCELLATION_REASON_SET = new Set(
  RESERVED_SYSTEM_CANCELLATION_REASONS
);
function isReservedSystemCancellationReason(reason) {
  return RESERVED_SYSTEM_CANCELLATION_REASON_SET.has(reason.trim());
}
var PREFERENCES_MAX_DEPTH = 8;
var PREFERENCES_MAX_BYTES = 16 * 1024;
function exceedsDepth(value, max, depth = 0) {
  if (depth > max) return true;
  if (Array.isArray(value)) {
    return value.some((v) => exceedsDepth(v, max, depth + 1));
  }
  if (value !== null && typeof value === "object") {
    return Object.values(value).some(
      (v) => exceedsDepth(v, max, depth + 1)
    );
  }
  return false;
}
var boundedPreferences = z.record(z.string(), z.unknown()).refine(
  (p) => !exceedsDepth(p, PREFERENCES_MAX_DEPTH) && JSON.stringify(p).length <= PREFERENCES_MAX_BYTES,
  { message: "Preferences are too large or nested too deeply" }
);
z.object({
  email: emailSchema,
  name: z.string().min(1).max(255),
  phone: phoneSchema.optional(),
  preferences: boundedPreferences.optional(),
  consentMarketing: z.boolean().optional().default(false),
  consentDataSharing: z.boolean().optional().default(false)
});
z.object({
  name: z.string().min(1).max(255).optional(),
  phone: phoneSchema.nullable().optional(),
  preferences: boundedPreferences.optional(),
  consentMarketing: z.boolean().optional(),
  consentDataSharing: z.boolean().optional()
});
z.object({
  otpCode: z.string().regex(/^\d{6}$/, "Verification code must be 6 digits").optional()
});
var companySettingsSchema = z.object({
  minHoursNotice: z.number().int().min(0).max(168).optional(),
  maxAdvanceBookingDays: z.number().int().min(1).max(365).optional(),
  autoConfirmReservations: z.boolean().optional(),
  autoDispatchCascadeEnabled: z.boolean().optional(),
  supportsWheelchairRides: z.boolean().optional(),
  soloAutoAccept: z.enum(["always", "when_available"]).optional(),
  requireGuestPhone: z.boolean().optional(),
  quoteExpiryDays: z.number().int().min(1).max(365).optional(),
  quoteFollowUpLeadHours: z.number().int().min(1).max(168).optional(),
  serviceLevelsEnabled: z.boolean().optional()
});
z.object({
  minHoursNotice: z.number().int().min(0).max(168).optional(),
  maxAdvanceBookingDays: z.number().int().min(1).max(365).optional(),
  autoConfirmReservations: z.boolean().optional(),
  autoDispatchCascadeEnabled: z.boolean().optional(),
  offerNudgeMinutes: z.number().int().min(1).max(1440).optional(),
  offerStaffEscalationMinutes: z.number().int().min(1).max(1440).optional(),
  offerAutoHandoffEnabled: z.boolean().optional(),
  offerAutoHandoffMinutes: z.number().int().min(1).max(1440).optional(),
  unassignedAlertLeadMinutes: z.number().int().min(1).max(1440).optional(),
  supportsWheelchairRides: z.boolean().optional(),
  soloAutoAccept: z.enum(["always", "when_available"]).optional(),
  requireGuestPhone: z.boolean().optional(),
  quoteExpiryDays: z.number().int().min(1).max(365).optional(),
  quoteFollowUpLeadHours: z.number().int().min(1).max(168).optional(),
  depositPolicyEnabled: z.boolean().optional(),
  depositPolicyType: z.enum(["fixed", "percent"]).optional(),
  depositFixedAmount: z.number().min(0).max(1e5).optional(),
  depositPercent: z.number().min(1).max(100).optional()
}).strict();
z.object({
  precision: z.enum(["exact", "coarse"]).optional(),
  retentionDays: z.number().int().min(1).max(3650).nullable().optional(),
  customerRouteAfterCompletion: z.boolean().optional(),
  fleetMapEnabled: z.boolean().optional()
}).strict();
var internationalAddressSchema = z.object({
  addressLine1: z.string().min(1, "Street address is required").max(255),
  addressLine2: z.string().max(255).optional(),
  city: z.string().min(1, "City is required").max(100),
  stateOrRegion: z.string().max(100).optional(),
  // Not required for all countries
  postalCode: z.string().max(20).optional(),
  // Not required for all countries
  country: z.string().min(2, "Country is required").max(100).default("United States")
});
var addressSchema = internationalAddressSchema;
var companyStructuredAddressSchema = internationalAddressSchema.extend({
  country: z.string().min(2, "Country is required").max(100)
});
z.object({
  name: z.string().min(1).max(255),
  slug: slugSchema,
  email: emailSchema,
  phone: phoneSchema.optional(),
  address: z.string().max(500).optional(),
  timezone: timezoneSchema.optional().default("America/New_York"),
  settings: companySettingsSchema.optional()
});
var embedOriginSchema = z.string().refine(
  (value) => {
    try {
      const url = new URL(value);
      return url.protocol === "https:" && url.origin === value;
    } catch {
      return false;
    }
  },
  'Each origin must be a full https:// origin with no path, query, or fragment, e.g. "https://yoursite.com"'
);
var paymentRequirementSchema = z.enum([
  PAYMENT_REQUIREMENT.PAY_AT_PICKUP,
  PAYMENT_REQUIREMENT.ONLINE_UPFRONT
]);
z.object({
  name: z.string().min(1).max(255).optional(),
  email: emailSchema.optional(),
  phone: phoneSchema.nullable().optional(),
  // `Company.address` holds EITHER the structured object below or a legacy line
  // of free text, so the update body carries both spellings and the caller picks
  // one: `addressStructured` is the shape every structured writer uses (and the
  // only one that preserves a per-company country signal), while `address` is
  // the flat legacy form a text-only editor still sends. Sending both at once is
  // rejected server-side rather than silently resolved — see `updateCompany`.
  address: z.string().max(500).nullable().optional(),
  addressStructured: companyStructuredAddressSchema.nullable().optional(),
  timezone: timezoneSchema.optional(),
  settings: companySettingsSchema.optional(),
  showPricingToCustomers: z.boolean().optional(),
  driverReminderEmail: z.boolean().optional(),
  driverReminderSms: z.boolean().optional(),
  // Allowlist of origins permitted to embed the booking widget (#458).
  // Each entry must be a full https:// origin (no path/query/fragment).
  allowedEmbedOrigins: z.array(embedOriginSchema).optional(),
  // Company-authored cancellation/refund POLICY text (#966) — legally-required
  // transparency, NOT a refund decision. Free text only, shown verbatim
  // (plain text, never HTML) to riders. Null/empty clears it (surfaces then
  // render nothing rather than an empty box).
  cancellationPolicy: z.string().trim().max(2e3).nullable().optional(),
  // Structured late-cancellation fee policy (#1728). Entirely
  // operator-configurable and DEFAULTS OFF: `lateCancellationPolicyEnabled` is
  // the explicit master switch (a stored `lateCancelFeePercent: 0` is a valid
  // "always free" configuration, never itself treated as "disabled"). The
  // cross-field rule — enabling requires both a window and a fee percent to
  // be configured — is enforced server-side against the FINAL merged state
  // (this request's fields + the company's existing row), not here, since
  // this is a partial-patch schema where an omitted field means "leave
  // unchanged" (see `updateCompany` controller).
  lateCancellationPolicyEnabled: z.boolean().optional(),
  lateCancelWindowHours: z.number().int("Late-cancel window must be a whole number of hours").min(0, "Late-cancel window cannot be negative").max(336, "Late-cancel window cannot exceed 336 hours (14 days)").nullable().optional(),
  lateCancelFeePercent: z.number().int("Late-cancel fee must be a whole percent").min(0, "Late-cancel fee cannot be negative").max(100, "Late-cancel fee cannot exceed 100%").nullable().optional(),
  // Distinct from `cancellationPolicy` (#966, general legal-transparency free
  // text) — this is a short note specifically attached to the late-fee line,
  // only rendered when `lateCancellationPolicyEnabled` is true.
  cancellationPolicyNote: z.string().trim().max(280).nullable().optional(),
  // Flight-tracked late-cancel opt-in (#2466). Off by default: a flight_tracked
  // ride never incurs a late-cancel fee/window unless the company turns this on
  // (and the global kill-switch is enabled). Persisted in Company.settings, not
  // a column. Omitted means "leave unchanged".
  flightTrackedLateCancelEnabled: z.boolean().optional(),
  // Company-level payment-timing DEFAULT. Defaults to "pay_at_pickup"
  // server-side; "online_upfront" is enforced by the saved-card lifecycle.
  paymentRequirement: paymentRequirementSchema.optional()
});
z.object({
  slug: slugSchema
});
var companyRoleSchema = z.enum([
  COMPANY_ROLES.ADMIN,
  COMPANY_ROLES.MANAGER,
  COMPANY_ROLES.DRIVER
]);
z.object({
  userId: uuidSchema,
  companyId: uuidSchema,
  roles: z.array(companyRoleSchema).min(1)
});
z.object({
  roles: z.array(companyRoleSchema).min(1).optional(),
  isActive: z.boolean().optional()
});
var invitationMetadataSchema = z.object({
  message: z.string().max(500).optional(),
  requirements: z.array(z.string()).optional()
});
z.object({
  email: emailSchema,
  roles: z.array(companyRoleSchema).min(1),
  expiresInDays: z.number().int().min(1).max(30).optional(),
  metadata: invitationMetadataSchema.optional()
});
var invitationStatusSchema = z.enum(["pending", "accepted", "cancelled", "expired"]);
z.object({
  status: invitationStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional()
});
z.object({
  /** The nominee — must already be an existing ACTIVE `admin` member of the company. */
  toUserId: z.uuid()
});
var serviceLevelFeaturesSchema = z.object({
  wifi: z.boolean().optional(),
  refreshments: z.boolean().optional(),
  childSeat: z.boolean().optional()
});
var serviceLevelRestrictionsSchema = z.object({
  maxDistance: z.number().positive().optional(),
  allowedAreas: z.array(z.string()).optional()
});
var SERVICE_LEVEL_MIN_MINIMUM_HOURS = 1;
var SERVICE_LEVEL_MAX_MINIMUM_HOURS = 24;
var SERVICE_LEVEL_DEFAULT_MINIMUM_HOURS = 2;
var minimumHoursSchema = z.number().int("Minimum hours must be a whole number").min(SERVICE_LEVEL_MIN_MINIMUM_HOURS, "Minimum hours must be at least 1").max(SERVICE_LEVEL_MAX_MINIMUM_HOURS, "Minimum hours cannot exceed 24");
var createServiceLevelSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1e3).optional(),
  sortOrder: z.number().int().min(0).optional().default(0),
  isDefault: z.boolean().optional().default(false),
  minPrice: z.number().min(0),
  hourlyRate: z.number().min(0).optional(),
  minimumHours: minimumHoursSchema.optional().default(SERVICE_LEVEL_DEFAULT_MINIMUM_HOURS),
  maxPassengers: z.number().int().min(1).optional(),
  minPassengers: z.number().int().min(1).optional(),
  maxLuggage: z.number().int().min(0).optional(),
  restrictions: serviceLevelRestrictionsSchema.optional(),
  features: serviceLevelFeaturesSchema.optional(),
  settings: z.record(z.string(), z.unknown()).optional()
});
createServiceLevelSchema.partial().extend({
  // Omission on a partial update leaves the stored floor unchanged.
  minimumHours: minimumHoursSchema.optional()
});
var VEHICLE_ICON_KEYS = [
  "sedan",
  "suv",
  "van",
  "sprinter",
  "limo",
  "bus",
  "wheelchair"
];
var vehicleIconKeySchema = z.enum(VEHICLE_ICON_KEYS);
var serviceLevelFeaturesObjectSchema = z.strictObject({
  wifi: z.boolean().optional(),
  water: z.boolean().optional(),
  charger: z.boolean().optional(),
  childSeat: z.boolean().optional(),
  champagne: z.boolean().optional(),
  entertainment: z.boolean().optional(),
  wheelchairAccessible: z.boolean().optional()
});
var SERVICE_LEVEL_MAX_PRICE = 1e4;
var SERVICE_LEVEL_MIN_PASSENGERS = 1;
var SERVICE_LEVEL_MAX_PASSENGERS = 50;
var SERVICE_LEVEL_MAX_LUGGAGE = 30;
z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or fewer"),
  description: z.string().trim().max(500, "Description must be 500 characters or fewer").optional(),
  minPrice: z.number({ error: "Minimum price is required" }).min(0, "Minimum price cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Minimum price is too high"),
  hourlyRate: z.number().min(0, "Hourly rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Hourly rate is too high").nullable().optional(),
  /**
   * Shortest engagement this level may be booked for. Not nullable: a level
   * always has a floor, and omitting it takes the default rather than
   * clearing it.
   */
  minimumHours: minimumHoursSchema.optional().default(SERVICE_LEVEL_DEFAULT_MINIMUM_HOURS),
  perMileRate: z.number().min(0, "Per-mile rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-mile rate is too high").nullable().optional(),
  perMinuteRate: z.number().min(0, "Per-minute rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-minute rate is too high").nullable().optional(),
  minPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Minimum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Minimum passengers cannot exceed 50").optional(),
  maxPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Maximum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Maximum passengers cannot exceed 50").optional(),
  maxLuggage: z.number().int("Luggage count must be a whole number").min(0, "Luggage count cannot be negative").max(SERVICE_LEVEL_MAX_LUGGAGE, "Luggage count cannot exceed 30").optional(),
  features: serviceLevelFeaturesObjectSchema.optional(),
  /**
   * Curated vehicle icon for this level's tier card (#1635). `null` (or
   * omitted) means no icon chosen — the client renders a neutral fallback.
   */
  iconKey: vehicleIconKeySchema.nullable().optional(),
  sortOrder: z.number().int("Sort order must be a whole number").min(0, "Sort order cannot be negative").optional().default(0),
  isDefault: z.boolean().optional()
}).superRefine((data, ctx) => {
  if (data.minPassengers !== void 0 && data.maxPassengers !== void 0 && data.minPassengers > data.maxPassengers) {
    ctx.addIssue({
      code: "custom",
      message: "Minimum passengers cannot exceed maximum passengers",
      path: ["minPassengers"]
    });
  }
});
z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or fewer").optional(),
  description: z.string().trim().max(500, "Description must be 500 characters or fewer").nullable().optional(),
  minPrice: z.number().min(0, "Minimum price cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Minimum price is too high").optional(),
  hourlyRate: z.number().min(0, "Hourly rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Hourly rate is too high").nullable().optional(),
  /**
   * Omitting it leaves the stored floor alone; `null` is refused rather than
   * treated as "clear", because a level without a floor cannot exist.
   */
  minimumHours: minimumHoursSchema.optional(),
  perMileRate: z.number().min(0, "Per-mile rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-mile rate is too high").nullable().optional(),
  perMinuteRate: z.number().min(0, "Per-minute rate cannot be negative").max(SERVICE_LEVEL_MAX_PRICE, "Per-minute rate is too high").nullable().optional(),
  minPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Minimum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Minimum passengers cannot exceed 50").nullable().optional(),
  maxPassengers: z.number().int("Passenger count must be a whole number").min(SERVICE_LEVEL_MIN_PASSENGERS, "Maximum passengers must be at least 1").max(SERVICE_LEVEL_MAX_PASSENGERS, "Maximum passengers cannot exceed 50").nullable().optional(),
  maxLuggage: z.number().int("Luggage count must be a whole number").min(0, "Luggage count cannot be negative").max(SERVICE_LEVEL_MAX_LUGGAGE, "Luggage count cannot exceed 30").nullable().optional(),
  features: serviceLevelFeaturesObjectSchema.optional(),
  /**
   * `null` clears a previously-chosen icon; omitting it leaves the stored key
   * unchanged. (Unlike `minimumHours`, a level need not carry an icon at all.)
   */
  iconKey: vehicleIconKeySchema.nullable().optional(),
  sortOrder: z.number().int("Sort order must be a whole number").min(0, "Sort order cannot be negative").optional(),
  isDefault: z.boolean().optional()
}).superRefine((data, ctx) => {
  if (data.minPassengers !== void 0 && data.minPassengers !== null && data.maxPassengers !== void 0 && data.maxPassengers !== null && data.minPassengers > data.maxPassengers) {
    ctx.addIssue({
      code: "custom",
      message: "Minimum passengers cannot exceed maximum passengers",
      path: ["minPassengers"]
    });
  }
});
z.object({
  enabledRideTypeIds: z.array(uuidSchema).max(100)
});
z.object({
  airportIata: z.string().trim().toUpperCase().pipe(
    z.string().regex(/^[A-Z]{3}$/, "Airport code must be 3 letters").refine(isKnownAirportIata, "Unknown airport code")
  ),
  label: z.string().trim().max(60, "Label must be 60 characters or fewer").transform((label) => label.length === 0 ? null : label).optional()
});
z.enum([
  RIDE_TYPES.POINT_TO_POINT,
  RIDE_TYPES.HOURLY,
  RIDE_TYPES.CUSTOM
]);
var stopRoleSchema = z.enum(["pickup", "dropoff"]);
var stopInputSchema = z.object({
  role: stopRoleSchema,
  address: z.string("Address is required").min(1, "Address is required").max(500, "Address must be 500 characters or less"),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional()
});
var rideSubTypeSchema = z.enum([RIDE_SUB_TYPES.ARRIVAL, RIDE_SUB_TYPES.DEPARTURE]);
var reservationStatusSchema = z.enum([
  RESERVATION_STATUS.PENDING,
  RESERVATION_STATUS.CONFIRMED,
  RESERVATION_STATUS.ASSIGNED,
  RESERVATION_STATUS.IN_PROGRESS,
  RESERVATION_STATUS.COMPLETED,
  RESERVATION_STATUS.CANCELLED
]);
var paymentStatusSchema = z.enum([
  PAYMENT_STATUS.UNPAID,
  PAYMENT_STATUS.PAID,
  PAYMENT_STATUS.PARTIAL
]);
var completionOutcomeSchema = z.enum([
  COMPLETION_OUTCOME.NORMAL,
  COMPLETION_OUTCOME.NO_SHOW,
  COMPLETION_OUTCOME.INCIDENT
]);
var paymentMethodSchema = z.enum([
  PAYMENT_METHODS.CASH,
  PAYMENT_METHODS.VENMO,
  PAYMENT_METHODS.ZELLE,
  PAYMENT_METHODS.PAYPAL,
  PAYMENT_METHODS.CHECK,
  PAYMENT_METHODS.BANK_TRANSFER,
  PAYMENT_METHODS.CREDIT_CARD,
  PAYMENT_METHODS.OTHER
]);
var PICKUP_TIME_GRACE_MS = 5 * 60 * 1e3;
var FLIGHT_CODE_REGEX = /^[A-Za-z0-9]+$/;
function flightCodeSchema(maxLen, label) {
  return z.string().transform((val) => val.trim().replace(/\s+/g, "").toUpperCase()).refine((val) => val.length <= maxLen, {
    message: `${label} must be ${String(maxLen)} characters or less`
  }).refine((val) => val.length === 0 || FLIGHT_CODE_REGEX.test(val), {
    message: `${label} can only contain letters and numbers`
  });
}
var createReservationBaseObject = z.object({
  rideTypeId: z.guid("Please choose a ride type"),
  // Required: the service level is the pricing basis (its rate drives the
  // server-derived `totalCost`). A booking with no service level used to persist
  // a $0 reservation, so it is rejected with a friendly 400 here (QA #198).
  serviceLevelId: z.guid("Please choose a service level"),
  guestEmail: emailSchema,
  guestFirstName: z.string("Please enter a first name").min(1, "Please enter a first name").max(255, "First name must be 255 characters or less"),
  guestLastName: z.string("Please enter a last name").min(1, "Please enter a last name").max(255, "Last name must be 255 characters or less"),
  guestPhone: phoneSchema,
  pickupDatetime: z.iso.datetime({ error: "Please choose a pickup date and time" }).refine((value) => new Date(value).getTime() > Date.now() - PICKUP_TIME_GRACE_MS, {
    message: "Pickup time must be in the future"
  }),
  pickupTimezone: timezoneSchema,
  dropoffDatetime: z.iso.datetime({ error: "Please choose a valid dropoff date and time" }).optional(),
  dropoffTimezone: timezoneSchema.optional(),
  // Legacy single-leg fields — required when `stops` is absent (back-compat).
  // When `stops` is provided these are derived server-side and optional here.
  pickupLocation: z.string("Please enter a pickup address").min(1, "Please enter a pickup address").max(500, "Pickup address must be 500 characters or less").optional(),
  dropoffLocation: z.string("Please enter a dropoff address").min(1, "Please enter a dropoff address").max(500, "Dropoff address must be 500 characters or less").optional(),
  passengerCount: z.number("Please enter the number of passengers").int("Passenger count must be a whole number").min(1, "At least 1 passenger is required").max(50, "Passenger count cannot exceed 50").optional().default(1),
  luggageCount: z.number("Please enter the number of bags").int("Bag count must be a whole number").min(0, "Bag count cannot be negative").max(50, "Bag count cannot exceed 50").optional().default(0),
  // Notes-driven requirements (#648): a gate boolean MANDATES a corresponding
  // free-text note (enforced in this schema's superRefine + ReservationService).
  // The old carSeatsInfant/Toddler/Booster counts + the petFriendly flag were
  // removed; animal/service-animal needs go in specialNotes (ADA accommodation
  // is policy, not a field — see non-functional.md).
  oversizedLuggage: z.boolean().optional().default(false),
  carSeatsNeeded: z.boolean().optional().default(false),
  carSeatNotes: z.string().max(1e3, "Car seat details must be 1,000 characters or less").nullable().optional(),
  wheelchairAccess: z.boolean().optional().default(false),
  // Trimmed so a textarea's stray spaces/newlines cannot persist as a note that
  // is truthy but blank — a reader would render its "Special Notes" heading
  // over an empty box. Interior line breaks survive.
  specialNotes: z.string().max(2e3, "Notes must be 2,000 characters or less").trim().optional(),
  /**
   * Free-text description of oversized or special luggage (skis, golf bags,
   * extra bags…). Surfaced behind the `oversizedLuggage` gate; when that gate is
   * on this note is REQUIRED (see the superRefine) so the business/driver can
   * adjudicate. Optional otherwise.
   */
  luggageDetails: z.string().max(1e3, "Luggage details must be 1,000 characters or less").optional(),
  subType: rideSubTypeSchema.optional(),
  // #979: airlineCode was max(50) — far looser than the actual Postgres
  // `FlightInfo.airlineCode` VarChar(10) column bound, so an 11-50 char value
  // passed this schema only to 500 at insert time. Tightened to max(10)
  // (matching updateReservationSchema/guestFlightEditSchema, #978) with a
  // normalize-then-validate pass (see {@link flightCodeSchema}) on both
  // fields; both stay optional.
  flightNumber: flightCodeSchema(20, "Flight number").optional(),
  airlineCode: flightCodeSchema(10, "Airline code").optional(),
  /**
   * Derive-pickup-from-flight opt-in (#2435). `explicit` (default) keeps the
   * human `pickupDatetime` authoritative — today's behavior. `flight_tracked`
   * opts an airport-ARRIVAL pickup into tracking the linked inbound flight's
   * gate arrival: the server seeds the derived pickup from the flight's
   * scheduled arrival at booking and accepts the mode ONLY when the flight
   * resolves to one (a non-airport pickup, a missing flight, or an
   * unresolvable flight is rejected so the caller falls back to an explicit
   * time). `pickupDatetime` stays required even here — it is the
   * expected-arrival reference the flight lookup + dedup key are scoped by, and
   * the effective-pickup COALESCE backstop.
   */
  pickupMode: z.enum([PICKUP_MODE.EXPLICIT, PICKUP_MODE.FLIGHT_TRACKED]).optional(),
  /**
   * Booked trip duration in hours — the basis of an hourly fare (rate ×
   * hours), not an estimate.
   *
   * **Required for an hourly booking, forbidden-to-require for a routed one.**
   * That conditional cannot be expressed here: a request carries only a bare
   * `rideTypeId` guid, and resolving its kind needs a DB round trip. Marking it
   * `.required()` outright would reject every routed booking, which never sends
   * one and whose fare never reads it. So the field stays optional in the shape
   * and every create path enforces the rule immediately after the ride-type
   * lookup — the same deferral, for the same reason, as the dropoff requirement
   * (see {@link reservationCreateRefineDeferringDropoff}). An hourly booking
   * that omits it, or states less than the service level's `minimumHours`, is
   * refused with a `HOURLY_DURATION_REQUIRED` error carrying that minimum.
   */
  estimatedDurationHours: z.number("Please enter the duration in hours").min(1, "Duration must be at least 1 hour").max(24, "Duration must be 24 hours or less").optional(),
  /**
   * Where the booking was submitted from. `"widget"` marks a create that
   * originated in the chromeless embeddable widget (`/widget/:slug`), a paid
   * feature (allowEmbeddableWidget — business_plus/business_ultimate). The
   * public create endpoint gates ONLY widget-origin creates on that tier flag
   * (#199); the hosted booking page (`/book/:slug`, available to every tier)
   * omits this field and is never gated. Defaults to `"hosted"`.
   */
  source: z.enum(["hosted", "widget"]).optional().default("hosted"),
  /**
   * High-entropy retry identity for an authenticated staff round trip. The
   * controller and service require it only when `returnTrip` is present, so
   * deployed one-way operator clients remain compatible. The raw UUID is
   * request-only; persistence stores only its tenant-scoped digest.
   */
  bookingAttemptId: z.guid().optional(),
  /**
   * Pre-resolved geocoordinates from the frontend autocomplete. When present,
   * the API uses these directly (skipping a geocoding round-trip); distance is
   * always recomputed server-side and coords are never trusted for pricing.
   */
  pickupLat: z.number().min(-90).max(90).optional(),
  pickupLng: z.number().min(-180).max(180).optional(),
  dropoffLat: z.number().min(-90).max(90).optional(),
  dropoffLng: z.number().min(-180).max(180).optional(),
  /**
   * Public customer-channel wire fields. While CUSTOMER_SMS_ENABLED is off, no
   * customer SMS choice appears and the create boundary stores no new SMS
   * election or disclosure version. Once enabled, an eligible company may
   * offer the optional choice; the server still resolves phone, STOP, tier,
   * and current disclosure evidence. Authenticated operational SMS uses
   * separate preferences and routes.
   */
  notifyByEmail: z.boolean().optional().default(true),
  notifyBySms: z.boolean().optional().default(false),
  /**
   * Multi-stop route. When present (≥1 pickup + ≥1 dropoff), these are
   * canonical — `pickupLocation`/`dropoffLocation` are derived server-side
   * from the first pickup / last dropoff address. When absent, the legacy flat
   * fields are required for backward compatibility.
   */
  stops: z.array(stopInputSchema).max(8, "Maximum 8 stops allowed").optional(),
  /**
   * Set when the customer chose "I'm not flying" at an airport endpoint.
   * Flight tracking won't be attempted; surfaces a warning on dispatch.
   */
  noFlightFlag: z.boolean().optional().default(false),
  /**
   * Airport terminal collected when an endpoint stop is detected as an airport.
   * Optional — stored in FlightInfo.terminal.
   */
  terminal: z.string().max(20, "Terminal must be 20 characters or less").optional(),
  /**
   * Optional return leg for round-trip bookings. When present the server creates
   * a second linked reservation using the same passenger info, ride type, and
   * service level as the outbound. Stops and endpoint-flight capture are
   * independent for the return leg; stops default to the reversed outbound stops
   * when omitted.
   */
  returnTrip: z.object({
    pickupDatetime: z.iso.datetime({ error: "Please choose a return pickup date and time" }).refine((value) => new Date(value).getTime() > Date.now() - PICKUP_TIME_GRACE_MS, {
      message: "Return pickup time must be in the future"
    }),
    pickupTimezone: timezoneSchema,
    stops: z.array(stopInputSchema).min(2, "Return trip needs at least one pickup and one dropoff").max(8, "Maximum 8 stops allowed").optional(),
    /**
     * Set when the customer chose "I'm not flying" for the return leg.
     * Independent from the outbound leg's airport-endpoint choice.
     */
    noFlightFlag: z.boolean().optional().default(false),
    /**
     * Return-leg airport terminal collected when a return endpoint is an airport.
     * Optional — stored in FlightInfo.terminal.
     */
    terminal: z.string().max(20, "Terminal must be 20 characters or less").optional(),
    flightNumber: flightCodeSchema(20, "Flight number").optional(),
    airlineCode: flightCodeSchema(10, "Airline code").optional()
  }).superRefine((ret, ctx) => {
    if (ret.noFlightFlag && ret.flightNumber) {
      ctx.addIssue({
        code: "custom",
        message: 'A return trip cannot be marked "not flying" while also setting a flight.',
        path: ["noFlightFlag"]
      });
    }
  }).optional()
});
createReservationBaseObject.shape.returnTrip.unwrap();
function reservationStopsRefine(data, ctx, options = {}) {
  const dropoffOptional = options.dropoffOptional === true;
  if (data.stops && data.stops.length > 0) {
    const pickups = data.stops.filter((s) => s.role === "pickup");
    const dropoffs = data.stops.filter((s) => s.role === "dropoff");
    if (pickups.length < 1) {
      ctx.addIssue({ code: "custom", message: "At least one pickup stop is required", path: ["stops"] });
    }
    if (!dropoffOptional && dropoffs.length < 1) {
      ctx.addIssue({ code: "custom", message: "At least one dropoff stop is required", path: ["stops"] });
    }
    if (pickups.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 pickup stops allowed", path: ["stops"] });
    }
    if (dropoffs.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 dropoff stops allowed", path: ["stops"] });
    }
  } else {
    if (!data.pickupLocation) {
      ctx.addIssue({ code: "custom", message: "Please enter a pickup address", path: ["pickupLocation"] });
    }
    if (!dropoffOptional && !data.dropoffLocation) {
      ctx.addIssue({ code: "custom", message: "Please enter a dropoff address", path: ["dropoffLocation"] });
    }
  }
}
function returnTripRefine(data, ctx) {
  const ret = data.returnTrip;
  if (!ret) return;
  if (data.pickupDatetime && ret.pickupDatetime) {
    if (new Date(ret.pickupDatetime).getTime() <= new Date(data.pickupDatetime).getTime()) {
      ctx.addIssue({
        code: "custom",
        message: "Return pickup must be after the outbound pickup time",
        path: ["returnTrip", "pickupDatetime"]
      });
    }
  }
  if (ret.stops && ret.stops.length > 0) {
    const pickups = ret.stops.filter((s) => s.role === "pickup");
    const dropoffs = ret.stops.filter((s) => s.role === "dropoff");
    if (pickups.length < 1) {
      ctx.addIssue({ code: "custom", message: "Return trip needs at least one pickup stop", path: ["returnTrip", "stops"] });
    }
    if (dropoffs.length < 1) {
      ctx.addIssue({ code: "custom", message: "Return trip needs at least one dropoff stop", path: ["returnTrip", "stops"] });
    }
    if (pickups.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 pickup stops allowed", path: ["returnTrip", "stops"] });
    }
    if (dropoffs.length > 4) {
      ctx.addIssue({ code: "custom", message: "Maximum 4 dropoff stops allowed", path: ["returnTrip", "stops"] });
    }
  }
}
function reservationCreateRefine(data, ctx, options = {}) {
  reservationStopsRefine(data, ctx, options);
  returnTripRefine(data, ctx);
  if (data.noFlightFlag && data.flightNumber) {
    ctx.addIssue({
      code: "custom",
      path: ["noFlightFlag"],
      message: 'A reservation cannot be marked "not flying" while also setting a flight.'
    });
  }
  if (data.carSeatsNeeded && !data.carSeatNotes?.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["carSeatNotes"],
      message: "Describe the car seat requirement (type, ages, who provides)."
    });
  }
  if (data.oversizedLuggage && !data.luggageDetails?.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["luggageDetails"],
      message: "Describe the oversized or special luggage."
    });
  }
}
function reservationCreateRefineDeferringDropoff(data, ctx) {
  reservationCreateRefine(data, ctx, { dropoffOptional: true });
}
createReservationBaseObject.superRefine(
  reservationCreateRefineDeferringDropoff
);
var OTHER_REQUEST_DESCRIPTION_MIN = 50;
var OTHER_REQUEST_DESCRIPTION_MAX = 2e3;
var otherRequestSchema = z.object({
  companyId: z.guid(),
  specialNotes: z.string("Please describe your request").trim().min(
    OTHER_REQUEST_DESCRIPTION_MIN,
    `Please provide a bit more detail (at least ${String(OTHER_REQUEST_DESCRIPTION_MIN)} characters).`
  ).max(
    OTHER_REQUEST_DESCRIPTION_MAX,
    `Description must be ${String(OTHER_REQUEST_DESCRIPTION_MAX)} characters or fewer.`
  ),
  pickupDatetime: z.iso.datetime({ error: "Please choose a valid pickup date and time" }).refine((value) => new Date(value).getTime() > Date.now() - PICKUP_TIME_GRACE_MS, {
    message: "Pickup time must be in the future"
  }).optional(),
  pickupTimezone: timezoneSchema.optional(),
  pickupLocation: z.string().max(500, "Pickup address must be 500 characters or less").optional(),
  dropoffLocation: z.string().max(500, "Dropoff address must be 500 characters or less").optional(),
  pickupLat: z.number().min(-90).max(90).optional(),
  pickupLng: z.number().min(-180).max(180).optional(),
  dropoffLat: z.number().min(-90).max(90).optional(),
  dropoffLng: z.number().min(-180).max(180).optional(),
  /** Spec default 2 (docs/reservation-form-ux.md §6) — applied server-side when omitted. */
  passengerCount: z.number("Please enter the number of passengers").int("Passenger count must be a whole number").min(1, "At least 1 passenger is required").max(20, "Passenger count cannot exceed 20").optional(),
  guestEmail: emailSchema,
  guestFirstName: z.string("Please enter a first name").min(1, "Please enter a first name").max(255, "First name must be 255 characters or less"),
  guestLastName: z.string("Please enter a last name").min(1, "Please enter a last name").max(255, "Last name must be 255 characters or less"),
  // Optional at the schema boundary, same as publicReservationSchema —
  // whether it's required is an operator policy (requireGuestPhone)
  // enforced server-side once the company config is known.
  guestPhone: z.string().trim().transform((v) => v === "" ? void 0 : v).pipe(phoneSchema.optional()).optional(),
  notifyByEmail: z.boolean().optional().default(true),
  notifyBySms: z.boolean().optional().default(false),
  source: z.enum(["hosted", "widget"]).optional().default("hosted")
});
otherRequestSchema.omit({
  companyId: true,
  source: true
});
var operatorGuestEmailSchema = z.string().trim().pipe(z.union([z.literal(""), emailSchema])).optional().default("");
z.object({
  ...createReservationBaseObject.shape,
  guestEmail: operatorGuestEmailSchema,
  /**
   * Customer SMS is not a delivered staff-booking channel at launch. Keep
   * the authenticated boundary fail-closed instead of accepting a flag that
   * would be persisted but never honored. Driver/staff operational SMS uses
   * separate authenticated services and is unaffected.
   */
  notifyBySms: z.literal(false).optional().default(false),
  /**
   * Staff-only, explicit request to bypass the customer minimum-notice
   * floor. Authorization, exact violation type, and audit recording are
   * enforced by ReservationService; this never applies to public create.
   */
  overrideMinimumNotice: z.boolean().optional().default(false)
}).superRefine(reservationCreateRefineDeferringDropoff).transform((data) => ({
  ...data,
  // Normalize the staff contract itself, not just persistence: an omitted
  // email is the no-email sentinel and can never request customer email.
  notifyByEmail: data.guestEmail.length > 0 && data.notifyByEmail
}));
var updateReservationSchema = z.object({
  // Optional optimistic-concurrency token for operational editors. Omission
  // preserves compatibility; the service enforces a tenant-scoped CAS when set.
  expectedUpdatedAt: z.iso.datetime().optional(),
  serviceLevelId: uuidSchema.nullable().optional(),
  pickupDatetime: z.iso.datetime().optional(),
  pickupTimezone: timezoneSchema.optional(),
  // Flight-alignment bookkeeping (#868): the arrival the pickup was last aligned
  // to via "adjust pickup to match flight". Set only by that action (alongside
  // pickupDatetime) so the suggestion stays convergent; null clears it. Never on
  // the public/guest booking payload (this is the operator/platform update only).
  flightAdjustedForArrival: z.iso.datetime().nullable().optional(),
  dropoffDatetime: z.iso.datetime().nullable().optional(),
  dropoffTimezone: timezoneSchema.nullable().optional(),
  pickupLocation: z.string().min(1).max(500).optional(),
  dropoffLocation: z.string().min(1).max(500).optional(),
  passengerCount: z.number().int().min(1).max(50).optional(),
  luggageCount: z.number().int().min(0).max(50).optional(),
  // Notes-driven requirements (#648). The gate⇒note rule is enforced in
  // ReservationService.update against the MERGED row (a partial update schema
  // can't see un-patched fields). Dropped: carSeatsInfant/Toddler/Booster, petFriendly.
  oversizedLuggage: z.boolean().optional(),
  carSeatsNeeded: z.boolean().optional(),
  carSeatNotes: z.string().max(1e3).nullable().optional(),
  wheelchairAccess: z.boolean().optional(),
  // Trimmed for the same reason as the create schema: an edit that leaves only
  // whitespace behind must not become a blank-but-truthy stored note. The
  // guest-token subset picks this field and inherits the normalization.
  specialNotes: z.string().max(2e3).trim().nullable().optional(),
  luggageDetails: z.string().max(1e3).nullable().optional(),
  // Legacy runtime recognition only (#1509). This field is intentionally absent
  // from the public generic-update OpenAPI schema. Keeping it here prevents Zod
  // from silently stripping stale-client fare attempts before the service can
  // return its stable fail-closed conflict.
  totalCost: z.number().min(0).nullable().optional(),
  quoteExpiresAt: z.iso.datetime().nullable().optional(),
  // #978: flight info is now editable/addable pre-trip (was write-once at
  // booking). Routed through ReservationService.setFlight — a new chokepoint,
  // NOT the plain field patch — so the state guard + provider-field reset +
  // audit row all apply. `null` clears the corresponding value; omitted
  // leaves it untouched. `airlineCode` is capped at 10 (the actual Postgres
  // `FlightInfo.airlineCode` VarChar(10) column bound) — deliberately
  // DIFFERENT from (and does not touch) the CREATE schema's `airlineCode`
  // bound above, which is a separate, pre-existing mismatch out of scope here.
  flightNumber: z.string().max(20, "Flight number must be 20 characters or less").nullable().optional(),
  airlineCode: z.string().max(10, "Airline code must be 10 characters or less").nullable().optional(),
  terminal: z.string().max(20, "Terminal must be 20 characters or less").nullable().optional(),
  noFlightFlag: z.boolean().optional()
});
z.object({
  reason: z.string().trim().min(1).max(255).refine((reason) => !isReservedSystemCancellationReason(reason), {
    message: "This cancellation reason is reserved for system use."
  })
});
z.object({
  totalCost: z.number().min(0).optional()
});
z.strictObject({});
z.strictObject({});
updateReservationSchema.pick({
  flightNumber: true,
  airlineCode: true,
  terminal: true,
  noFlightFlag: true
});
updateReservationSchema.pick({
  passengerCount: true,
  luggageCount: true,
  specialNotes: true,
  flightNumber: true,
  airlineCode: true,
  terminal: true,
  noFlightFlag: true
}).extend({
  guestPhone: z.preprocess(
    (value) => typeof value === "string" && value.trim() === "" ? "" : value,
    z.union([z.literal(""), phoneSchema])
  ).optional()
});
z.object({
  driverUserId: uuidSchema.optional(),
  placeholderDriverId: uuidSchema.optional(),
  vehicleId: uuidSchema.nullable().optional(),
  confirmConflict: z.boolean().optional()
}).refine(
  (value) => value.driverUserId === void 0 !== (value.placeholderDriverId === void 0),
  {
    message: "Name exactly one of driverUserId or placeholderDriverId",
    path: ["driverUserId"]
  }
);
z.object({
  paymentStatus: paymentStatusSchema,
  paymentMethod: paymentMethodSchema.nullable().optional(),
  amountPaid: z.number().min(0).refine((amount) => {
    const cents = amount * 100;
    return Number.isSafeInteger(Math.round(cents)) && Math.abs(cents - Math.round(cents)) < 1e-8;
  }, "Payment amount must use whole cents").optional(),
  confirmationAmount: z.number().min(0).refine((amount) => {
    const cents = amount * 100;
    return Number.isSafeInteger(Math.round(cents)) && Math.abs(cents - Math.round(cents)) < 1e-8;
  }, "Payment confirmation must use whole cents"),
  tipAmount: z.number().min(0).refine((amount) => {
    const cents = amount * 100;
    return Number.isSafeInteger(Math.round(cents)) && Math.abs(cents - Math.round(cents)) < 1e-8;
  }, "Tip amount must use whole cents").nullable().optional(),
  paidAt: z.iso.datetime().nullable().optional(),
  paymentNotes: z.string().max(500).optional(),
  reason: z.string().trim().min(1).max(500)
}).superRefine((value, ctx) => {
  if (value.paymentStatus === PAYMENT_STATUS.UNPAID) {
    if (value.amountPaid !== void 0 && value.amountPaid !== 0) {
      ctx.addIssue({
        code: "custom",
        path: ["amountPaid"],
        message: "Unpaid reservations cannot retain a positive paid amount"
      });
    }
    if (value.paymentMethod !== void 0 && value.paymentMethod !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["paymentMethod"],
        message: "Unpaid reservations cannot retain a payment method"
      });
    }
    if (value.paidAt !== void 0 && value.paidAt !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["paidAt"],
        message: "Unpaid reservations cannot retain a paid timestamp"
      });
    }
    if (value.confirmationAmount !== 0) {
      ctx.addIssue({
        code: "custom",
        path: ["confirmationAmount"],
        message: "Unpaid reservations require a zero-value confirmation"
      });
    }
    if (value.tipAmount !== void 0 && value.tipAmount !== null && value.tipAmount !== 0) {
      ctx.addIssue({
        code: "custom",
        path: ["tipAmount"],
        message: "Unpaid reservations cannot retain a positive tip"
      });
    }
    return;
  }
  if (value.amountPaid === void 0 || value.amountPaid <= 0) {
    ctx.addIssue({
      code: "custom",
      path: ["amountPaid"],
      message: "Paid and partially paid reservations require a positive amount"
    });
  }
  if (value.paymentMethod === void 0 || value.paymentMethod === null) {
    ctx.addIssue({
      code: "custom",
      path: ["paymentMethod"],
      message: "Paid and partially paid reservations require a manual payment method"
    });
  }
  if (value.paidAt === void 0 || value.paidAt === null) {
    ctx.addIssue({
      code: "custom",
      path: ["paidAt"],
      message: "Paid and partially paid reservations require a paid timestamp"
    });
  }
  if (value.amountPaid !== void 0 && Math.round(value.amountPaid * 100) !== Math.round(value.confirmationAmount * 100)) {
    ctx.addIssue({
      code: "custom",
      path: ["confirmationAmount"],
      message: "Payment amount and confirmation must match exactly"
    });
  }
});
z.object({
  /** Dollars; must be > 0 and <= the reservation's refundable balance (server-checked). */
  amount: z.number().positive().refine(
    (amount) => {
      const cents = amount * 100;
      return Number.isSafeInteger(Math.round(cents)) && Math.abs(cents - Math.round(cents)) < 1e-8;
    },
    "Refund amount must use whole cents"
  ),
  /** Independent destructive-action confirmation; must exactly match `amount`. */
  confirmationAmount: z.number().positive(),
  /** Free-text, required — recorded on the audit trail (ReservationStatusHistory). */
  reason: z.string().min(1).max(500),
  /**
   * Required client-generated token (e.g. a UUID minted once per confirm
   * dialog) so a network retry/double-click of the SAME request reuses the
   * same Stripe idempotency key instead of risking a second real refund.
   */
  idempotencyKey: z.string().trim().min(1).max(200),
  /** Exact server-owned unfinished request explicitly selected for recovery. */
  resumeRequestId: uuidSchema.optional()
}).superRefine((value, ctx) => {
  const confirmationCents = value.confirmationAmount * 100;
  if (!Number.isSafeInteger(Math.round(confirmationCents)) || Math.abs(confirmationCents - Math.round(confirmationCents)) >= 1e-8) {
    ctx.addIssue({
      code: "custom",
      path: ["confirmationAmount"],
      message: "Refund confirmation must use whole cents"
    });
    return;
  }
  if (Math.round(value.amount * 100) !== Math.round(confirmationCents)) {
    ctx.addIssue({
      code: "custom",
      path: ["confirmationAmount"],
      message: "Refund confirmation must exactly match the requested amount"
    });
  }
});
z.object({
  /** Independent confirmation; must exactly match the server-computed amount due. */
  confirmationAmount: z.number().positive(),
  /**
   * Required client-generated token (e.g. a UUID minted once per confirm
   * dialog) so a network retry/double-click of the SAME request reuses the
   * same collection instead of risking a second real charge.
   */
  idempotencyKey: z.string().trim().min(1).max(200)
}).superRefine((value, ctx) => {
  const confirmationCents = value.confirmationAmount * 100;
  if (!Number.isSafeInteger(Math.round(confirmationCents)) || Math.abs(confirmationCents - Math.round(confirmationCents)) >= 1e-8) {
    ctx.addIssue({
      code: "custom",
      path: ["confirmationAmount"],
      message: "Confirmation amount must use whole cents"
    });
  }
});
z.object({
  outcome: completionOutcomeSchema.optional(),
  /** Optional free-text note — recorded on the audit trail (ReservationStatusHistory). */
  notes: z.string().max(500).optional()
});
z.object({
  /**
   * Free-text audit reason, recorded on the audit trail
   * (ReservationStatusHistory). Required whenever a real driver holds the ride
   * — the operator is rescuing a stuck one and owes an explanation. Optional
   * for a placeholder-driven ride, where nobody could tap complete and the
   * operator doing it IS the ordinary end of the ride. The server decides
   * which case applies from the locked row, never from the request.
   */
  reason: z.string().min(1).max(500).optional(),
  /** Optional completion outcome (#969) — see {@link completionOutcomeSchema}. */
  outcome: completionOutcomeSchema.optional()
});
z.object({
  /** Free-text, required — the retrospective reason, recorded on the audit trail. */
  reason: z.string().min(1).max(500),
  /** Optional completion outcome (#1886/#969) — drives the payment branch server-side. */
  outcome: completionOutcomeSchema.optional()
});
z.object({
  /** Optional free-text note — recorded on the audit trail (ReservationStatusHistory). */
  notes: z.string().max(500).optional(),
  /**
   * Optional price in dollars (> 0); must whole-cent-match the CURRENT price
   * or the request is rejected server-side — see the schema doc comment.
   */
  totalCost: z.number().positive().optional()
});
var wholeCentRefine = (amount) => {
  const cents = amount * 100;
  return Number.isSafeInteger(Math.round(cents)) && Math.abs(cents - Math.round(cents)) < 1e-8;
};
z.object({
  /** New price in dollars; required, non-negative, whole cents. */
  totalCost: z.number().min(0).refine(wholeCentRefine, "The new price must use whole cents"),
  /** Independent confirmation; must exactly match `totalCost`. */
  confirmationTotalCost: z.number().min(0).refine(wholeCentRefine, "The price confirmation must use whole cents"),
  /** Required free-text reason — recorded on the audit trail. */
  reason: z.string().trim().min(1).max(500),
  /** Required CAS token — the reservation generation this request was built against. */
  expectedUpdatedAt: z.iso.datetime()
}).superRefine((value, ctx) => {
  if (Math.round(value.totalCost * 100) !== Math.round(value.confirmationTotalCost * 100)) {
    ctx.addIssue({
      code: "custom",
      path: ["confirmationTotalCost"],
      message: "The price and its confirmation must match exactly"
    });
  }
});
var vehicleCapabilityStatusSchema = z.enum(["yes", "no", "unknown"]);
var createVehicleSchema = z.object({
  make: z.string().min(1).max(100),
  model: z.string().min(1).max(100),
  year: z.number().int().min(1900).max(2100).nullable().optional(),
  color: z.string().max(50).nullable().optional(),
  licensePlate: z.string().max(20).nullable().optional(),
  vin: z.string().max(17).nullable().optional(),
  capacity: z.number().int().min(1).max(100).nullable().optional(),
  // Accessibility & amenity capabilities (dispatch vehicle-fit, epic #522 U4).
  //
  // `petFriendly` is deprecated and no current UI exposes it, but supported
  // installed clients still read and write the field. Keep its legacy default
  // and round-trip semantics until those clients cross the retirement boundary.
  // Animal requirements are notes-driven and this flag does not affect fit.
  wheelchairAccessible: z.boolean().optional().default(false),
  wheelchairAccessibleStatus: vehicleCapabilityStatusSchema.optional(),
  petFriendly: z.boolean().optional().default(false),
  childSeatCapable: z.boolean().optional().default(false),
  childSeatCapableStatus: vehicleCapabilityStatusSchema.optional(),
  luggageCapacity: z.number().int().min(0).max(100).nullable().optional(),
  isDefault: z.boolean().optional().default(false)
});
createVehicleSchema.partial().extend({
  wheelchairAccessible: z.boolean().optional(),
  petFriendly: z.boolean().optional(),
  childSeatCapable: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  status: z.enum(["active", "retired"]).optional()
});
z.object({
  status: reservationStatusSchema.optional(),
  driverUserId: uuidSchema.optional(),
  serviceLevelId: uuidSchema.optional(),
  fromDate: z.iso.datetime().optional(),
  toDate: z.iso.datetime().optional(),
  search: z.string().max(100).optional()
}).extend(paginationSchema.shape);
var documentTypeSchema = z.enum([
  DOCUMENT_TYPES.DRIVER_LICENSE,
  DOCUMENT_TYPES.INSURANCE,
  DOCUMENT_TYPES.VEHICLE_REGISTRATION,
  DOCUMENT_TYPES.OTHER
]);
z.enum([
  DOCUMENT_ACCESS_STATUS.PENDING,
  DOCUMENT_ACCESS_STATUS.GRANTED,
  DOCUMENT_ACCESS_STATUS.DENIED,
  DOCUMENT_ACCESS_STATUS.REVOKED
]);
z.object({
  documentType: documentTypeSchema,
  expiresAt: z.iso.datetime().optional(),
  metadata: z.record(z.string(), z.unknown()).optional()
});
z.object({
  documentId: uuidSchema,
  notes: z.string().max(500).optional()
});
z.object({
  status: z.enum([DOCUMENT_ACCESS_STATUS.GRANTED, DOCUMENT_ACCESS_STATUS.DENIED]),
  notes: z.string().max(500).optional()
});
z.object({
  reviewStatus: z.enum([DOCUMENT_REVIEW_STATUS.APPROVED, DOCUMENT_REVIEW_STATUS.REJECTED]),
  reviewReason: z.string().trim().min(1).max(500).optional()
}).refine((d) => d.reviewStatus !== DOCUMENT_REVIEW_STATUS.REJECTED || Boolean(d.reviewReason), {
  message: "A reason is required when rejecting a document",
  path: ["reviewReason"]
});
var driverApprovalStatusSchema = z.enum([
  DRIVER_APPROVAL_STATUS.PENDING_DOCUMENTS,
  DRIVER_APPROVAL_STATUS.DOCUMENTS_RECEIVED,
  DRIVER_APPROVAL_STATUS.APPROVED,
  DRIVER_APPROVAL_STATUS.REJECTED,
  DRIVER_APPROVAL_STATUS.MORE_INFO_NEEDED
]);
z.object({
  approvalStatus: driverApprovalStatusSchema,
  notes: z.string().max(1e3).optional()
});
var availabilityStatusSchema = z.enum([
  AVAILABILITY_STATUS.AVAILABLE,
  AVAILABILITY_STATUS.UNAVAILABLE,
  AVAILABILITY_STATUS.BUSY
]);
var recurrenceRuleSchema = z.object({
  frequency: z.enum(["daily", "weekly", "monthly"]),
  interval: z.number().int().min(1).max(12),
  byDay: z.array(z.string()).optional(),
  until: z.iso.datetime().optional()
});
var createAvailabilitySchema = z.object({
  startDatetime: z.iso.datetime(),
  endDatetime: z.iso.datetime(),
  timezone: timezoneSchema,
  status: availabilityStatusSchema,
  reason: z.string().max(255).optional(),
  isRecurring: z.boolean().optional().default(false),
  recurrenceRule: recurrenceRuleSchema.optional()
});
createAvailabilitySchema.partial();
var createSavedAddressSchema = z.object({
  label: z.string().min(1).max(100),
  addressLine1: z.string().min(1).max(255),
  addressLine2: z.string().max(255).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(50).optional(),
  postalCode: z.string().max(20).optional(),
  country: z.string().max(100).optional().default("USA"),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  isDefault: z.boolean().optional().default(false)
});
createSavedAddressSchema.partial();
var clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be in HH:MM (24-hour) format");
var availabilityEndTimeSchema = z.union([clockTimeSchema, z.literal("24:00")]);
var availabilitySlotSchema = z.object({
  start: clockTimeSchema,
  end: availabilityEndTimeSchema
}).refine((slot) => slot.end !== slot.start, {
  message: "End time must be different from start time",
  path: ["end"]
});
var MAX_AVAILABILITY_WINDOWS_PER_DAY = 6;
var dayAvailabilitySchema = z.array(availabilitySlotSchema).max(MAX_AVAILABILITY_WINDOWS_PER_DAY, {
  message: `A day can have at most ${String(MAX_AVAILABILITY_WINDOWS_PER_DAY)} availability windows`
});
z.object({
  monday: dayAvailabilitySchema,
  tuesday: dayAvailabilitySchema,
  wednesday: dayAvailabilitySchema,
  thursday: dayAvailabilitySchema,
  friday: dayAvailabilitySchema,
  saturday: dayAvailabilitySchema,
  sunday: dayAvailabilitySchema
});
var MINUTES_PER_DAY = 1440;
function minuteOf(time) {
  const [h, m] = time.split(":");
  return (parseInt(h ?? "0", 10) || 0) * 60 + (parseInt(m ?? "0", 10) || 0);
}
function toIntervals(start, end) {
  if (start === end) return [];
  if (end > start) return [[start, end]];
  return [
    [start, MINUTES_PER_DAY],
    [0, end]
  ];
}
function slotsOverlapMinutes(a, b) {
  const aIntervals = toIntervals(minuteOf(a.start), minuteOf(a.end));
  const bIntervals = toIntervals(minuteOf(b.start), minuteOf(b.end));
  return aIntervals.some(([as, ae]) => bIntervals.some(([bs, be]) => as < be && bs < ae));
}
z.object({
  logoUrl: z.url().max(500).nullable().optional(),
  primaryColor: hexColorSchema.optional()
});
z.object({
  email: z.email("Invalid email address").max(255),
  password: passwordSchema
});
z.object({
  certifiedLicensed: z.literal(true, {
    error: "You must certify that you hold a valid driver's license"
  }),
  certifiedInsured: z.literal(true, {
    error: "You must certify that you have commercial auto insurance"
  }),
  certifiedAuthorized: z.literal(true, {
    error: "You must certify that you are authorized to operate"
  }),
  termsAcceptedAt: z.iso.datetime()
  // Covers driver terms + indemnification
});
z.object({
  plan: z.literal("paid_driver"),
  companyId: z.guid().optional()
});
z.object({
  plan: z.literal("free_driver")
});
z.object({
  companyName: z.string().min(1, "Company name is required").max(255),
  phone: phoneSchema.optional(),
  address: z.string().max(500).optional()
});
var DRIVER_REQUIREMENT_DOCUMENT_TYPES = [
  "driver_license",
  "insurance",
  "vehicle_registration",
  "background_check"
];
var DEFAULT_REQUIRED_DOCUMENT_TYPES = [
  "driver_license",
  "insurance"
];
var DRIVER_REQUIREMENT_VEHICLE_FIELDS = [
  "make",
  "model",
  "year",
  "color",
  "licensePlate",
  "vin",
  "capacity"
];
var driverRequirementsSchema = z.object({
  requiredDocumentTypes: z.array(z.enum(DRIVER_REQUIREMENT_DOCUMENT_TYPES)).max(DRIVER_REQUIREMENT_DOCUMENT_TYPES.length).default([...DEFAULT_REQUIRED_DOCUMENT_TYPES]),
  requireVehicleInfo: z.boolean().default(false),
  requiredVehicleFields: z.array(z.enum(DRIVER_REQUIREMENT_VEHICLE_FIELDS)).max(DRIVER_REQUIREMENT_VEHICLE_FIELDS.length).refine((fields) => new Set(fields).size === fields.length, {
    message: "Vehicle requirement fields must be unique"
  }).meta({ uniqueItems: true }).optional(),
  autoRequestDriverDocuments: z.boolean().default(false)
});
var companyTierSchema = z.enum([
  "business",
  "business_plus",
  "business_ultimate"
]);
var companyRegistrationInfoSchema = z.object({
  companyName: z.string().min(1, "Company name is required").max(255),
  phone: phoneSchema,
  // Kept optional for pending applications created before timezone capture was
  // introduced. New web registrations always send a validated IANA zone.
  timezone: timezoneSchema.optional().nullable(),
  address: addressSchema.optional().nullable(),
  fleetSize: z.enum(["1", "2-5", "6-10", "11-25", "26+"]).optional().nullable(),
  termsAcceptedAt: z.iso.datetime(),
  // The company tier the applicant chose during registration. Persisted so the
  // approval modal can default to (and an admin can confirm/override) it, instead
  // of silently forcing every company onto Business (#297). Optional/nullable for
  // backward-compatibility with pending rows registered before it was captured.
  selectedPlan: companyTierSchema.optional().nullable(),
  // What the company will require from every driver (chosen at onboarding).
  driverRequirements: driverRequirementsSchema.optional()
});
companyRegistrationInfoSchema.extend({
  selectedPlan: companyTierSchema
});
var baseRegisterSchema = z.object({
  email: z.email("Invalid email address").max(255),
  password: passwordSchema,
  name: z.string().min(1, "Name is required").max(255),
  phone: z.union([
    z.literal(""),
    phoneSchema
  ]).optional().transform((val) => val === "" ? void 0 : val),
  /**
   * When true, creates a solo driver account with:
   * - Auto-created company in the driver's name
   * - Driver role only (no admin access)
   * - Free driver tier (can upgrade later)
   * - Requires additional driver fields (license, insurance)
   * When false or undefined, creates a customer account (no company).
   */
  registerAsDriver: z.boolean().optional().default(false),
  /**
   * When true, creates a company admin account that:
   * - Requires platform admin approval
   * - Once approved, creates a new company with admin role
   * - Requires additional company fields
   */
  registerAsCompanyAdmin: z.boolean().optional().default(false)
});
var driverRegisterFieldsSchema = z.object({
  selectedPlan: z.enum(["free_driver", "paid_driver"]).optional(),
  certifiedLicensed: z.boolean().optional(),
  certifiedInsured: z.boolean().optional(),
  certifiedAuthorized: z.boolean().optional(),
  agreedToDriverTerms: z.boolean().optional()
});
var companyAdminRegisterFieldsSchema = z.object({
  companyName: z.string().min(1).max(255).optional(),
  businessAddress: z.string().max(500).optional().nullable(),
  fleetSize: z.enum(["1", "2-5", "6-10", "11-25", "26+"]).optional().nullable(),
  agreedToCompanyTerms: z.boolean().optional()
});
baseRegisterSchema.extend(driverRegisterFieldsSchema.shape).extend(companyAdminRegisterFieldsSchema.shape).superRefine((data, ctx) => {
  if (data.registerAsDriver && data.registerAsCompanyAdmin) {
    ctx.addIssue({
      code: "custom",
      message: "Cannot register as both driver and company admin",
      path: ["registerAsDriver"]
    });
    return;
  }
  if (data.registerAsDriver) {
    if (!data.selectedPlan) {
      ctx.addIssue({
        code: "custom",
        message: "Please select a plan",
        path: ["selectedPlan"]
      });
    }
    if (!data.certifiedLicensed) {
      ctx.addIssue({
        code: "custom",
        message: "You must certify that you hold a valid driver's license",
        path: ["certifiedLicensed"]
      });
    }
    if (!data.certifiedInsured) {
      ctx.addIssue({
        code: "custom",
        message: "You must certify that you have commercial auto insurance",
        path: ["certifiedInsured"]
      });
    }
    if (!data.certifiedAuthorized) {
      ctx.addIssue({
        code: "custom",
        message: "You must certify that you are authorized to operate",
        path: ["certifiedAuthorized"]
      });
    }
    if (!data.agreedToDriverTerms) {
      ctx.addIssue({
        code: "custom",
        message: "You must agree to the Driver Terms of Service",
        path: ["agreedToDriverTerms"]
      });
    }
  }
  if (data.registerAsCompanyAdmin) {
    if (!data.companyName) {
      ctx.addIssue({
        code: "custom",
        message: "Company name is required",
        path: ["companyName"]
      });
    }
    if (!data.phone) {
      ctx.addIssue({
        code: "custom",
        message: "Phone number is required for company registration",
        path: ["phone"]
      });
    }
    if (!data.agreedToCompanyTerms) {
      ctx.addIssue({
        code: "custom",
        message: "You must agree to the Company Terms",
        path: ["agreedToCompanyTerms"]
      });
    }
  }
});
z.object({
  email: z.email("Invalid email address").max(255)
});
z.object({
  token: z.string().min(1),
  password: passwordSchema
});
z.object({
  companyId: uuidSchema,
  rideTypeId: uuidSchema,
  serviceLevelId: uuidSchema.optional(),
  // Stops mode (multi-stop): coords are preferred for faster pricing.
  stops: z.array(stopInputSchema).max(8).optional(),
  // Legacy mode: flat pickup/dropoff (required when stops absent).
  pickupLocation: z.string().min(1).max(500).optional(),
  dropoffLocation: z.string().min(1).max(500).optional(),
  pickupDatetime: z.iso.datetime(),
  pickupTimezone: timezoneSchema,
  passengerCount: z.number().int().min(1).max(50).optional().default(1)
});
z.object({
  companyId: uuidSchema,
  // #979: normalize-then-validate (see {@link flightCodeSchema}); required
  // (min length 1, via the extra `.refine` below) unlike the CREATE schema's
  // optional flightNumber.
  flightNumber: flightCodeSchema(20, "Flight number").refine((val) => val.length >= 1, {
    message: "Please enter a flight number"
  }),
  // #979: airlineCode was max(50) — tightened to max(10) (the actual Postgres
  // FlightInfo.airlineCode VarChar(10) bound) + normalize, mirroring the
  // createReservationSchema fix.
  airlineCode: flightCodeSchema(10, "Airline code").optional(),
  pickupDatetime: z.iso.datetime({ error: "Invalid pickup date and time" }).optional(),
  pickupTimezone: timezoneSchema.optional(),
  subType: rideSubTypeSchema.optional()
});
var inquiryStatusSchema = z.enum([
  INQUIRY_STATUS.NEW,
  INQUIRY_STATUS.CONTACTED,
  INQUIRY_STATUS.CONVERTED,
  INQUIRY_STATUS.CLOSED
]);
var inquiryPlanOptionSchema = z.enum([
  INQUIRY_PLAN_OPTIONS.BUSINESS,
  INQUIRY_PLAN_OPTIONS.BUSINESS_PLUS,
  INQUIRY_PLAN_OPTIONS.BUSINESS_ULTIMATE,
  INQUIRY_PLAN_OPTIONS.HELP_ME_CHOOSE
]);
var fleetSizeSchema = z.enum([
  FLEET_SIZES.ONE,
  FLEET_SIZES.TWO_TO_FIVE,
  FLEET_SIZES.SIX_TO_TEN,
  FLEET_SIZES.ELEVEN_TO_TWENTYFIVE,
  FLEET_SIZES.TWENTYSIX_PLUS
]);
z.enum([
  INQUIRY_SOURCE.REGISTRATION,
  INQUIRY_SOURCE.HOMEPAGE_DEMO
]);
var createInquirySchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  email: emailSchema,
  phone: phoneSchema,
  companyName: z.string().min(1, "Company name is required").max(255),
  desiredPlan: inquiryPlanOptionSchema,
  fleetSize: fleetSizeSchema.optional(),
  country: z.string().min(1, "Country is required").max(100)
});
var homepageInquirySchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  email: emailSchema,
  companyName: z.string().min(1, "Company name is required").max(255),
  fleetSize: fleetSizeSchema,
  phone: phoneSchema.optional()
});
z.discriminatedUnion("source", [
  createInquirySchema.extend({ source: z.literal(INQUIRY_SOURCE.REGISTRATION) }),
  homepageInquirySchema.extend({ source: z.literal(INQUIRY_SOURCE.HOMEPAGE_DEMO) })
]);
z.object({
  status: inquiryStatusSchema.optional(),
  notes: z.string().max(2e3).optional().nullable()
});
z.object({
  status: inquiryStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0)
});
z.object({
  name: z.string().min(1, "Name is required").max(255),
  email: emailSchema,
  phone: phoneSchema,
  companyName: z.string().min(1, "Company name is required").max(255),
  desiredPlan: inquiryPlanOptionSchema,
  fleetSize: fleetSizeSchema.optional(),
  country: z.string().min(1, "Country is required").max(100)
});
var GUEST_MESSAGE_MAX_LENGTH = 2e3;
z.object({
  body: z.string().trim().min(1, "Message cannot be empty").max(GUEST_MESSAGE_MAX_LENGTH, "Message cannot exceed 2000 characters")
});
var cancelSelfServiceReservationSchema = z.object({
  acknowledgeLateFee: z.boolean().optional()
});
var acknowledgedLateCancellationPolicySchema = z.object({
  lateCancelWindowHours: z.number().int("Acknowledged late-cancel window must be a whole number of hours").min(1, "Acknowledged late-cancel window must be at least one hour").max(336, "Acknowledged late-cancel window cannot exceed 336 hours (14 days)"),
  lateCancelFeePercent: z.number().int("Acknowledged late-cancel fee must be a whole percent").min(1, "Acknowledged late-cancel fee must be at least 1%").max(100, "Acknowledged late-cancel fee cannot exceed 100%")
});
cancelSelfServiceReservationSchema.extend({
  acknowledgedLateCancellationPolicy: acknowledgedLateCancellationPolicySchema.optional()
});
var SETUP_GUIDE_STEP_KEYS = [
  "services",
  "driverRequirements",
  "branding",
  "team",
  "vehicles",
  "payments",
  "widget",
  "firstBooking"
];
var setupGuideStepKeySchema = z.enum(SETUP_GUIDE_STEP_KEYS);
z.object({
  acknowledge: setupGuideStepKeySchema.optional(),
  dismissed: z.boolean().optional()
}).strict();
z.object({
  dismissed: z.boolean()
}).strict();
z.object({
  enabled: z.boolean()
}).strict();
z.object({
  acknowledged: z.boolean()
}).strict();

// src/utils/availabilityExpansion.ts
var JS_DAY_TO_KEY = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];
function weekdayKeyForDate(dateStr) {
  const jsDay = (/* @__PURE__ */ new Date(`${dateStr}T12:00:00Z`)).getUTCDay();
  return JS_DAY_TO_KEY[jsDay] ?? "monday";
}
function addUtcDays(dateStr, days) {
  const ms = Date.parse(`${dateStr}T00:00:00.000Z`) + days * 24 * 60 * 60 * 1e3;
  const iso = new Date(ms).toISOString();
  return iso.slice(0, 10);
}
function clockToMinutes(clock) {
  const [hours, minutes] = clock.split(":");
  return Number(hours ?? 0) * 60 + Number(minutes ?? 0);
}
function isOvernightSlot(slot) {
  return clockToMinutes(slot.end) < clockToMinutes(slot.start);
}
function msSinceMidnightToClock(offsetMs) {
  const totalMinutes = Math.round(offsetMs / 6e4);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
var DEFAULT_MAX_DAYS = 14;
function expandRecurringAvailability(schedule, range, options = {}) {
  const maxDays = options.maxDays ?? DEFAULT_MAX_DAYS;
  const startMs = Date.parse(range.start);
  const endMs = Date.parse(range.end);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
    return [];
  }
  const matches = [];
  const endDateStr = new Date(endMs).toISOString().slice(0, 10);
  let dateStr = new Date(startMs).toISOString().slice(0, 10);
  for (let i = 0; i < maxDays; i++) {
    const dayStartMs = Date.parse(`${dateStr}T00:00:00.000Z`);
    const dayEndMs = dayStartMs + 24 * 60 * 60 * 1e3;
    const clipStartMs = Math.max(startMs, dayStartMs);
    const clipEndMs = Math.min(endMs, dayEndMs);
    if (clipEndMs > clipStartMs && (options.isDateAvailable?.(dateStr) ?? true)) {
      const weekday = weekdayKeyForDate(dateStr);
      const windowSlot = {
        start: msSinceMidnightToClock(clipStartMs - dayStartMs),
        end: msSinceMidnightToClock(clipEndMs - dayStartMs)
      };
      for (const slot of schedule[weekday]) {
        const sameDaySlot = isOvernightSlot(slot) ? { start: slot.start, end: "24:00" } : slot;
        if (slotsOverlapMinutes(windowSlot, sameDaySlot)) {
          matches.push({ date: dateStr, weekday, slot });
        }
      }
      const previousDate = addUtcDays(dateStr, -1);
      const previousWeekday = weekdayKeyForDate(previousDate);
      for (const slot of schedule[previousWeekday]) {
        if (isOvernightSlot(slot) && slotsOverlapMinutes(windowSlot, { start: "00:00", end: slot.end })) {
          const alreadyMatched = matches.some(
            (match) => match.date === previousDate && match.weekday === previousWeekday && match.slot.start === slot.start && match.slot.end === slot.end
          );
          if (!alreadyMatched) {
            matches.push({ date: previousDate, weekday: previousWeekday, slot });
          }
        }
      }
    }
    if (dateStr === endDateStr) break;
    dateStr = addUtcDays(dateStr, 1);
  }
  return matches;
}

// src/utils/zonedAvailability.ts
var DEFAULT_MAX_SCAN_DAYS = 32;
var HARD_MAX_SCAN_DAYS = 370;
var MS_PER_UTC_MARKER_DAY = 24 * 60 * 60 * 1e3;
function addCalendarDays(date, days) {
  const marker = /* @__PURE__ */ new Date(`${date}T00:00:00.000Z`);
  marker.setUTCDate(marker.getUTCDate() + days);
  return marker.toISOString().slice(0, 10);
}
function calendarDayCount(start, end) {
  const startMs = Date.parse(`${start}T00:00:00.000Z`);
  const endMs = Date.parse(`${end}T00:00:00.000Z`);
  return Math.floor((endMs - startMs) / MS_PER_UTC_MARKER_DAY) + 1;
}
function clockMinutes(clock) {
  const [hours, minutes] = clock.split(":");
  return Number(hours ?? 0) * 60 + Number(minutes ?? 0);
}
function resolveOccurrence(date, weekday, slot, timeZone) {
  const start = wallTimeToInstant(date, slot.start, timeZone, {
    nonexistent: "shiftForward",
    ambiguous: "earlier"
  });
  const crossesMidnight = clockMinutes(slot.end) < clockMinutes(slot.start);
  const usesEndOfDayBoundary = slot.end === "24:00";
  const endDate = crossesMidnight || usesEndOfDayBoundary ? addCalendarDays(date, 1) : date;
  const end = wallTimeToInstant(
    endDate,
    usesEndOfDayBoundary ? "00:00" : slot.end,
    timeZone,
    {
      nonexistent: "shiftForward",
      ambiguous: "later"
    }
  );
  if (!start || !end || end.getTime() <= start.getTime()) return null;
  return { date, weekday, slot, start, end };
}
function expandWeeklyAvailabilityInZone(schedule, range, options = {}) {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return [];
  }
  const localStart = instantToWallTime(range.start, range.timeZone);
  const localEnd = instantToWallTime(new Date(endMs - 1), range.timeZone);
  if (!localStart || !localEnd) return [];
  const scanStart = addCalendarDays(localStart.date, -1);
  const scanDays = calendarDayCount(scanStart, localEnd.date);
  const requestedMax = options.maxDays ?? DEFAULT_MAX_SCAN_DAYS;
  const maxDays = Math.min(
    HARD_MAX_SCAN_DAYS,
    Math.max(1, Math.floor(Number.isFinite(requestedMax) ? requestedMax : 0))
  );
  if (scanDays < 1 || scanDays > maxDays) return [];
  const occurrences = [];
  const seen = /* @__PURE__ */ new Set();
  for (let offset = 0; offset < scanDays; offset++) {
    const date = addCalendarDays(scanStart, offset);
    const weekday = weekdayKeyForDate(date);
    for (const slot of schedule[weekday]) {
      const occurrence = resolveOccurrence(date, weekday, slot, range.timeZone);
      if (!occurrence || occurrence.start.getTime() >= endMs || occurrence.end.getTime() <= startMs) {
        continue;
      }
      const key = `${date}\0${slot.start}\0${slot.end}\0${String(
        occurrence.start.getTime()
      )}\0${String(occurrence.end.getTime())}`;
      if (!seen.has(key)) {
        seen.add(key);
        occurrences.push(occurrence);
      }
    }
  }
  return occurrences.sort(
    (a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime()
  );
}
function mergeIntervals(intervals) {
  const ordered = intervals.filter(([start, end]) => end > start).sort(([aStart, aEnd], [bStart, bEnd]) => aStart - bStart || aEnd - bEnd);
  const merged = [];
  for (const [start, end] of ordered) {
    const previous = merged.at(-1);
    if (!previous || start > previous[1]) {
      merged.push([start, end]);
    } else {
      previous[1] = Math.max(previous[1], end);
    }
  }
  return merged;
}
function minuteClock(minutes) {
  if (minutes === 24 * 60) return "24:00";
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
    minutes % 60
  ).padStart(2, "0")}`;
}
function projectWeeklyAvailabilityDate(schedule, date) {
  const weekday = weekdayKeyForDate(date);
  const previousWeekday = weekdayKeyForDate(addCalendarDays(date, -1));
  const intervals = [];
  for (const slot of schedule[previousWeekday]) {
    const start = clockMinutes(slot.start);
    const end = clockMinutes(slot.end);
    if (end < start && end > 0) intervals.push([0, end]);
  }
  for (const slot of schedule[weekday]) {
    const start = clockMinutes(slot.start);
    const end = clockMinutes(slot.end);
    intervals.push(end < start ? [start, 24 * 60] : [start, end]);
  }
  return mergeIntervals(intervals).map(([start, end]) => ({
    start: minuteClock(start),
    end: minuteClock(end)
  }));
}
function isWindowWithinWeeklyAvailability(schedule, range, options = {}) {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return false;
  }
  const merged = mergeIntervals(
    expandWeeklyAvailabilityInZone(schedule, range, options).map((occurrence) => [
      occurrence.start.getTime(),
      occurrence.end.getTime()
    ])
  );
  return merged.some(([start, end]) => start <= startMs && end >= endMs);
}

// src/utils/timelineProjection.ts
var ESTIMATED_RIDE_MS = 2 * 60 * 60 * 1e3;
function reservationSpan(r) {
  const startMs = new Date(r.pickupDatetime).getTime();
  const recordedEndMs = r.dropoffDatetime ? new Date(r.dropoffDatetime).getTime() : Number.NaN;
  const endMs = Number.isFinite(recordedEndMs) && recordedEndMs > startMs ? recordedEndMs : startMs + ESTIMATED_RIDE_MS;
  return { startMs, endMs };
}
function correlateWindows(busyWindows, ownReservations, target) {
  const mine = ownReservations.filter(
    (reservation) => target.kind === "member" ? reservation.driverUserId === target.id : reservation.placeholderDriverId === target.id
  );
  const mineById = new Map(mine.map((reservation) => [reservation.id, reservation]));
  return busyWindows.map((w) => {
    if (w.kind !== "ride") {
      return { start: w.start, end: w.end, kind: "foreign" };
    }
    if (w.scope !== void 0 && w.scope !== "own") {
      return { start: w.start, end: w.end, kind: "foreign" };
    }
    if (target.kind === "placeholder" && Object.prototype.hasOwnProperty.call(w, "reservationId")) {
      const match2 = typeof w.reservationId === "string" ? mineById.get(w.reservationId) : void 0;
      if (!match2) {
        return { start: w.start, end: w.end, kind: "foreign" };
      }
      return {
        start: w.start,
        end: w.end,
        kind: "own",
        reservationId: match2.id,
        guestName: match2.guestName
      };
    }
    const winStart = new Date(w.start).getTime();
    const winEnd = new Date(w.end).getTime();
    const match = mine.find((r) => {
      const { startMs, endMs } = reservationSpan(r);
      return startMs < winEnd && endMs > winStart;
    });
    if (match) {
      return {
        start: w.start,
        end: w.end,
        kind: "own",
        reservationId: match.id,
        guestName: match.guestName
      };
    }
    return { start: w.start, end: w.end, kind: "foreign" };
  });
}
var TRANSITION_SCAN_MS = 15 * 60 * 1e3;
var transitionCache = /* @__PURE__ */ new Map();
function transitionsForDay(date, timeZone, dayStartMs, dayEndExclusiveMs) {
  const key = `${timeZone}|${date}`;
  if (transitionCache.has(key)) {
    return transitionCache.get(key) ?? null;
  }
  let cursorMs = dayStartMs;
  let cursorOffset = zoneOffsetMinutes(new Date(cursorMs), timeZone);
  if (cursorOffset === null) {
    transitionCache.set(key, null);
    return null;
  }
  const transitions = [];
  while (cursorMs < dayEndExclusiveMs) {
    const probeMs = Math.min(cursorMs + TRANSITION_SCAN_MS, dayEndExclusiveMs);
    const probeOffset = zoneOffsetMinutes(new Date(probeMs), timeZone);
    if (probeOffset === null) {
      transitionCache.set(key, null);
      return null;
    }
    if (probeOffset === cursorOffset) {
      cursorMs = probeMs;
      continue;
    }
    let low = cursorMs + 1;
    let high = probeMs;
    while (low < high) {
      const mid = low + Math.floor((high - low) / 2);
      const midOffset = zoneOffsetMinutes(new Date(mid), timeZone);
      if (midOffset === null) {
        transitionCache.set(key, null);
        return null;
      }
      if (midOffset === cursorOffset) low = mid + 1;
      else high = mid;
    }
    const afterOffset = zoneOffsetMinutes(new Date(low), timeZone);
    if (afterOffset === null) {
      transitionCache.set(key, null);
      return null;
    }
    if (afterOffset !== cursorOffset) {
      transitions.push({ atMs: low, beforeOffset: cursorOffset, afterOffset });
    }
    cursorMs = low;
    cursorOffset = afterOffset;
  }
  transitionCache.set(key, transitions);
  return transitions;
}
function wallMinuteAt(instantMs, timeZone) {
  const wall = instantToWallTime(new Date(instantMs), timeZone);
  if (!wall) return null;
  const hour = Number(wall.time.slice(0, 2));
  const minute = Number(wall.time.slice(3, 5));
  return Number.isFinite(hour) && Number.isFinite(minute) ? hour * 60 + minute : null;
}
function projectWindowToDaySegments(w, date, timeZone) {
  const start = new Date(w.start);
  const end = new Date(w.end);
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return [];
  const dayStart = startOfDayInZone(date, timeZone);
  const dayEnd = endOfDayInZone(date, timeZone);
  if (!dayStart || !dayEnd) return [];
  const dayStartMs = dayStart.getTime();
  const dayEndExclusiveMs = dayEnd.getTime() + 1;
  const clippedStartMs = Math.max(startMs, dayStartMs);
  const clippedEndMs = Math.min(endMs, dayEndExclusiveMs);
  if (clippedEndMs <= clippedStartMs) return [];
  const dayTransitions = transitionsForDay(date, timeZone, dayStartMs, dayEndExclusiveMs);
  if (!dayTransitions) return [];
  const transitions = dayTransitions.filter(
    (transition) => transition.atMs > clippedStartMs && transition.atMs <= clippedEndMs
  );
  const transitionAt = new Set(transitions.map((transition) => transition.atMs));
  const boundaries = [
    clippedStartMs,
    ...transitions.filter((transition) => transition.atMs < clippedEndMs).map((transition) => transition.atMs),
    clippedEndMs
  ];
  const segments = [];
  for (let i = 0; i < boundaries.length - 1; i += 1) {
    const segmentStartMs = boundaries[i];
    const segmentEndMs = boundaries[i + 1];
    if (segmentStartMs === void 0 || segmentEndMs === void 0) continue;
    const startMin = wallMinuteAt(segmentStartMs, timeZone);
    const endMin = segmentEndMs === dayEndExclusiveMs ? 1440 : transitionAt.has(segmentEndMs) ? (() => {
      const lastMinute = wallMinuteAt(segmentEndMs - 1, timeZone);
      return lastMinute === null ? null : Math.min(1440, lastMinute + 1);
    })() : wallMinuteAt(segmentEndMs, timeZone);
    if (startMin === null || endMin === null || endMin <= startMin) continue;
    segments.push({ startMin, endMin });
  }
  return segments;
}
function projectWallWindowsToDaySegments(windows, date, timeZone) {
  const dayEnd = endOfDayInZone(date, timeZone);
  if (!dayEnd) return [];
  const nextDayStart = new Date(dayEnd.getTime() + 1);
  const segments = windows.flatMap((window) => {
    const start = wallTimeToInstant(date, window.start, timeZone, {
      nonexistent: "shiftForward",
      ambiguous: "earlier"
    });
    const end = window.end === "24:00" ? nextDayStart : wallTimeToInstant(date, window.end, timeZone, {
      nonexistent: "shiftForward",
      ambiguous: "later"
    });
    if (!start || !end) return [];
    return projectWindowToDaySegments(
      { start: start.toISOString(), end: end.toISOString() },
      date,
      timeZone
    );
  });
  return segments.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin).reduce((merged, segment) => {
    const previous = merged.at(-1);
    if (!previous || segment.startMin > previous.endMin) {
      merged.push({ ...segment });
    } else {
      previous.endMin = Math.max(previous.endMin, segment.endMin);
    }
    return merged;
  }, []);
}

// src/utils/capitalize.ts
function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

// src/utils/personPhoto.ts
var ALLOWED_PHOTO_PROTOCOLS = /* @__PURE__ */ new Set(["http:", "https:"]);
function initialsFor(name) {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words.slice(0, 2).map((w) => (w[0] ?? "").toUpperCase()).join("");
}
function safePhotoUrl(url) {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  return ALLOWED_PHOTO_PROTOCOLS.has(parsed.protocol) ? trimmed : null;
}
function tilePhotoUrl(thumbUrl, photoUrl) {
  return safePhotoUrl(thumbUrl) ?? safePhotoUrl(photoUrl);
}

// src/utils/pickupOrder.ts
function sortByPickup(rides, direction = "asc") {
  const sign = direction === "asc" ? 1 : -1;
  return [...rides].sort(
    (a, b) => sign * (new Date(effectivePickupInstant(a)).getTime() - new Date(effectivePickupInstant(b)).getTime())
  );
}

// src/utils/driverRidePresentation.ts
var DRIVER_STATUS_LABELS = {
  pending: "Waiting on the company",
  confirmed: "Waiting for a driver",
  assigned: "Ride request",
  in_progress: "Trip in progress",
  completed: "Trip completed",
  cancelled: "Ride cancelled"
};
function driverStatusLabel(status) {
  return DRIVER_STATUS_LABELS[status] ?? "Check with dispatch";
}
function plural(count, noun) {
  return `${String(count)} ${noun}${count === 1 ? "" : "s"}`;
}
function requirementChips(ride) {
  const chips = [
    { label: plural(ride.passengerCount, "passenger"), kind: "info" },
    { label: ride.luggageCount > 0 ? plural(ride.luggageCount, "bag") : "No bags", kind: "info" }
  ];
  if (ride.carSeatsNeeded) chips.push({ label: "Child seat(s)", kind: "attention" });
  if (ride.oversizedLuggage) chips.push({ label: "Oversized luggage", kind: "attention" });
  if (ride.wheelchairAccess) chips.push({ label: "Wheelchair access", kind: "attention" });
  return chips;
}
function formatPickupDay(iso, tz) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
      month: "short",
      day: "numeric"
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}
function formatPickupTime(iso, tz) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit"
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleTimeString();
  }
}
function isExpiredRideOffer(ride, now = /* @__PURE__ */ new Date()) {
  if (ride.status !== "assigned" || ride.driverAcceptedAt) return false;
  const pickupMs = new Date(effectivePickupInstant(ride)).getTime();
  return Number.isFinite(pickupMs) && pickupMs <= now.getTime();
}
function friendlyDriverError(msg, refreshInstruction) {
  const m = msg.toLowerCase();
  if (m.includes("already") || m.includes("transition")) {
    return `This ride was already taken or changed. ${refreshInstruction}`;
  }
  if (m.includes("vehicle")) return "Pick your car first.";
  if (m.includes("accept")) return "Accept the ride before starting it.";
  return msg;
}

// src/utils/flightChipStatus.ts
var FLIGHT_GLANCE_STATUS_PRESENTATION = {
  scheduled: { label: "On time", status: "on-time" },
  on_time: { label: "On time", status: "on-time" },
  active: { label: "In the air", status: "on-time" },
  departed: { label: "In the air", status: "on-time" },
  en_route: { label: "In the air", status: "on-time" },
  in_air: { label: "In the air", status: "on-time" },
  arrived: { label: "Landed", status: "on-time" },
  landed: { label: "Landed", status: "on-time" },
  delayed: { label: "Delayed", status: "delayed" },
  cancelled: { label: "Cancelled", status: "cancelled" },
  canceled: { label: "Cancelled", status: "cancelled" },
  diverted: { label: "Diverted", status: "delayed" }
};
function flightGlanceStatusPresentation(status) {
  const key = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const fallbackLabel = status.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase()) || "Unknown";
  return FLIGHT_GLANCE_STATUS_PRESENTATION[key] ?? {
    label: fallbackLabel,
    status: "unknown"
  };
}
function toFlightChipStatus(status) {
  switch (status) {
    case "delayed":
      return "delayed";
    case "departed":
    case "arrived":
      return "on-time";
    case "cancelled":
      return "cancelled";
    case "scheduled":
    default:
      return "unknown";
  }
}
function formatFlightDelayLabel(baseLabel, delayMinutes) {
  return delayMinutes != null && delayMinutes > 0 ? `${baseLabel} ${String(delayMinutes)} min` : baseLabel;
}
function flightBoardStatusRole(status) {
  switch (status) {
    case "delayed":
      return "warning";
    case "cancelled":
      return "destructive";
    case "departed":
      return "live";
    case "arrived":
      return "success";
    case "scheduled":
    default:
      return "neutral";
  }
}

// src/utils/flightBoardPresentation.ts
var FLIGHT_BOARD_STALE_THRESHOLD_MINUTES = 20;
var FLIGHT_BOARD_STATUS_ROLE_CLASS = {
  warning: "text-warning-400",
  destructive: "text-destructive-500",
  live: "text-live-500",
  success: "text-success-500",
  neutral: "text-ink-foreground"
};
var STATUS_LABELS = {
  scheduled: "Scheduled",
  delayed: "Delayed",
  departed: "Departed",
  arrived: "Arrived",
  cancelled: "Cancelled"
};
function flightBoardStatusLabel(row) {
  const base = STATUS_LABELS[row.status] ?? capitalize(row.status);
  return row.status === "delayed" ? formatFlightDelayLabel(base, row.arrDelayMinutes ?? row.depDelayMinutes) : base;
}
function flightBoardGateLabel(row) {
  const parts = [
    row.gate ? `Gate ${row.gate}` : null,
    row.terminal ? `T${row.terminal}` : null
  ].filter((p) => p != null);
  return parts.length > 0 ? parts.join(" \xB7 ") : "\u2014";
}
function minutesAgo(iso, now) {
  if (!iso) return null;
  const then = parseISO(iso).getTime();
  return Number.isNaN(then) ? null : Math.floor((now - then) / 6e4);
}
function oldestFlightBoardUpdateMinutesAgo(rows, now = Date.now()) {
  const ages = rows.map((r) => minutesAgo(r.lastUpdated, now)).filter((m) => m != null);
  return ages.length === 0 ? null : Math.max(...ages);
}

// src/utils/driverStartEligibility.ts
var DRIVER_START_LEAD_MINUTES = 90;
var EARLY_START_NOTATION_THRESHOLD_MINUTES = 30;
var DRIVER_START_LEAD_MS = DRIVER_START_LEAD_MINUTES * 60 * 1e3;
var EARLY_START_NOTATION_THRESHOLD_MS = EARLY_START_NOTATION_THRESHOLD_MINUTES * 60 * 1e3;
function finiteEpoch(value) {
  const epoch = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(epoch) ? epoch : null;
}
function isValidIanaTimezone(timezone) {
  if (typeof timezone !== "string" || timezone.trim().length === 0) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(0);
    return true;
  } catch {
    return false;
  }
}
function getEarlyStartContext(params) {
  if (params.startedAt === null) return null;
  const pickupEpoch = finiteEpoch(params.pickupDatetime);
  const startedEpoch = finiteEpoch(params.startedAt);
  if (pickupEpoch === null || startedEpoch === null) return null;
  const earlyByMs = pickupEpoch - startedEpoch;
  if (!Number.isFinite(earlyByMs) || earlyByMs <= EARLY_START_NOTATION_THRESHOLD_MS) {
    return null;
  }
  return {
    scheduledPickupAt: new Date(pickupEpoch).toISOString(),
    actualStartAt: new Date(startedEpoch).toISOString(),
    earlyByMinutes: Math.ceil(earlyByMs / (60 * 1e3))
  };
}
function formatPickupLocalInstant(params) {
  if (!isValidIanaTimezone(params.pickupTimezone)) return null;
  const epoch = finiteEpoch(params.instant);
  if (epoch === null) return null;
  try {
    const instant = new Date(epoch);
    const day = new Intl.DateTimeFormat("en-US", {
      timeZone: params.pickupTimezone,
      month: "short",
      day: "numeric"
    }).format(instant);
    const time = new Intl.DateTimeFormat("en-US", {
      timeZone: params.pickupTimezone,
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short"
    }).format(instant);
    return `${day} at ${time}`;
  } catch {
    return null;
  }
}
function formatEarlyStartContext(params) {
  const context = getEarlyStartContext(params);
  if (!context) return null;
  const scheduled = formatPickupLocalInstant({
    instant: context.scheduledPickupAt,
    pickupTimezone: params.pickupTimezone
  });
  const actual = formatPickupLocalInstant({
    instant: context.actualStartAt,
    pickupTimezone: params.pickupTimezone
  });
  if (!scheduled || !actual) return null;
  return {
    summary: `Started ${String(context.earlyByMinutes)} min early`,
    scheduled: `Scheduled ${scheduled}`,
    actual: `Actual ${actual}`
  };
}
function getDriverStartEligibility(params) {
  const pickupEpoch = finiteEpoch(params.pickupDatetime);
  const nowEpoch = finiteEpoch(params.now ?? /* @__PURE__ */ new Date());
  if (pickupEpoch === null || nowEpoch === null || !isValidIanaTimezone(params.pickupTimezone)) {
    return { canStart: false, reason: "invalid_schedule", availableAt: null };
  }
  const availableAtEpoch = pickupEpoch - DRIVER_START_LEAD_MS;
  if (!Number.isFinite(availableAtEpoch)) {
    return { canStart: false, reason: "invalid_schedule", availableAt: null };
  }
  const availableAt = new Date(availableAtEpoch).toISOString();
  if (nowEpoch < availableAtEpoch) {
    return { canStart: false, reason: "too_early", availableAt };
  }
  return { canStart: true, availableAt };
}
function formatDriverStartAvailability(params) {
  if (params.availableAt === null) return null;
  const localInstant = formatPickupLocalInstant({
    instant: params.availableAt,
    pickupTimezone: params.pickupTimezone
  });
  return localInstant ? `Start available ${localInstant} (pickup time).` : null;
}

// src/utils/reservationActionCapabilities.ts
var RESERVATION_ACTION_START_PROMINENCE_HOURS = 6;
var RESERVATION_ACTION_OPERATOR_REVIEW_HOURS = 12;
var RESERVATION_ACTION_FALLBACK_DURATION_MINUTES = 120;
var HOUR_MS = 60 * 60 * 1e3;
var MINUTE_MS = 60 * 1e3;
var START_PROMINENCE_LEAD_MS = RESERVATION_ACTION_START_PROMINENCE_HOURS * HOUR_MS;
var OPERATOR_REVIEW_AFTER_MS = RESERVATION_ACTION_OPERATOR_REVIEW_HOURS * HOUR_MS;
var FALLBACK_DURATION_MS = RESERVATION_ACTION_FALLBACK_DURATION_MINUTES * MINUTE_MS;
var RESERVATION_ACTION_CAPABILITY_KEYS = [
  "canEdit",
  "canCancel",
  "canAcknowledge",
  "canConfirmForDriver",
  "canStart",
  "canStartEarly",
  "canStartLate",
  "canComplete",
  "canCompleteWithoutStart",
  "canReportIssue"
];
var RESERVATION_ACTION_REASON_CODES = [
  "available",
  "early_start_confirmation_required",
  "early_confirmation_not_required",
  "late_start_available",
  "retrospective_completion_available",
  "operator_recovery_available",
  "operator_resolution_available",
  "implementation_unavailable",
  "terminal_reservation",
  "wrong_lifecycle",
  "invalid_schedule",
  "not_assigned_driver",
  "not_owning_operator",
  "not_rider_or_owning_operator",
  "missing_assigned_driver",
  "missing_vehicle",
  "ineligible_vehicle",
  "awaiting_driver_acknowledgement",
  "already_acknowledged",
  "before_pickup",
  "pickup_already_passed",
  "after_expected_end",
  "operator_resolution_required"
];
var RESERVATION_ACTION_VEHICLE_ADVISORIES = [
  "missing_vehicle",
  "wheelchair_vehicle_missing"
];
var EMPTY_TIMING = {
  effectivePickupAt: null,
  startProminenceAt: null,
  normalStartAt: null,
  expectedEndAt: null,
  operatorReviewAt: null
};
function finiteEpoch2(value) {
  const epoch = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(epoch) ? epoch : null;
}
function finiteIso(epoch) {
  if (!Number.isFinite(epoch)) return null;
  try {
    return new Date(epoch).toISOString();
  } catch {
    return null;
  }
}
function expectedEndEpoch(input, effectivePickupEpoch) {
  if (input.dropoffDatetime !== null && input.dropoffDatetime !== void 0) {
    const dropoffEpoch = finiteEpoch2(input.dropoffDatetime);
    if (dropoffEpoch !== null && dropoffEpoch > effectivePickupEpoch) {
      return dropoffEpoch;
    }
  }
  const durationMinutes = input.durationMinutes;
  if (durationMinutes !== null && durationMinutes !== void 0 && Number.isFinite(durationMinutes) && durationMinutes > 0) {
    const durationMs = durationMinutes * MINUTE_MS;
    if (Number.isFinite(durationMs)) return effectivePickupEpoch + durationMs;
  }
  return effectivePickupEpoch + FALLBACK_DURATION_MS;
}
function evaluateReservationActionTiming(input) {
  if (input.pickupDatetime === null) {
    return { timePhase: "unscheduled", timing: EMPTY_TIMING, startEligibility: null };
  }
  const bookedPickupEpoch = finiteEpoch2(input.pickupDatetime);
  const effectivePickupValue = input.effectivePickupDatetime ?? input.pickupDatetime;
  const effectivePickupEpoch = finiteEpoch2(effectivePickupValue);
  const nowEpoch = finiteEpoch2(input.now);
  if (bookedPickupEpoch === null || effectivePickupEpoch === null || nowEpoch === null) {
    return { timePhase: "invalid_schedule", timing: EMPTY_TIMING, startEligibility: null };
  }
  const startEligibility = getDriverStartEligibility({
    pickupDatetime: effectivePickupEpoch,
    pickupTimezone: input.pickupTimezone,
    now: nowEpoch
  });
  if (!startEligibility.canStart && startEligibility.reason === "invalid_schedule") {
    return { timePhase: "invalid_schedule", timing: EMPTY_TIMING, startEligibility: null };
  }
  const derivedExpectedEndEpoch = expectedEndEpoch(input, effectivePickupEpoch);
  const reviewByPickupEpoch = effectivePickupEpoch + OPERATOR_REVIEW_AFTER_MS;
  const operatorReviewEpoch = Math.max(reviewByPickupEpoch, derivedExpectedEndEpoch);
  const effectivePickupAt = finiteIso(effectivePickupEpoch);
  const startProminenceAt = finiteIso(effectivePickupEpoch - START_PROMINENCE_LEAD_MS);
  const expectedEndAt = finiteIso(derivedExpectedEndEpoch);
  const operatorReviewAt = finiteIso(operatorReviewEpoch);
  if (effectivePickupAt === null || startProminenceAt === null || expectedEndAt === null || operatorReviewAt === null || startEligibility.availableAt === null) {
    return { timePhase: "invalid_schedule", timing: EMPTY_TIMING, startEligibility: null };
  }
  let timePhase;
  if (nowEpoch > derivedExpectedEndEpoch) {
    timePhase = nowEpoch >= operatorReviewEpoch ? "operator_only" : "retrospective";
  } else if (nowEpoch >= effectivePickupEpoch) {
    timePhase = "late_start";
  } else if (nowEpoch < effectivePickupEpoch - START_PROMINENCE_LEAD_MS) {
    timePhase = "early";
  } else {
    timePhase = "ready";
  }
  return {
    timePhase,
    timing: {
      effectivePickupAt,
      startProminenceAt,
      normalStartAt: startEligibility.availableAt,
      expectedEndAt,
      operatorReviewAt
    },
    startEligibility
  };
}
function emptyCapabilities() {
  return {
    canEdit: false,
    canCancel: false,
    canAcknowledge: false,
    canConfirmForDriver: false,
    canStart: false,
    canStartEarly: false,
    canStartLate: false,
    canComplete: false,
    canCompleteWithoutStart: false,
    canReportIssue: false
  };
}
function defaultReasons() {
  return {
    canEdit: "wrong_lifecycle",
    canCancel: "wrong_lifecycle",
    canAcknowledge: "wrong_lifecycle",
    canConfirmForDriver: "wrong_lifecycle",
    canStart: "wrong_lifecycle",
    canStartEarly: "wrong_lifecycle",
    canStartLate: "wrong_lifecycle",
    canComplete: "wrong_lifecycle",
    canCompleteWithoutStart: "wrong_lifecycle",
    canReportIssue: "wrong_lifecycle"
  };
}
function isTerminal(status) {
  return status === RESERVATION_STATUS.COMPLETED || status === RESERVATION_STATUS.CANCELLED;
}
function isPreStart(status) {
  return status === RESERVATION_STATUS.PENDING || status === RESERVATION_STATUS.CONFIRMED || status === RESERVATION_STATUS.ASSIGNED;
}
function invalidTimePhase(timePhase) {
  return timePhase === "unscheduled" || timePhase === "invalid_schedule";
}
function vehicleEligibilityBlock(vehicleEligibility, missingVehiclePolicy = "block") {
  if (vehicleEligibility === "missing" && missingVehiclePolicy === "block") {
    return "missing_vehicle";
  }
  if (vehicleEligibility === "ineligible") return "ineligible_vehicle";
  return null;
}
function assignmentActionBlock(input, timePhase, allowOwningOperator, owningOperatorImplementationAvailable = false) {
  if (isTerminal(input.status)) return "terminal_reservation";
  if (input.status !== RESERVATION_STATUS.ASSIGNED) return "wrong_lifecycle";
  if (timePhase === "operator_only") return "operator_resolution_required";
  if (!input.actor.isAssignedDriver) {
    if (!allowOwningOperator || !input.actor.isOwningOperator) {
      return "not_assigned_driver";
    }
    if (!owningOperatorImplementationAvailable) {
      return "implementation_unavailable";
    }
  }
  if (!input.hasAssignedDriver) return "missing_assigned_driver";
  if (!input.driverAcceptedAt) return "awaiting_driver_acknowledgement";
  const vehicleBlock = vehicleEligibilityBlock(
    input.vehicleEligibility,
    input.missingVehiclePolicy
  );
  if (vehicleBlock) return vehicleBlock;
  if (invalidTimePhase(timePhase)) return "invalid_schedule";
  return null;
}
function withoutImpossibleDriverActor(input) {
  if (input.assignedDriverKind !== "placeholder" || !input.actor.isAssignedDriver) {
    return input;
  }
  return { ...input, actor: { ...input.actor, isAssignedDriver: false } };
}
function evaluateReservationActionCapabilities(rawInput) {
  const input = withoutImpossibleDriverActor(rawInput);
  const derived = evaluateReservationActionTiming(input);
  const lifecycle = getReservationStage({
    status: input.status,
    driverAcceptedAt: input.driverAcceptedAt
  });
  const acknowledgement = input.status !== RESERVATION_STATUS.ASSIGNED ? "not_applicable" : input.driverAcceptedAt ? "confirmed" : "awaiting";
  const issueState = input.issueState ?? "none";
  const implementation = {
    owningOperatorEdit: input.implementation?.owningOperatorEdit ?? false,
    owningOperatorCancellation: input.implementation?.owningOperatorCancellation ?? false,
    owningOperatorStart: input.implementation?.owningOperatorStart ?? false,
    owningOperatorComplete: input.implementation?.owningOperatorComplete ?? false,
    assignedRiderCancellation: input.implementation?.assignedRiderCancellation ?? false,
    operatorConfirmForDriver: input.implementation?.operatorConfirmForDriver ?? false,
    reportIssue: input.implementation?.reportIssue ?? false,
    completeWithoutStart: input.implementation?.completeWithoutStart ?? false
  };
  const capabilities = emptyCapabilities();
  const reasons = defaultReasons();
  const terminal = isTerminal(input.status);
  const preStart = isPreStart(input.status);
  const vehicleBlock = vehicleEligibilityBlock(
    input.vehicleEligibility,
    input.missingVehiclePolicy
  );
  const vehicleAdvisory = input.vehicleEligibility === "missing" && input.missingVehiclePolicy === "warn" ? input.wheelchairAccess ? "wheelchair_vehicle_missing" : "missing_vehicle" : null;
  const decide = (key, allowed, reason) => {
    capabilities[key] = allowed;
    reasons[key] = reason;
  };
  if (terminal) {
    decide("canEdit", false, "terminal_reservation");
  } else if (!preStart) {
    decide("canEdit", false, "wrong_lifecycle");
  } else if (derived.timePhase === "operator_only") {
    decide("canEdit", false, "operator_resolution_required");
  } else if (!input.actor.isOwningOperator) {
    decide("canEdit", false, "not_owning_operator");
  } else if (!implementation.owningOperatorEdit) {
    decide("canEdit", false, "implementation_unavailable");
  } else {
    decide("canEdit", true, "available");
  }
  if (terminal) {
    decide("canCancel", false, "terminal_reservation");
  } else if (!preStart) {
    decide("canCancel", false, "wrong_lifecycle");
  } else if (derived.timePhase === "operator_only") {
    decide("canCancel", false, "operator_resolution_required");
  } else if (input.actor.isOwningOperator && implementation.owningOperatorCancellation || input.actor.isRider && (input.status !== RESERVATION_STATUS.ASSIGNED || implementation.assignedRiderCancellation)) {
    decide("canCancel", true, "available");
  } else if (input.actor.isOwningOperator || input.actor.isRider) {
    decide("canCancel", false, "implementation_unavailable");
  } else {
    decide("canCancel", false, "not_rider_or_owning_operator");
  }
  if (terminal) {
    decide("canAcknowledge", false, "terminal_reservation");
  } else if (input.status !== RESERVATION_STATUS.ASSIGNED) {
    decide("canAcknowledge", false, "wrong_lifecycle");
  } else if (derived.timePhase === "operator_only") {
    decide("canAcknowledge", false, "operator_resolution_required");
  } else if (!input.actor.isAssignedDriver) {
    decide("canAcknowledge", false, "not_assigned_driver");
  } else if (!input.hasAssignedDriver) {
    decide("canAcknowledge", false, "missing_assigned_driver");
  } else if (input.driverAcceptedAt) {
    decide("canAcknowledge", false, "already_acknowledged");
  } else if (vehicleBlock) {
    decide("canAcknowledge", false, vehicleBlock);
  } else if (invalidTimePhase(derived.timePhase)) {
    decide("canAcknowledge", false, "invalid_schedule");
  } else {
    decide("canAcknowledge", true, "available");
  }
  if (terminal) {
    decide("canConfirmForDriver", false, "terminal_reservation");
  } else if (input.status !== RESERVATION_STATUS.ASSIGNED) {
    decide("canConfirmForDriver", false, "wrong_lifecycle");
  } else if (!input.actor.isOwningOperator) {
    decide("canConfirmForDriver", false, "not_owning_operator");
  } else if (derived.timePhase === "operator_only") {
    decide("canConfirmForDriver", false, "operator_resolution_required");
  } else if (!input.hasAssignedDriver) {
    decide("canConfirmForDriver", false, "missing_assigned_driver");
  } else if (input.driverAcceptedAt) {
    decide("canConfirmForDriver", false, "already_acknowledged");
  } else if (vehicleBlock) {
    decide("canConfirmForDriver", false, vehicleBlock);
  } else if (invalidTimePhase(derived.timePhase)) {
    decide("canConfirmForDriver", false, "invalid_schedule");
  } else if (!implementation.operatorConfirmForDriver) {
    decide("canConfirmForDriver", false, "implementation_unavailable");
  } else {
    decide("canConfirmForDriver", true, "available");
  }
  const placeholderOperatorStartAuthority = input.assignedDriverKind === "placeholder" && input.actor.isOwningOperator;
  const driverStartBlock = assignmentActionBlock(
    input,
    derived.timePhase,
    placeholderOperatorStartAuthority,
    implementation.owningOperatorStart
  );
  if (driverStartBlock) {
    decide("canStart", false, driverStartBlock);
    decide("canStartEarly", false, driverStartBlock);
  } else if ((derived.timePhase === "early" || derived.timePhase === "ready") && derived.startEligibility && !derived.startEligibility.canStart && derived.startEligibility.reason === "too_early") {
    if (placeholderOperatorStartAuthority) {
      decide("canStart", false, "before_pickup");
      decide("canStartEarly", false, "not_assigned_driver");
    } else {
      decide("canStart", false, "early_start_confirmation_required");
      decide("canStartEarly", true, "early_start_confirmation_required");
    }
  } else if (derived.timePhase === "ready" && derived.startEligibility?.canStart === true) {
    decide("canStart", true, "available");
    decide("canStartEarly", false, "early_confirmation_not_required");
  } else if (derived.timePhase === "late_start") {
    decide("canStart", false, "pickup_already_passed");
    decide("canStartEarly", false, "pickup_already_passed");
  } else if (derived.timePhase === "retrospective") {
    decide("canStart", false, "after_expected_end");
    decide("canStartEarly", false, "after_expected_end");
  } else {
    decide("canStart", false, "wrong_lifecycle");
    decide("canStartEarly", false, "wrong_lifecycle");
  }
  const lateStartBlock = assignmentActionBlock(
    input,
    derived.timePhase,
    true,
    implementation.owningOperatorStart
  );
  if (lateStartBlock) {
    decide("canStartLate", false, lateStartBlock);
  } else if (derived.timePhase === "late_start") {
    decide("canStartLate", true, "late_start_available");
  } else if (derived.timePhase === "early" || derived.timePhase === "ready") {
    decide("canStartLate", false, "before_pickup");
  } else {
    decide("canStartLate", false, "after_expected_end");
  }
  if (terminal) {
    decide("canComplete", false, "terminal_reservation");
  } else if (input.status !== RESERVATION_STATUS.IN_PROGRESS) {
    decide("canComplete", false, "wrong_lifecycle");
  } else if (input.actor.isAssignedDriver) {
    decide("canComplete", true, "available");
  } else if (input.actor.isOwningOperator) {
    decide(
      "canComplete",
      implementation.owningOperatorComplete,
      implementation.owningOperatorComplete ? "operator_recovery_available" : "implementation_unavailable"
    );
  } else {
    decide("canComplete", false, "not_assigned_driver");
  }
  if (terminal) {
    decide("canCompleteWithoutStart", false, "terminal_reservation");
  } else if (input.status !== RESERVATION_STATUS.ASSIGNED) {
    decide("canCompleteWithoutStart", false, "wrong_lifecycle");
  } else if (!implementation.completeWithoutStart) {
    decide("canCompleteWithoutStart", false, "implementation_unavailable");
  } else if (!input.hasAssignedDriver) {
    decide("canCompleteWithoutStart", false, "missing_assigned_driver");
  } else if (invalidTimePhase(derived.timePhase)) {
    decide("canCompleteWithoutStart", false, "invalid_schedule");
  } else if (derived.timePhase === "early" || derived.timePhase === "ready") {
    decide("canCompleteWithoutStart", false, "before_pickup");
  } else if (input.actor.isOwningOperator) {
    decide(
      "canCompleteWithoutStart",
      true,
      derived.timePhase === "operator_only" ? "operator_resolution_available" : "operator_recovery_available"
    );
  } else if (!input.actor.isAssignedDriver) {
    decide("canCompleteWithoutStart", false, "not_assigned_driver");
  } else if (derived.timePhase === "operator_only") {
    decide("canCompleteWithoutStart", false, "operator_resolution_required");
  } else if (!input.driverAcceptedAt) {
    decide("canCompleteWithoutStart", false, "awaiting_driver_acknowledgement");
  } else if (vehicleBlock) {
    decide("canCompleteWithoutStart", false, vehicleBlock);
  } else {
    decide("canCompleteWithoutStart", true, "retrospective_completion_available");
  }
  if (terminal) {
    decide("canReportIssue", false, "terminal_reservation");
  } else if (!implementation.reportIssue) {
    decide("canReportIssue", false, "implementation_unavailable");
  } else if (!input.actor.isAssignedDriver) {
    decide("canReportIssue", false, "not_assigned_driver");
  } else if (input.status !== RESERVATION_STATUS.ASSIGNED && input.status !== RESERVATION_STATUS.IN_PROGRESS) {
    decide("canReportIssue", false, "wrong_lifecycle");
  } else if (!input.hasAssignedDriver) {
    decide("canReportIssue", false, "missing_assigned_driver");
  } else if (input.status === RESERVATION_STATUS.ASSIGNED && derived.timePhase === "operator_only") {
    decide("canReportIssue", false, "operator_resolution_required");
  } else if (input.status === RESERVATION_STATUS.ASSIGNED && !input.driverAcceptedAt) {
    decide("canReportIssue", false, "awaiting_driver_acknowledgement");
  } else {
    decide("canReportIssue", true, "available");
  }
  return {
    lifecycle,
    acknowledgement,
    issueState,
    timePhase: derived.timePhase,
    timing: derived.timing,
    capabilities,
    reasons,
    vehicleAdvisory,
    requiresOperatorResolution: preStart && derived.timePhase === "operator_only"
  };
}

// src/utils/reservationIssuePolicy.ts
function isAtOrAfterPickup(timePhase) {
  return timePhase === "late_start" || timePhase === "retrospective" || timePhase === "operator_only";
}
function deriveReservationIssueSeverity(category, context) {
  if (category === RESERVATION_ISSUE_CATEGORY.INCIDENT) {
    return RESERVATION_ISSUE_SEVERITY.URGENT;
  }
  if (category === RESERVATION_ISSUE_CATEGORY.CANNOT_DRIVE || category === RESERVATION_ISSUE_CATEGORY.NO_SHOW) {
    return RESERVATION_ISSUE_SEVERITY.HIGH;
  }
  const afterPickupOrInProgress = context.status === RESERVATION_STATUS.IN_PROGRESS || isAtOrAfterPickup(context.timePhase);
  return afterPickupOrInProgress ? RESERVATION_ISSUE_SEVERITY.HIGH : RESERVATION_ISSUE_SEVERITY.NORMAL;
}
function evaluateReservationIssueCategoryEligibility(category, context) {
  if (category === RESERVATION_ISSUE_CATEGORY.CANNOT_DRIVE) {
    const preStartAssigned = context.status === RESERVATION_STATUS.ASSIGNED && (context.timePhase === "early" || context.timePhase === "ready");
    return preStartAssigned ? { allowed: true } : { allowed: false, reason: "cannot_drive_requires_pre_start_assigned" };
  }
  if (category === RESERVATION_ISSUE_CATEGORY.NO_SHOW) {
    if (context.status === RESERVATION_STATUS.IN_PROGRESS) {
      return { allowed: false, reason: "no_show_never_in_progress" };
    }
    const unstartedAtOrAfterPickup = context.status === RESERVATION_STATUS.ASSIGNED && (context.timePhase === "late_start" || context.timePhase === "retrospective");
    return unstartedAtOrAfterPickup ? { allowed: true } : { allowed: false, reason: "no_show_requires_at_or_after_pickup" };
  }
  return { allowed: true };
}
function deriveReservationIssueState(statuses) {
  if (statuses.some((status) => status === RESERVATION_ISSUE_STATUS.OPEN)) return "open";
  if (statuses.some((status) => status === RESERVATION_ISSUE_STATUS.ACKNOWLEDGED)) {
    return "acknowledged";
  }
  if (statuses.some((status) => status === RESERVATION_ISSUE_STATUS.RESOLVED)) return "resolved";
  return "none";
}

// src/utils/reservationActionPresentation.ts
var COMPLETE_WITHOUT_START_WARNING = "This ride was never started in RideReserv. Completing it will record a retrospective completion, notify the rider, and may collect the saved payment.";
var RESERVATION_ISSUE_CATEGORY_LABELS = {
  cannot_drive: "I cannot drive this ride",
  no_show: "Passenger no-show",
  incident: "Safety or incident",
  other: "Other"
};
var RESERVATION_ISSUE_SEVERITY_LABELS = {
  urgent: "Urgent",
  high: "High",
  normal: "Normal"
};
var RESERVATION_ISSUE_STATUS_LABELS = {
  open: "Open",
  acknowledged: "Acknowledged",
  resolved: "Resolved"
};
var RESERVATION_ISSUE_ATTENTION_BADGE_CAP = 99;
function reservationVehicleAdvisoryPresentation(advisory) {
  switch (advisory) {
    case "missing_vehicle":
      return {
        title: "No vehicle attached",
        message: "This ride can still be confirmed and started, but a vehicle must be coordinated before pickup.",
        priority: "warning"
      };
    case "wheelchair_vehicle_missing":
      return {
        title: "Wheelchair vehicle not confirmed",
        message: "Wheelchair access was requested, but no vehicle is attached. Confirm an accessible vehicle before pickup. This ride can still proceed.",
        priority: "urgent"
      };
  }
}
function formatIssueAttentionBadge(count) {
  if (!Number.isFinite(count) || count <= 0) return null;
  const whole = Math.floor(count);
  return whole > RESERVATION_ISSUE_ATTENTION_BADGE_CAP ? `${String(RESERVATION_ISSUE_ATTENTION_BADGE_CAP)}+` : String(whole);
}
var OPERATOR_RESOLUTION_REASONS = /* @__PURE__ */ new Set([
  "operator_resolution_available",
  "operator_recovery_available"
]);
function isTerminalLifecycle(evaluation) {
  return evaluation.lifecycle === "completed" || evaluation.lifecycle === "cancelled";
}
function operatorResolution(evaluation, key) {
  return OPERATOR_RESOLUTION_REASONS.has(evaluation.reasons[key]);
}
function descriptor(evaluation, kind, key, emphasis, requiresConfirmation) {
  return {
    kind,
    emphasis,
    requiresConfirmation,
    operatorResolution: operatorResolution(evaluation, key)
  };
}
function pickPrimary(evaluation, viewer) {
  const caps = evaluation.capabilities;
  if (viewer === "rider") {
    return caps.canCancel ? descriptor(evaluation, "cancel", "canCancel", "primary", true) : null;
  }
  if (evaluation.lifecycle === "in_progress") {
    return caps.canComplete ? descriptor(evaluation, "complete", "canComplete", "primary", false) : null;
  }
  if (evaluation.acknowledgement === "awaiting") {
    if (viewer === "operator") {
      return caps.canConfirmForDriver ? descriptor(evaluation, "confirmForDriver", "canConfirmForDriver", "primary", false) : null;
    }
    return caps.canAcknowledge ? descriptor(evaluation, "acknowledge", "canAcknowledge", "primary", false) : null;
  }
  switch (evaluation.timePhase) {
    case "ready":
      if (caps.canStart) return descriptor(evaluation, "start", "canStart", "primary", false);
      if (caps.canStartEarly)
        return descriptor(evaluation, "startEarly", "canStartEarly", "primary", true);
      return null;
    case "early":
      return null;
    case "late_start":
      if (caps.canStartLate)
        return descriptor(evaluation, "startLate", "canStartLate", "primary", false);
      return null;
    case "retrospective":
      if (caps.canCompleteWithoutStart)
        return descriptor(evaluation, "completeWithoutStart", "canCompleteWithoutStart", "primary", true);
      return null;
    case "operator_only":
      if (caps.canCompleteWithoutStart)
        return descriptor(evaluation, "completeWithoutStart", "canCompleteWithoutStart", "primary", true);
      if (caps.canComplete)
        return descriptor(evaluation, "complete", "canComplete", "primary", true);
      return null;
    default:
      return null;
  }
}
function deriveReservationActionPresentation(evaluation, context) {
  const { viewer } = context;
  const caps = evaluation.capabilities;
  const empty = (readOnly2, readOnlyReason) => ({
    primary: null,
    secondary: [],
    more: [],
    actions: [],
    readOnly: readOnly2,
    readOnlyReason,
    changeVehicle: false
  });
  if (isTerminalLifecycle(evaluation)) {
    return empty(true, "terminal");
  }
  const primary = pickPrimary(evaluation, viewer);
  const secondary = [];
  const more = [];
  const primaryKind = primary?.kind ?? null;
  const has = (kind) => primaryKind === kind || secondary.some((d) => d.kind === kind) || more.some((d) => d.kind === kind);
  if (viewer !== "rider" && caps.canReportIssue && viewer !== "operator") {
    secondary.push(descriptor(evaluation, "reportIssue", "canReportIssue", "secondary", false));
  }
  if (caps.canCompleteWithoutStart && primaryKind !== "completeWithoutStart") {
    secondary.push(
      descriptor(evaluation, "completeWithoutStart", "canCompleteWithoutStart", "secondary", true)
    );
  }
  if (caps.canStartEarly && primaryKind !== "startEarly") {
    more.push(descriptor(evaluation, "startEarly", "canStartEarly", "more", true));
  }
  if (caps.canEdit && !has("edit")) {
    more.push(descriptor(evaluation, "edit", "canEdit", "more", false));
  }
  if (caps.canCancel && viewer !== "rider" && !has("cancel")) {
    more.push(descriptor(evaluation, "cancel", "canCancel", "more", true));
  }
  const readOnly = primary === null && secondary.length === 0 && more.length === 0;
  if (readOnly && evaluation.timePhase === "operator_only" && evaluation.requiresOperatorResolution) {
    return empty(true, "operator_resolution_required");
  }
  const changeVehicle = viewer === "driver" && context.selfCompany === true && evaluation.reasons.canAcknowledge === "ineligible_vehicle";
  const actions = [...primary ? [primary] : [], ...secondary, ...more];
  return {
    primary,
    secondary,
    more,
    actions,
    readOnly: readOnly && actions.length === 0 && !changeVehicle,
    readOnlyReason: readOnly && actions.length === 0 && !changeVehicle ? "unavailable" : null,
    changeVehicle
  };
}
function reportableIssueCategories(evaluation) {
  if (!evaluation.capabilities.canReportIssue) return [];
  const status = lifecycleToStatus(evaluation);
  if (status === null) return [];
  return RESERVATION_ISSUE_CATEGORIES.filter(
    (category) => evaluateReservationIssueCategoryEligibility(category, {
      status,
      timePhase: evaluation.timePhase
    }).allowed
  );
}
function lifecycleToStatus(evaluation) {
  switch (evaluation.lifecycle) {
    case "in_progress":
      return RESERVATION_STATUS.IN_PROGRESS;
    case "awaiting_driver":
    case "driver_confirmed":
      return RESERVATION_STATUS.ASSIGNED;
    default:
      return null;
  }
}

// src/utils/reservationEditMateriality.ts
var RESERVATION_EDIT_FIELD_MATERIALITY = {
  expectedUpdatedAt: "non_material",
  serviceLevelId: "material",
  pickupDatetime: "material",
  pickupTimezone: "material",
  flightAdjustedForArrival: "material",
  dropoffDatetime: "material",
  dropoffTimezone: "material",
  pickupLocation: "material",
  dropoffLocation: "material",
  passengerCount: "material",
  luggageCount: "material",
  oversizedLuggage: "material",
  carSeatsNeeded: "material",
  carSeatNotes: "material",
  wheelchairAccess: "material",
  specialNotes: "non_material",
  luggageDetails: "material",
  totalCost: "non_material",
  quoteExpiresAt: "non_material",
  flightNumber: "material",
  airlineCode: "material",
  terminal: "non_material",
  noFlightFlag: "material",
  guestPhone: "non_material",
  stops: "material",
  driverUserId: "material",
  vehicleId: "material",
  // Attaching/detaching flight tracking (derive-pickup-from-flight, #2435)
  // changes which time the trip actually runs on (derived gate arrival vs a
  // pinned human time), so an assigned driver must re-acknowledge — the same
  // treatment any pickup-time change gets.
  pickupMode: "material"
};
function fieldsWithMateriality(materiality) {
  return Object.entries(RESERVATION_EDIT_FIELD_MATERIALITY).filter(([, value]) => value === materiality).map(([field]) => field).sort();
}
var MATERIAL_RESERVATION_EDIT_FIELDS = Object.freeze(
  fieldsWithMateriality("material")
);
var NON_MATERIAL_RESERVATION_EDIT_FIELDS = Object.freeze(
  fieldsWithMateriality("non_material")
);
function classifyReservationEditMateriality(changedFields) {
  const uniqueFields = [...new Set(changedFields)].sort();
  const materialChangedFields = uniqueFields.filter(
    (field) => RESERVATION_EDIT_FIELD_MATERIALITY[field] === "material"
  );
  const nonMaterialChangedFields = uniqueFields.filter(
    (field) => RESERVATION_EDIT_FIELD_MATERIALITY[field] === "non_material"
  );
  return {
    materialChangedFields,
    nonMaterialChangedFields,
    requiresDriverReconfirmation: materialChangedFields.length > 0
  };
}

// src/utils/startTiming.ts
var LATE_START_NOTATION_THRESHOLD_MINUTES = 15;
var LATE_START_NOTATION_THRESHOLD_MS = LATE_START_NOTATION_THRESHOLD_MINUTES * 60 * 1e3;
function finiteEpoch3(value) {
  const epoch = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(epoch) ? epoch : null;
}
function isStartTimingBasis(value) {
  return value === "scheduled" || value === "flight_estimated" || value === "flight_actual" || value === "unresolved";
}
function getLateStartContext(params) {
  if (params.startedAt === null || params.snapshot === null) return null;
  if (!isStartTimingBasis(params.snapshot.basis)) return null;
  const actualEpoch = finiteEpoch3(params.startedAt);
  const scheduledEpoch = finiteEpoch3(params.snapshot.scheduledPickupAt);
  if (actualEpoch === null || scheduledEpoch === null) return null;
  const basis = params.snapshot.basis;
  const expectedEpoch = basis === "unresolved" ? scheduledEpoch : params.snapshot.expectedPickupAt === null ? null : finiteEpoch3(params.snapshot.expectedPickupAt);
  if (expectedEpoch === null) return null;
  const lateByMs = actualEpoch - expectedEpoch;
  if (!Number.isFinite(lateByMs) || lateByMs <= LATE_START_NOTATION_THRESHOLD_MS) {
    return null;
  }
  return {
    basis,
    scheduledPickupAt: new Date(scheduledEpoch).toISOString(),
    expectedPickupAt: basis === "unresolved" ? null : new Date(expectedEpoch).toISOString(),
    actualStartAt: new Date(actualEpoch).toISOString(),
    lateByMinutes: Math.ceil(lateByMs / 6e4)
  };
}
function formatLateStartContext(params) {
  const context = getLateStartContext(params);
  if (!context) return null;
  const scheduled = formatPickupLocalInstant({
    instant: context.scheduledPickupAt,
    pickupTimezone: params.pickupTimezone
  });
  const actual = formatPickupLocalInstant({
    instant: context.actualStartAt,
    pickupTimezone: params.pickupTimezone
  });
  if (!scheduled || !actual) return null;
  const hasFlightExpectation = context.basis === "flight_estimated" || context.basis === "flight_actual";
  const expected = hasFlightExpectation ? formatPickupLocalInstant({
    instant: context.expectedPickupAt ?? "",
    pickupTimezone: params.pickupTimezone
  }) : null;
  if (hasFlightExpectation && !expected) return null;
  const unresolved = context.basis === "unresolved";
  return {
    summary: unresolved ? `Started ${String(context.lateByMinutes)} min after booked pickup` : `Started ${String(context.lateByMinutes)} min late`,
    scheduled: `Booked ${scheduled}`,
    expected: expected ? `Expected ${expected}` : null,
    actual: `Actual ${actual}`,
    detail: unresolved ? "Flight timing unavailable at start" : null
  };
}

// src/utils/lateCancellationPolicy.ts
function lateCancelBasisPickupAt(params) {
  const { pickupMode, pickupDatetime, derivedPickupAt } = params;
  if (pickupMode !== PICKUP_MODE.FLIGHT_TRACKED) return pickupDatetime;
  const effective = derivedPickupAt ?? pickupDatetime;
  if (pickupDatetime == null) return effective;
  if (effective == null) return pickupDatetime;
  const bookedMs = new Date(pickupDatetime).getTime();
  const effectiveMs = new Date(effective).getTime();
  if (Number.isNaN(bookedMs)) return effective;
  if (Number.isNaN(effectiveMs)) return pickupDatetime;
  return effectiveMs >= bookedMs ? effective : pickupDatetime;
}
function lateCancelFeePolicyAppliesToMode(pickupMode, flightTrackedLateCancelEnabled) {
  if (pickupMode === PICKUP_MODE.FLIGHT_TRACKED) {
    return flightTrackedLateCancelEnabled === true;
  }
  return true;
}
function isLateCancellationPolicyAlwaysFree(config) {
  return (config.lateCancelFeePercent ?? 0) === 0 || (config.lateCancelWindowHours ?? 0) === 0;
}
function isWithinLateCancelWindow(params) {
  const { pickupDatetime, lateCancelWindowHours, now = /* @__PURE__ */ new Date() } = params;
  if (pickupDatetime == null || lateCancelWindowHours == null || lateCancelWindowHours <= 0) {
    return false;
  }
  const pickup = typeof pickupDatetime === "string" ? new Date(pickupDatetime) : pickupDatetime;
  const hoursUntilPickup = (pickup.getTime() - now.getTime()) / (60 * 60 * 1e3);
  return hoursUntilPickup <= lateCancelWindowHours;
}
function formatLateCancellationPolicyLine(config, ride) {
  if (!config.lateCancellationPolicyEnabled) return null;
  const { lateCancelWindowHours: windowHours, lateCancelFeePercent: feePercent } = config;
  if (windowHours == null || feePercent == null) return null;
  if (ride !== void 0 && !lateCancelFeePolicyAppliesToMode(ride.pickupMode, ride.flightTrackedLateCancelEnabled)) {
    return "Free cancellation at any time before pickup.";
  }
  if (isLateCancellationPolicyAlwaysFree({ lateCancelWindowHours: windowHours, lateCancelFeePercent: feePercent })) {
    return "Free cancellation at any time before pickup.";
  }
  const windowLabel = String(windowHours);
  const feeLabel = String(feePercent);
  return `Free cancellation until ${windowLabel}h before pickup. Cancelling within ${windowLabel}h of pickup may incur a fee of up to ${feeLabel}%.`;
}
function requiresLateCancelAcknowledgement(params) {
  const {
    lateCancellationPolicyEnabled,
    lateCancelWindowHours,
    lateCancelFeePercent,
    pickupDatetime,
    isPaid,
    now,
    pickupMode,
    derivedPickupAt,
    flightTrackedLateCancelEnabled
  } = params;
  if (!lateCancellationPolicyEnabled || !isPaid) return false;
  if (lateCancelFeePercent == null || lateCancelFeePercent === 0) return false;
  if (!lateCancelFeePolicyAppliesToMode(pickupMode, flightTrackedLateCancelEnabled)) {
    return false;
  }
  const basisPickup = lateCancelBasisPickupAt({ pickupMode, pickupDatetime, derivedPickupAt });
  return isWithinLateCancelWindow({ pickupDatetime: basisPickup, lateCancelWindowHours, now });
}

// src/utils/reservationCancellationOutcome.ts
function deriveReservationCancellationOutcome(input) {
  if (input.status !== RESERVATION_STATUS.CANCELLED) return null;
  return input.cancellationReason === COMPANY_DECLINED_CANCELLATION_REASON ? RESERVATION_CANCELLATION_OUTCOME.DECLINED : RESERVATION_CANCELLATION_OUTCOME.CANCELLED;
}

// src/utils/reservationLocation.ts
var EMPTY_RESERVATION_LOCATION_LABEL = "Not yet specified";
var DEFAULT_ROUTE_SEPARATOR = " \u2192 ";
function displayReservationLocation(location) {
  return location?.trim() ? location : EMPTY_RESERVATION_LOCATION_LABEL;
}
function hasReservationLocation(location) {
  return !!location?.trim();
}
function formatReservationRoute(pickup, dropoff, options = {}) {
  const separator = options.separator ?? DEFAULT_ROUTE_SEPARATOR;
  const start = displayReservationLocation(pickup);
  if (!dropoff?.trim()) return start;
  return `${start}${separator}${dropoff}`;
}

// src/utils/specialNotes.ts
function visibleSpecialNotes(notes) {
  const trimmed = notes?.trim();
  if (!trimmed) return null;
  return trimmed;
}
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/utils/geoMath.ts
var EARTH_RADIUS_MILES = 3958.8;
function haversineDistanceMiles(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(a));
}
function estimateDriveTimeMinutes(distanceMiles, avgSpeedMph = 25) {
  if (distanceMiles <= 0 || avgSpeedMph <= 0) return 0;
  return Math.round(distanceMiles / avgSpeedMph * 60);
}

// src/utils/geocodingSession.ts
function generateGeocodingSessionToken() {
  const random = () => Math.random().toString(36).slice(2);
  return `rr-${random()}${random()}`;
}

// src/utils/airports.ts
var AIRPORT_RADIUS_MILES = 3 / 1.60934;
var LAT_PAD = 0.03;
var LNG_PAD_BASE = 0.04;
var MILES_PER_LAT_DEGREE = 69;
var MIN_COS_LAT = 0.15;
function cosLatForPadding(lat) {
  return Math.max(Math.abs(Math.cos(lat * Math.PI / 180)), MIN_COS_LAT);
}
function lngPadForLat(lat, radiusMiles = AIRPORT_RADIUS_MILES) {
  const cosLat = cosLatForPadding(lat);
  const radiusPad = radiusMiles / (MILES_PER_LAT_DEGREE * cosLat);
  return Math.max(LNG_PAD_BASE / cosLat, radiusPad);
}
function latPadForRadius(radiusMiles) {
  return Math.max(LAT_PAD, radiusMiles / MILES_PER_LAT_DEGREE);
}
function normalizeIataCodeSet(codes) {
  if (!codes || codes.length === 0) return null;
  const normalized = codes.map((code) => code.trim().toUpperCase()).filter(Boolean);
  return normalized.length > 0 ? new Set(normalized) : null;
}
function resolveSearchRadius(options) {
  if (options?.radiusMiles === void 0) return AIRPORT_RADIUS_MILES;
  if (!Number.isFinite(options.radiusMiles) || options.radiusMiles <= 0) return null;
  return options.radiusMiles;
}
function airportCodeAllowed(airport, allowedCodes) {
  return allowedCodes === null || allowedCodes.has(airport.iata.toUpperCase());
}
function pointInBbox(lat, lng, bbox) {
  if (!bbox) return false;
  const [west, south, east, north] = bbox;
  if (lat < south || lat > north) return false;
  if (west > east) return lng >= west || lng <= east;
  return lng >= west && lng <= east;
}
function isNearAirport(lat, lng, options) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const radiusMiles = resolveSearchRadius(options);
  if (radiusMiles === null) return null;
  const allowedCodes = normalizeIataCodeSet(options?.airportIataCodes);
  let best = null;
  const useDefaultBoundary = options?.radiusMiles === void 0;
  const latPad = latPadForRadius(radiusMiles);
  const lngPad = lngPadForLat(lat, radiusMiles);
  for (const airport of AIRPORTS) {
    if (!airportCodeAllowed(airport, allowedCodes)) continue;
    const usesDefaultBbox = useDefaultBoundary && airport.bbox !== void 0;
    if (usesDefaultBbox) {
      if (!pointInBbox(lat, lng, airport.bbox)) continue;
    } else {
      if (Math.abs(airport.lat - lat) > latPad || Math.abs(airport.lng - lng) > lngPad) {
        continue;
      }
    }
    const distanceMiles = haversineDistanceMiles(lat, lng, airport.lat, airport.lng);
    const qualifies = usesDefaultBbox || distanceMiles <= radiusMiles;
    if (qualifies && (best === null || distanceMiles < best.distanceMiles)) {
      best = { airport, distanceMiles };
    }
  }
  return best;
}

// src/utils/stops.ts
function hasNoFlightWarning(reservation) {
  if (!reservation.noFlightFlag) return false;
  const isAirportRide = reservation.hasAirportEndpoint === true || reservation.subType === "arrival" || reservation.subType === "departure";
  return isAirportRide;
}
function computeHasAirportEndpoint(input) {
  if (input.stops.length === 0) {
    return input.subType === "arrival" || input.subType === "departure";
  }
  const pickups = input.stops.filter((s) => s.role === "pickup").sort((a, b) => a.position - b.position);
  const dropoffs = input.stops.filter((s) => s.role === "dropoff").sort((a, b) => a.position - b.position);
  const firstPickup = pickups[0];
  const lastDropoff = dropoffs[dropoffs.length - 1];
  return !!firstPickup?.isAirport || !!lastDropoff?.isAirport;
}
function deriveLocationsFromStops(stops) {
  const pickups = stops.filter((s) => s.role === "pickup").sort((a, b) => a.position - b.position);
  const dropoffs = stops.filter((s) => s.role === "dropoff").sort((a, b) => a.position - b.position);
  return {
    pickupLocation: pickups[0]?.address ?? null,
    dropoffLocation: dropoffs[dropoffs.length - 1]?.address ?? null
  };
}
function deriveOperationalEditPickupAnchor(stops) {
  const pickups = stops.filter((stop) => stop.role === "pickup").sort((left, right) => left.position - right.position);
  const finalPickup = pickups.at(-1);
  const tracksEditedPickup = pickups.length <= 1;
  if (typeof finalPickup?.lat !== "number" || !Number.isFinite(finalPickup.lat) || typeof finalPickup.lng !== "number" || !Number.isFinite(finalPickup.lng)) {
    return { coords: void 0, tracksEditedPickup };
  }
  return {
    coords: { lat: finalPickup.lat, lng: finalPickup.lng },
    tracksEditedPickup
  };
}
function associatedPickupCoords(stops, dropoffIndex) {
  if (stops[dropoffIndex]?.role !== "dropoff") return void 0;
  const pickup = stops.filter((stop) => stop.role === "pickup").at(-1);
  if (typeof pickup?.lat !== "number" || !Number.isFinite(pickup.lat) || typeof pickup.lng !== "number" || !Number.isFinite(pickup.lng)) {
    return void 0;
  }
  return { lat: pickup.lat, lng: pickup.lng };
}
function coordWaypointsForRole(stops, role) {
  return stops.filter((s) => s.role === role).sort((a, b) => a.position - b.position).flatMap(
    (s) => s.lat !== null && s.lng !== null ? [{ lat: s.lat, lng: s.lng }] : []
  );
}
function orderWaypointsForPricing(stops) {
  return [
    ...coordWaypointsForRole(stops, "pickup"),
    ...coordWaypointsForRole(stops, "dropoff")
  ];
}

// src/utils/csv.ts
var FORMULA_TRIGGER_CHARS = /^[=+\-@\t\r]/;
var NUMERIC_CELL_TEXT = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;
function isTrustedNumericCsvValue(value) {
  if (value === null || value === void 0 || value === "") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "bigint") return true;
  if (typeof value === "string") return NUMERIC_CELL_TEXT.test(value);
  return false;
}
function trustedNumericColumnIndexes(headers, options) {
  const indexes = new Set(options.trustedNumericColumnIndexes ?? []);
  const names = new Set(options.trustedNumericColumnNames ?? []);
  if (names.size > 0) {
    headers.forEach((header, index) => {
      if (names.has(header)) indexes.add(index);
    });
  }
  return indexes;
}
function escapeCsvCell(value, options = {}) {
  if (value === null || value === void 0) return "";
  let s = typeof value === "string" ? value : `${value}`;
  const { formulaGuard = true } = options;
  if (formulaGuard && FORMULA_TRIGGER_CHARS.test(s)) {
    s = `'${s}`;
  }
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    s = `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function rowsToCsv(headers, rows, options = {}) {
  const trustedNumericColumns = trustedNumericColumnIndexes(headers, options);
  const lines = [
    headers.map((header) => escapeCsvCell(header)).join(","),
    ...rows.map(
      (row) => row.map(
        (cell, index) => escapeCsvCell(cell, {
          formulaGuard: !trustedNumericColumns.has(index) || !isTrustedNumericCsvValue(cell)
        })
      ).join(",")
    )
  ];
  return lines.join("\r\n");
}
function csvFilename(entity, date = /* @__PURE__ */ new Date()) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${entity}-${String(yyyy)}-${mm}-${dd}.csv`;
}

// src/utils/tierFeatureGroups.ts
var FLAG_DEFAULTS = {
  allowCustomBranding: false,
  allowDriverAssignment: false,
  allowAnalytics: false,
  allowSmsNotifications: false,
  allowEmbeddableWidget: false,
  allowDriverAnalytics: false,
  allowBrandedReceipts: false,
  allowAutoDispatch: false,
  allowOnlinePayments: false,
  allowDistancePricing: false,
  allowZonePricing: false,
  allowSurchargePolicies: false,
  allowFleetMap: false,
  allowPercentSurcharges: false,
  hidePlatformBranding: false
};
var CAP_DEFAULTS = {
  maxDrivers: 0,
  maxReservationsMonth: 0,
  maxServiceLevels: 0,
  csvExportRowLimit: 0,
  smsPerMonthIncluded: 0,
  aiDispatchPerDay: 0,
  // Default null (unlimited-shaped), NOT 0 — this matches the Postgres
  // ADD COLUMN default for these nullable columns (no explicit DB default),
  // so a tier this repo's fixture doesn't override agrees with what a fresh
  // `db push` (no seed) produces. The gating flag (allowSurchargePolicies /
  // allowZonePricing) is what actually denies access on tiers without the
  // feature — an unused "unlimited" cap on a flag-disabled tier is inert.
  maxPricingZones: null,
  maxSurchargePolicies: null,
  maxSurchargeRulesPerPolicy: null,
  // #1108: 0, not null — 0 means "no monitored-airport entitlement" (the
  // baseline for every tier below business_plus), matching the Postgres
  // ADD COLUMN default this repo's deploy-path SQL migration sets explicitly
  // for every tier (never relies on the nullable-column default).
  maxMonitoredAirports: 0,
  // Paid completed-leg overage is a live billing product (#1704). SMS remains
  // a hard stop and has no price knob (#2162).
  overageReservationPriceCents: 0,
  transactionFeePercent: 2.5
};
var TIER_FEATURE_GROUPS = {
  branding: { allowCustomBranding: true },
  driver_assignment: { allowDriverAssignment: true },
  analytics: { allowAnalytics: true, allowDriverAnalytics: true },
  sms: { allowSmsNotifications: true },
  widget: { allowEmbeddableWidget: true },
  branded_receipts: { allowBrandedReceipts: true },
  auto_dispatch: { allowAutoDispatch: true },
  online_payments: { allowOnlinePayments: true },
  distance_pricing: { allowDistancePricing: true },
  zone_pricing: { allowZonePricing: true },
  surcharge_policies: { allowSurchargePolicies: true },
  fleet_map: { allowFleetMap: true },
  // Percent-type surcharge rule authoring gate (#1031). Business Plus+ only.
  percent_surcharges: { allowPercentSurcharges: true },
  // Operator branding-consistency + "Powered by RideReserv" gate (#975).
  // White-label perk — single-flag group, business_ultimate only.
  white_label: { hidePlatformBranding: true }
};
function buildTier(params) {
  const flags = { ...FLAG_DEFAULTS };
  for (const groupName of params.includes ?? []) {
    Object.assign(flags, TIER_FEATURE_GROUPS[groupName]);
  }
  const caps = { ...CAP_DEFAULTS, ...params.caps };
  return {
    id: params.id,
    displayName: params.displayName,
    description: params.description,
    monthlyPrice: params.monthlyPrice,
    sortOrder: params.sortOrder,
    isActive: params.isActive ?? true,
    ...flags,
    ...caps
  };
}

// src/utils/dispatchSuggestion.ts
var WHEELCHAIR_UNRECORDED_NOTICE = "Wheelchair accessibility not recorded for this vehicle";
function vehicleFitWarnings(vehicle, req) {
  const warnings = [];
  const notices = [];
  if (!vehicle) return { warnings, notices };
  if (vehicle.capacity != null && vehicle.capacity < req.passengerCount) {
    warnings.push(
      `Seats ${String(vehicle.capacity)}, but the ride has ${String(req.passengerCount)} passenger(s)`
    );
  }
  if (vehicle.luggageCapacity != null && vehicle.luggageCapacity < req.luggageCount) {
    warnings.push(
      `Holds ${String(vehicle.luggageCapacity)} bag(s), but the ride has ${String(req.luggageCount)}`
    );
  }
  if (req.wheelchairAccess) {
    if (vehicle.wheelchairAccessible === false) {
      warnings.push("Not wheelchair accessible");
    } else if (vehicle.wheelchairAccessible == null) {
      notices.push(WHEELCHAIR_UNRECORDED_NOTICE);
    }
  }
  return { warnings, notices };
}
function isDispatchSuggestable(status, driverAcceptedAt) {
  return status === RESERVATION_STATUS.CONFIRMED || status === RESERVATION_STATUS.ASSIGNED && driverAcceptedAt == null;
}
function suggestionEtaLabel(etaToPickup) {
  if (etaToPickup == null || !Number.isFinite(etaToPickup)) return void 0;
  return `~${String(Math.max(0, Math.round(etaToPickup)))} min`;
}
function suggestionWarning(candidate) {
  if (!candidate.available) return "May have a scheduling conflict";
  if (candidate.withinDeclaredHours === false) {
    return "Outside this driver's declared hours";
  }
  if (candidate.fitWarnings && candidate.fitWarnings.length > 0) return candidate.fitWarnings[0];
  return void 0;
}

// src/utils/dispatchPlanCodes.ts
var DISPATCH_PLAN_BLOCKER_LABELS = {
  driver_authority_unavailable: {
    label: "Not an active driver",
    detail: "This person is no longer an active driver on your team, or is suspended."
  },
  driver_time_off: {
    label: "On time off",
    detail: "The driver declared personal time off across this ride's window."
  },
  missing_vehicle: {
    label: "Vehicle missing",
    detail: "The vehicle this candidate was evaluated with is no longer on record."
  },
  vehicle_misfit: {
    label: "Vehicle doesn't fit",
    detail: "The evaluated vehicle fails a requirement this ride states."
  },
  infeasible_reposition: {
    label: "Can't reach pickup",
    detail: "The driver can't reach this pickup in time after their prior ride."
  },
  wheelchair_unsupported_company: {
    label: "Wheelchair not supported",
    detail: "This ride needs wheelchair access and your company hasn't declared support."
  },
  wheelchair_vehicle_unavailable: {
    label: "No accessible vehicle",
    detail: "This ride needs wheelchair access and no confirmed accessible vehicle is available."
  }
};
var DISPATCH_PLAN_WARNING_LABELS = {
  driver_ride_conflict: {
    label: "Overlapping ride",
    detail: "This driver already holds a ride that overlaps. Assigning needs your confirmation."
  },
  outside_declared_hours: {
    label: "Outside declared hours",
    detail: "This ride falls outside the driver's declared weekly hours."
  },
  vehicle_ride_conflict: {
    label: "Vehicle double-booked",
    detail: "The evaluated vehicle is already committed to an overlapping ride."
  },
  vehicle_maintenance: {
    label: "Vehicle in maintenance",
    detail: "The evaluated vehicle has a maintenance window across this ride."
  }
};
var UNKNOWN_BLOCKER = {
  label: "Not available",
  detail: "The server blocked this driver for a reason this version can't describe yet."
};
var UNKNOWN_WARNING = {
  label: "Advisory",
  detail: "The server flagged an advisory this version can't describe yet."
};
function dispatchPlanBlockerLabel(code) {
  return DISPATCH_PLAN_BLOCKER_LABELS[code] ?? UNKNOWN_BLOCKER;
}
function dispatchPlanWarningLabel(code) {
  return DISPATCH_PLAN_WARNING_LABELS[code] ?? UNKNOWN_WARNING;
}
var STALE_DISPATCH_PREVIEW_REASON = "stale_dispatch_preview";
function isStaleDispatchPreviewFailure(details) {
  return details?.reason === STALE_DISPATCH_PREVIEW_REASON;
}
function canResubmitReviewedRow(outcome) {
  if (!outcome) return true;
  if (outcome.status === "assigned") return false;
  return !isStaleDispatchPreviewFailure(outcome.details);
}

// src/utils/vehicleCapabilityStatus.ts
var VEHICLE_CAPABILITY_STATUS_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "Unknown" }
];
var VEHICLE_CAPABILITY_UNKNOWN_HINT = "No recorded answer. Dispatch never treats an unknown as a yes, and automatic assignment skips this vehicle for rides that need it.";
function readVehicleCapabilityStatus(status, legacy) {
  if (status === "yes" || status === "no" || status === "unknown") return status;
  return legacy === true ? "yes" : "unknown";
}
function vehicleCapabilityEvidence(status, legacy) {
  const resolved = readVehicleCapabilityStatus(status, legacy);
  if (resolved === "yes") return true;
  if (resolved === "no") return false;
  return null;
}
var MARKED_CAPABILITIES = [
  { key: "wheelchair", label: "Wheelchair" },
  { key: "childSeat", label: "Child seat" }
];
function vehicleCapabilityMarks(vehicle) {
  const statusByKey = {
    wheelchair: readVehicleCapabilityStatus(
      vehicle.wheelchairAccessibleStatus,
      vehicle.wheelchairAccessible
    ),
    childSeat: readVehicleCapabilityStatus(
      vehicle.childSeatCapableStatus,
      vehicle.childSeatCapable
    )
  };
  return MARKED_CAPABILITIES.map(({ key, label }) => {
    const status = statusByKey[key];
    const statusLabel = VEHICLE_CAPABILITY_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? "";
    return { key, label, status, text: `${label}: ${statusLabel}` };
  });
}

// src/utils/soloVehicleEvidence.ts
function coversEveryLeg(vehicle, legs) {
  return legs.every(
    (leg) => vehicleFitWarnings(vehicle, leg).warnings.length === 0 && // An explicit no rules the vehicle out; a blank answer does not (it is
    // caught by the completeness pass instead).
    !(leg.carSeatsNeeded === true && vehicle.childSeatCapable === false)
  );
}
function hasCompleteEvidenceFor(vehicle, legs) {
  if (vehicle.capacity == null || vehicle.luggageCapacity == null) return false;
  if (legs.some((leg) => leg.wheelchairAccess) && vehicle.wheelchairAccessible == null) {
    return false;
  }
  if (legs.some((leg) => leg.carSeatsNeeded === true) && vehicle.childSeatCapable == null) {
    return false;
  }
  return true;
}
function deriveSoloVehicleEvidenceNudge(vehicles, requirements) {
  if (!vehicles || vehicles.length === 0) return "no_vehicle";
  const legs = Array.isArray(requirements) ? requirements : [requirements];
  const defaults = vehicles.filter((vehicle) => vehicle.isDefault);
  if (defaults.length > 1) return "default_selection_required";
  const selectionPool = defaults.length === 1 ? defaults : vehicles;
  const candidates = selectionPool.filter((vehicle) => coversEveryLeg(vehicle, legs));
  if (candidates.length === 0) return "unfit";
  if (candidates.some((vehicle) => !hasCompleteEvidenceFor(vehicle, legs))) {
    return "incomplete_evidence";
  }
  if (defaults.length === 0 && candidates.length > 1) {
    return "default_selection_required";
  }
  return null;
}
var NUDGE_MESSAGES = {
  no_vehicle: "Add your car so this ride can be assigned to you.",
  incomplete_evidence: "Finish your vehicle details (seats, luggage, and the accessibility answers) so this ride can be matched to your car.",
  unfit: "Your car's recorded details don't cover this ride \u2014 update them or add another vehicle.",
  default_selection_required: "Choose one default vehicle so rides can be matched to the right car."
};
function soloVehicleEvidenceNudgeMessage(nudge) {
  return NUDGE_MESSAGES[nudge];
}

// src/utils/reservationEditLogic.ts
var RESERVATION_UPDATE_FIELD_SCOPE = {
  expectedUpdatedAt: "concurrency",
  serviceLevelId: "operational",
  pickupDatetime: "operational",
  pickupTimezone: "operational",
  flightAdjustedForArrival: "flight",
  dropoffDatetime: "operational",
  dropoffTimezone: "operational",
  pickupLocation: "operational",
  dropoffLocation: "operational",
  passengerCount: "operational",
  luggageCount: "operational",
  oversizedLuggage: "operational",
  carSeatsNeeded: "operational",
  carSeatNotes: "operational",
  wheelchairAccess: "operational",
  specialNotes: "operational",
  luggageDetails: "operational",
  totalCost: "fare",
  quoteExpiresAt: "fare",
  flightNumber: "flight",
  airlineCode: "flight",
  terminal: "flight",
  noFlightFlag: "flight"
};
function isOperationalEditField(field) {
  return Object.prototype.hasOwnProperty.call(RESERVATION_UPDATE_FIELD_SCOPE, field) && RESERVATION_UPDATE_FIELD_SCOPE[field] === "operational";
}
var OPERATIONAL_RESERVATION_EDIT_FIELDS = Object.freeze(Object.keys(RESERVATION_EDIT_FIELD_MATERIALITY).filter(isOperationalEditField).sort());
function sanitizeOperationalEditPatch(input) {
  const source = input;
  const safe = {};
  for (const field of OPERATIONAL_RESERVATION_EDIT_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) safe[field] = source[field];
  }
  return safe;
}
function withExpectedUpdatedAt(patch, expectedUpdatedAt) {
  return { ...sanitizeOperationalEditPatch(patch), expectedUpdatedAt };
}
function capacityWarning(counts, vehicle) {
  if (!vehicle) return null;
  const passengerCapacity = vehicle.capacity ?? null;
  const luggageCapacity = vehicle.luggageCapacity ?? null;
  const exceeded = vehicleFitWarnings(
    { capacity: passengerCapacity, luggageCapacity, wheelchairAccessible: null },
    {
      passengerCount: counts.passengerCount,
      luggageCount: counts.luggageCount,
      wheelchairAccess: false
    }
  ).warnings;
  if (exceeded.length === 0) return null;
  return {
    passengerCount: counts.passengerCount,
    passengerCapacity,
    luggageCount: counts.luggageCount,
    luggageCapacity
  };
}
function parseExactNonNegativeMoney(value) {
  const trimmed = value.trim();
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!match) return null;
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(fraction)) return null;
  const cents = whole * 100 + fraction;
  if (!Number.isSafeInteger(cents) || cents < 0) return null;
  return { amount: cents / 100, cents };
}
var STALE_RESERVATION_MESSAGE = "This reservation was updated by someone else. Reload and reapply your changes.";
function conflictCandidate(error) {
  if (typeof error !== "object" || error === null) return null;
  const response = error.response;
  if (response !== void 0) {
    if (response.status !== HTTP_STATUS.CONFLICT || typeof response.data !== "object" || response.data === null) {
      return null;
    }
    const apiError = response.data.error;
    if (typeof apiError !== "object" || apiError === null) return null;
    const fields = apiError;
    const details = fields["details"];
    return {
      code: fields["code"],
      message: fields["message"],
      reason: typeof details === "object" && details !== null ? details["reason"] : void 0
    };
  }
  const direct = error;
  return {
    code: direct.code,
    message: direct.message,
    reason: typeof direct.details === "object" && direct.details !== null ? direct.details["reason"] : void 0
  };
}
function isStaleReservationConflict(error) {
  const candidate = conflictCandidate(error);
  if (candidate?.code !== ERROR_CODES.RESOURCE_ALREADY_EXISTS) return false;
  if (typeof candidate.reason === "string") {
    return candidate.reason === CONFLICT_REASONS.RESERVATION_VERSION_CONFLICT;
  }
  return candidate.message === STALE_RESERVATION_MESSAGE;
}

// src/utils/flightBoardTimezone.ts
function formatFlightBoardTime(iso, timezone) {
  const explicitTimezone = timezone?.trim();
  if (!iso || !explicitTimezone) return "\u2014";
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return "\u2014";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: explicitTimezone,
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short"
    }).format(instant);
  } catch {
    return "\u2014";
  }
}

// src/utils/airportStatusRole.ts
function airportStatusLevelRole(level) {
  switch (level) {
    case "normal":
      return "success";
    case "moderate":
      return "warning";
    case "major":
      return "live";
    case "severe":
      return "destructive";
    case "closed":
      return "ink";
    default:
      return "neutral";
  }
}
var AIRPORT_STATUS_LEVEL_LABEL = {
  normal: "Normal operations",
  moderate: "Moderate delays",
  major: "Major delays",
  severe: "Severe disruption",
  closed: "Airport closed"
};
var AIRPORT_STATUS_STALE_MS = {
  faa: 10 * 60 * 1e3,
  airlabs: 60 * 60 * 1e3
};
function isAirportStatusStale(polledAt, source, now = Date.now()) {
  const then = new Date(polledAt).getTime();
  if (Number.isNaN(then)) return false;
  const thresholdMs = AIRPORT_STATUS_STALE_MS[source];
  return now - then > thresholdMs;
}
function airportStatusUpdatedAgoLabel(polledAt, now = Date.now()) {
  const then = new Date(polledAt).getTime();
  const minutes = Number.isNaN(then) ? 0 : Math.max(0, Math.floor((now - then) / 6e4));
  return `updated ${String(minutes)}m ago`;
}

// src/utils/bookingUrl.ts
function bookingPagePath(slug) {
  return `/${slug}/book`;
}
function buildBookingUrl(slug, baseUrl = "ridereserv.com") {
  return `${baseUrl.replace(/\/+$/, "")}${bookingPagePath(slug)}`;
}

// src/utils/authErrorCopy.ts
var AUTH_TRANSPORT_ERROR_MESSAGE = "Couldn't reach the server \u2014 check your connection and try again.";
var PHONE_CODE_SENT_NOTICE = "If an account exists for that number, we've texted a 6-digit code.";
var PHONE_CODE_RESENT_NOTICE = "If an account exists for that number, we've texted a new 6-digit code.";
var PHONE_CODE_RESEND_COOLDOWN_SECONDS = 60;
var RETRY_AFTER_PATTERN = /after (\d+) second/i;
function isAuthThrottleError(error) {
  return error.status === 429 || (error.code ?? "").includes("rate_limit") || /rate limit/i.test(error.message ?? "") || RETRY_AFTER_PATTERN.test(error.message ?? "");
}
function authThrottleRetrySeconds(error) {
  const match = RETRY_AFTER_PATTERN.exec(error.message ?? "");
  if (!match?.[1]) return null;
  const seconds = Number(match[1]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}
function throttleClause(error) {
  const seconds = authThrottleRetrySeconds(error);
  if (seconds === null) return "wait a few minutes before trying again";
  return `wait ${String(seconds)} ${seconds === 1 ? "second" : "seconds"} before trying again`;
}
function isWeakPassword(error) {
  return error.code === "weak_password" || /password should be/i.test(error.message ?? "");
}
var CHOOSE_STRONGER_PASSWORD = "Please choose a stronger password \u2014 at least 8 characters.";
function authSendErrorMessage(error, options) {
  if (isAuthThrottleError(error)) {
    const what = options.channel === "email" ? "emails" : "texts";
    return `We've sent several ${what} recently. Please ${throttleClause(error)}.`;
  }
  return options.channel === "email" ? "We couldn't send that email right now. Please try again in a moment." : "We couldn't send that code right now. Check the number and try again.";
}
function authSignInErrorMessage(error) {
  if (isAuthThrottleError(error)) {
    return `Too many sign-in attempts. Please ${throttleClause(error)}.`;
  }
  if (error.code === "invalid_credentials" || /invalid login credentials|invalid email or password/i.test(error.message ?? "")) {
    return "That email and password don't match an account. Check both and try again.";
  }
  return "We couldn't sign you in. Please try again.";
}
function authReauthErrorMessage(error) {
  if (isAuthThrottleError(error)) {
    return `Too many attempts. Please ${throttleClause(error)}.`;
  }
  if (error.code === "invalid_credentials" || /invalid login credentials|invalid email or password/i.test(error.message ?? "")) {
    return "That password is incorrect. Please try again.";
  }
  return "We couldn't confirm your password. Please try again.";
}
function authSignUpErrorMessage(error) {
  if (isAuthThrottleError(error)) {
    return `We've sent several emails recently. Please ${throttleClause(error)}.`;
  }
  if (isWeakPassword(error)) return CHOOSE_STRONGER_PASSWORD;
  return "We couldn't complete your registration. Please try again.";
}
function authPasswordUpdateErrorMessage(error) {
  if (isAuthThrottleError(error)) {
    return `Too many attempts. Please ${throttleClause(error)}.`;
  }
  if (error.code === "same_password" || /should be different from the old password/i.test(error.message ?? "")) {
    return "Your new password must be different from your current one.";
  }
  if (isWeakPassword(error)) return CHOOSE_STRONGER_PASSWORD;
  return "We couldn't update your password. Please try again.";
}
function authCodeVerifyErrorMessage(error) {
  if (isAuthThrottleError(error)) {
    return `Too many attempts. Please ${throttleClause(error)}.`;
  }
  return "That code is incorrect or has expired. Request a new one.";
}

// src/utils/index.ts
function utcToLocal(utcDate, timezone) {
  const dateObj = typeof utcDate === "string" ? parseISO(utcDate) : utcDate;
  return toZonedTime(dateObj, timezone);
}
function toISOString(date) {
  return date.toISOString();
}
function plural2(n, unit) {
  return `${String(n)} ${unit}${n === 1 ? "" : "s"}`;
}
function evaluateBookingWindow(params) {
  const { now, timezone, policy } = params;
  const pickup = typeof params.pickup === "string" ? parseISO(params.pickup) : params.pickup;
  if (policy.minHoursNotice > 0) {
    const earliest = new Date(now.getTime() + policy.minHoursNotice * 60 * 60 * 1e3);
    if (pickup.getTime() < earliest.getTime()) {
      return {
        ok: false,
        violation: "too_soon",
        message: `Pickups need at least ${plural2(policy.minHoursNotice, "hour")} of notice.`
      };
    }
  }
  const todayLocal = instantToWallTime(now, timezone);
  if (!todayLocal) throw new RangeError(`Invalid time zone specified: ${timezone}`);
  const horizonMarker = /* @__PURE__ */ new Date(`${todayLocal.date}T00:00:00.000Z`);
  horizonMarker.setUTCDate(horizonMarker.getUTCDate() + policy.maxAdvanceBookingDays);
  const latestAllowed = endOfDayInZone(formatDatePart(horizonMarker), timezone);
  if (!latestAllowed) throw new RangeError(`Invalid time zone specified: ${timezone}`);
  if (pickup.getTime() > latestAllowed.getTime()) {
    return {
      ok: false,
      violation: "too_far",
      message: `Pickups can be booked at most ${plural2(
        policy.maxAdvanceBookingDays,
        "day"
      )} in advance.`
    };
  }
  return { ok: true };
}
function formatDatePart(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${String(y)}-${m}-${day}`;
}
var UPCOMING_GRACE_MS = 30 * 60 * 1e3;
var toEpochMs = (value) => (typeof value === "string" ? parseISO(value) : value).getTime();
function isActiveUpcoming(input) {
  const { status, pickupDatetime, now = /* @__PURE__ */ new Date(), graceMs = UPCOMING_GRACE_MS } = input;
  if (status === RESERVATION_STATUS.COMPLETED || status === RESERVATION_STATUS.CANCELLED) {
    return false;
  }
  if (status === RESERVATION_STATUS.IN_PROGRESS) return true;
  if (pickupDatetime === null) return false;
  return toEpochMs(pickupDatetime) >= now.getTime() - graceMs;
}
function isOverdueUnstarted(input) {
  const { status, pickupDatetime, now = /* @__PURE__ */ new Date() } = input;
  if (status === RESERVATION_STATUS.COMPLETED || status === RESERVATION_STATUS.CANCELLED || status === RESERVATION_STATUS.IN_PROGRESS) {
    return false;
  }
  if (pickupDatetime === null) return false;
  return toEpochMs(pickupDatetime) < now.getTime();
}
var QUOTE_EXPIRY_URGENT_HOURS = 24;
function getQuoteExpiryStatus(quoteExpiresAt, now = /* @__PURE__ */ new Date()) {
  const diffMs = toEpochMs(quoteExpiresAt) - now.getTime();
  const expired = diffMs <= 0;
  const diffHours = expired ? 0 : diffMs / (60 * 60 * 1e3);
  return {
    expired,
    hoursRemaining: expired ? 0 : Math.floor(diffHours),
    daysRemaining: expired ? 0 : Math.floor(diffHours / 24),
    urgent: !expired && diffHours <= QUOTE_EXPIRY_URGENT_HOURS
  };
}
function formatFixAge(capturedAtIso, now = /* @__PURE__ */ new Date()) {
  const capturedAtMs = new Date(capturedAtIso).getTime();
  if (Number.isNaN(capturedAtMs)) return null;
  const diffMs = Math.max(0, now.getTime() - capturedAtMs);
  const diffMin = Math.floor(diffMs / (60 * 1e3));
  const diffHours = Math.floor(diffMs / (60 * 60 * 1e3));
  if (diffMin < 1) return "<1m ago";
  if (diffHours < 1) return `${String(diffMin)}m ago`;
  if (diffHours < 24) return `${String(diffHours)}h ago`;
  return "1d+ ago";
}
var FLIGHT_PICKUP_ADJUST_THRESHOLD_MINUTES = 15;
var FLIGHT_PICKUP_ADJUST_ELIGIBLE_STATUSES = /* @__PURE__ */ new Set([
  FLIGHT_STATUS.SCHEDULED,
  FLIGHT_STATUS.DELAYED,
  FLIGHT_STATUS.DEPARTED
]);
function computeFlightPickupAdjustment(input) {
  const {
    flightStatus,
    scheduledArrival,
    estimatedArrival,
    currentPickupDatetime,
    lastAlignedArrival,
    now = /* @__PURE__ */ new Date()
  } = input;
  if (!FLIGHT_PICKUP_ADJUST_ELIGIBLE_STATUSES.has(flightStatus)) return null;
  if (!estimatedArrival) return null;
  const baseline = lastAlignedArrival ?? scheduledArrival;
  if (!baseline) return null;
  const baselineMs = new Date(baseline).getTime();
  const estimatedMs = new Date(estimatedArrival).getTime();
  const currentPickupMs = new Date(currentPickupDatetime).getTime();
  if (Number.isNaN(baselineMs) || Number.isNaN(estimatedMs) || Number.isNaN(currentPickupMs)) {
    return null;
  }
  const deltaMinutes = Math.round((estimatedMs - baselineMs) / 6e4);
  if (Math.abs(deltaMinutes) < FLIGHT_PICKUP_ADJUST_THRESHOLD_MINUTES) return null;
  const suggestedPickupMs = currentPickupMs + deltaMinutes * 6e4;
  if (suggestedPickupMs <= now.getTime()) return null;
  return {
    suggestedPickupDatetime: new Date(suggestedPickupMs).toISOString(),
    deltaMinutes,
    alignedToArrival: new Date(estimatedMs).toISOString()
  };
}
function reconcileOverdueWithFlight(input) {
  const {
    status,
    pickupDatetime,
    pickupMode = PICKUP_MODE.EXPLICIT,
    derivedPickupAt = null,
    noFlightFlag = false,
    flight = null,
    flightAdjustedForArrival = null,
    now = /* @__PURE__ */ new Date()
  } = input;
  if (pickupMode === PICKUP_MODE.FLIGHT_TRACKED) {
    const effective = resolveEffectivePickupAt({ pickupMode, pickupDatetime, derivedPickupAt });
    if (effective === null) {
      return {
        rawOverdue: false,
        overdue: false,
        waitingOnDelayedFlight: false,
        newEtaArrival: null,
        effectivePickupDatetime: ""
      };
    }
    const effectiveIso = typeof effective === "string" ? effective : effective.toISOString();
    const overdue = isOverdueUnstarted({ status, pickupDatetime: effective, now });
    return {
      rawOverdue: overdue,
      overdue,
      waitingOnDelayedFlight: false,
      newEtaArrival: null,
      effectivePickupDatetime: effectiveIso
    };
  }
  if (pickupDatetime === null) {
    return {
      rawOverdue: false,
      overdue: false,
      waitingOnDelayedFlight: false,
      newEtaArrival: null,
      effectivePickupDatetime: ""
    };
  }
  const pickupIso = typeof pickupDatetime === "string" ? pickupDatetime : pickupDatetime.toISOString();
  const rawOverdue = isOverdueUnstarted({ status, pickupDatetime, now });
  if (!rawOverdue) {
    return {
      rawOverdue: false,
      overdue: false,
      waitingOnDelayedFlight: false,
      newEtaArrival: null,
      effectivePickupDatetime: pickupIso
    };
  }
  if (noFlightFlag || !flight) {
    return {
      rawOverdue: true,
      overdue: true,
      waitingOnDelayedFlight: false,
      newEtaArrival: null,
      effectivePickupDatetime: pickupIso
    };
  }
  const adjustment = computeFlightPickupAdjustment({
    flightStatus: flight.status,
    scheduledArrival: flight.scheduledArrival,
    estimatedArrival: flight.estimatedArrival,
    currentPickupDatetime: pickupIso,
    lastAlignedArrival: flightAdjustedForArrival,
    now
  });
  if (adjustment) {
    return {
      rawOverdue: true,
      overdue: false,
      waitingOnDelayedFlight: true,
      newEtaArrival: flight.estimatedArrival,
      effectivePickupDatetime: adjustment.suggestedPickupDatetime
    };
  }
  return {
    rawOverdue: true,
    overdue: true,
    waitingOnDelayedFlight: false,
    newEtaArrival: null,
    effectivePickupDatetime: pickupIso
  };
}
var FLIGHT_ARRIVAL_LOOKUP_BUFFER_MS = 90 * 60 * 1e3;
var FLIGHT_DEPARTURE_LOOKUP_BUFFER_MS = 3 * 60 * 60 * 1e3;
var FLIGHT_LOOKUP_MISMATCH_THRESHOLD_MS = 20 * 60 * 60 * 1e3;
function resolveExpectedFlightReference(input) {
  const { pickupDatetime, pickupTimezone, subType } = input;
  const pickupMs = toEpochMs(pickupDatetime);
  const referenceMs = subType === RIDE_SUB_TYPES.ARRIVAL ? pickupMs - FLIGHT_ARRIVAL_LOOKUP_BUFFER_MS : subType === RIDE_SUB_TYPES.DEPARTURE ? pickupMs + FLIGHT_DEPARTURE_LOOKUP_BUFFER_MS : pickupMs;
  const referenceInstant = new Date(referenceMs);
  const local = instantToWallTime(referenceInstant, pickupTimezone);
  if (!local) throw new RangeError(`Invalid time zone specified: ${pickupTimezone}`);
  return { referenceInstant, expectedDate: local.date };
}
function isFlightInstantConsistentWithPickup(candidateInstant, referenceInstant, thresholdMs = FLIGHT_LOOKUP_MISMATCH_THRESHOLD_MS) {
  if (!candidateInstant) return true;
  return Math.abs(candidateInstant.getTime() - referenceInstant.getTime()) <= thresholdMs;
}
function getDriverDisplayState(input) {
  const { status, driverAcceptedAt, hasVehicle } = input;
  switch (status) {
    case RESERVATION_STATUS.CANCELLED:
      return { state: "cancelled", label: "Cancelled", action: "none" };
    case RESERVATION_STATUS.COMPLETED:
      return { state: "completed", label: "Completed", action: "none" };
    case RESERVATION_STATUS.IN_PROGRESS:
      return { state: "in_progress", label: "Trip in progress", action: "complete" };
    case RESERVATION_STATUS.ASSIGNED:
      if (!driverAcceptedAt) {
        return { state: "offered", label: "Ride request", action: "respond" };
      }
      return hasVehicle ? { state: "accepted", label: "Accepted", action: "start" } : { state: "needs_vehicle", label: "Needs vehicle", action: "pick_vehicle" };
    case RESERVATION_STATUS.PENDING:
      return { state: "unassigned", label: "Waiting on the company", action: "none" };
    case RESERVATION_STATUS.CONFIRMED:
      return { state: "unassigned", label: "Waiting for a driver", action: "none" };
    default:
      return { state: "completed", label: "Unknown", action: "none" };
  }
}
var RESERVED_SLUGS = [
  // System routes
  "admin",
  "api",
  "app",
  "auth",
  "book",
  "booking",
  "bookings",
  "dashboard",
  "login",
  "logout",
  "register",
  "signup",
  "signin",
  "settings",
  "account",
  "profile",
  "user",
  "users",
  "widget",
  "reservation",
  "invite",
  "verify",
  // Subdomains / infrastructure
  "www",
  "mail",
  "email",
  "ftp",
  "cdn",
  "assets",
  "static",
  "media",
  "images",
  "img",
  "files",
  "docs",
  "help",
  "support",
  "status",
  "blog",
  // Platform terms
  "ridereserv",
  "ride-reserv",
  "driver",
  "drivers",
  "company",
  "companies",
  "fleet",
  "platform",
  "public",
  "private",
  // Common words that might cause confusion
  "test",
  "demo",
  "example",
  "sample",
  "null",
  "undefined",
  "true",
  "false",
  "new",
  "edit",
  "delete",
  "create",
  "update"
];
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function isReservedSlug(slug) {
  return RESERVED_SLUGS.includes(slug.toLowerCase());
}
function validateCompanySlug(slug) {
  if (slug.length < 2) {
    return "Slug must be at least 2 characters";
  }
  if (slug.length > 100) {
    return "Slug must be 100 characters or less";
  }
  if (!/^[a-z0-9-]+$/.test(slug)) {
    return "Slug can only contain lowercase letters, numbers, and hyphens";
  }
  if (slug.startsWith("-") || slug.endsWith("-")) {
    return "Slug cannot start or end with a hyphen";
  }
  if (slug.includes("--")) {
    return "Slug cannot contain consecutive hyphens";
  }
  if (isReservedSlug(slug)) {
    return "This URL is reserved and cannot be used";
  }
  return null;
}
function formatPhone(phone) {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}
var DEFAULT_PHONE_COUNTRY = "US";
function createRegionDisplayNames() {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
}
var regionDisplayNames = createRegionDisplayNames();
function getPhoneCountryLabel(country) {
  return PHONE_COUNTRY_NAMES[country] ?? regionDisplayNames?.of(country) ?? country;
}
var PHONE_COUNTRY_OPTIONS = getCountries().map((country) => ({
  value: country,
  label: getPhoneCountryLabel(country),
  callingCode: `+${getCountryCallingCode(country)}`
})).sort((left, right) => left.label.localeCompare(right.label, "en"));
var PHONE_COUNTRY_SEARCH_INDEX = PHONE_COUNTRY_OPTIONS.map((option) => ({
  option,
  names: [option.label, ...PHONE_COUNTRY_SEARCH_ALIASES[option.value] ?? []].map(
    foldCountrySearchText
  ),
  code: foldCountrySearchText(option.value),
  callingDigits: option.callingCode.replace(/^\+/, "")
}));
var COUNTRY_MATCH_EXACT = 0;
var COUNTRY_MATCH_PREFIX = 1;
var COUNTRY_MATCH_CONTAINS = 2;
var COUNTRY_MATCH_NONE = 3;
function rankPhoneCountryMatch(entry, folded, callingDigits) {
  if (entry.code === folded || entry.names.some((name) => name === folded)) {
    return COUNTRY_MATCH_EXACT;
  }
  if (entry.names.some((name) => name.startsWith(folded))) return COUNTRY_MATCH_PREFIX;
  if (entry.names.some((name) => name.includes(folded)) || entry.code.includes(folded) || callingDigits !== "" && entry.callingDigits.startsWith(callingDigits)) {
    return COUNTRY_MATCH_CONTAINS;
  }
  return COUNTRY_MATCH_NONE;
}
function filterPhoneCountryOptions(query) {
  const trimmed = query.trim();
  if (!trimmed) return PHONE_COUNTRY_OPTIONS;
  const folded = foldCountrySearchText(stripLeadingArticle(trimmed));
  if (!folded) {
    return isPhonePunctuationOnly(trimmed) ? PHONE_COUNTRY_OPTIONS : [];
  }
  let callingDigits = /^\d+$/.test(folded) ? folded : "";
  if (callingDigits.length > 2 && callingDigits.startsWith("00")) {
    callingDigits = callingDigits.slice(2);
  }
  const matches = [];
  PHONE_COUNTRY_SEARCH_INDEX.forEach((entry, order) => {
    const rank = rankPhoneCountryMatch(entry, folded, callingDigits);
    if (rank !== COUNTRY_MATCH_NONE) matches.push({ rank, order, option: entry.option });
  });
  matches.sort((left, right) => left.rank - right.rank || left.order - right.order);
  return matches.map((match) => match.option);
}
function getPhoneCountryOption(country) {
  return PHONE_COUNTRY_OPTIONS.find((option) => option.value === country) ?? {
    value: country,
    label: getPhoneCountryLabel(country),
    callingCode: `+${getCountryCallingCode(country)}`
  };
}
function getPhoneCountryFlag(country) {
  const code = country.toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return country;
  return Array.from(code).map((letter) => String.fromCodePoint(127397 + letter.charCodeAt(0))).join("");
}
function getPhoneInputPlaceholder(country = DEFAULT_PHONE_COUNTRY) {
  const exampleNumber = getExampleNumber(country, phoneExamples);
  return exampleNumber?.formatNational() ?? "Phone number";
}
function formatPhoneInput(value, country = DEFAULT_PHONE_COUNTRY) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    return new AsYouType(country).input(trimmed);
  } catch {
    return value;
  }
}
function getPhoneDigits(value) {
  return value.replace(/\D/g, "");
}
function findPhoneDigitBeforeCursor(value, cursorIndex) {
  const boundedCursorIndex = Math.min(Math.max(cursorIndex, 0), value.length);
  for (let index = boundedCursorIndex - 1; index >= 0; index -= 1) {
    if (/\d/.test(value[index] ?? "")) return index;
  }
  for (let index = boundedCursorIndex; index < value.length; index += 1) {
    if (/\d/.test(value[index] ?? "")) return index;
  }
  return -1;
}
function preparePhoneInputDraft(rawValue, previousValue, cursorIndex = rawValue.length) {
  const normalizedPreviousValue = previousValue ?? "";
  if (rawValue.length >= normalizedPreviousValue.length) return rawValue;
  const rawDigits = getPhoneDigits(rawValue);
  if (rawDigits.length === 0 || rawDigits !== getPhoneDigits(normalizedPreviousValue)) {
    return rawValue;
  }
  const digitIndex = findPhoneDigitBeforeCursor(rawValue, cursorIndex);
  if (digitIndex === -1) return rawValue;
  return `${rawValue.slice(0, digitIndex)}${rawValue.slice(digitIndex + 1)}`;
}
function detectPhoneInputCountry(value, defaultCountry = DEFAULT_PHONE_COUNTRY) {
  if (!value.trim()) return null;
  try {
    const formatter = new AsYouType(defaultCountry);
    formatter.input(value.trim());
    return formatter.getCountry() ?? null;
  } catch {
    return null;
  }
}
function normalizePhoneToE164(value, defaultCountry = DEFAULT_PHONE_COUNTRY) {
  if (!value.trim()) return "";
  try {
    const parsed = parsePhoneNumberFromString(value.trim(), defaultCountry);
    if (parsed?.isPossible()) return parsed.number;
  } catch {
  }
  return "";
}
function isPossiblePhoneInput(value, defaultCountry = DEFAULT_PHONE_COUNTRY) {
  return normalizePhoneToE164(value, defaultCountry) !== "";
}
function formatPhoneForDisplay(value, preferredCountry = DEFAULT_PHONE_COUNTRY) {
  if (!value.trim()) return "";
  try {
    const parsed = parsePhoneNumberFromString(value.trim(), preferredCountry);
    if (!parsed?.isPossible()) return value;
    return parsed.country === preferredCountry ? parsed.formatNational() : parsed.formatInternational();
  } catch {
    return value;
  }
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function formatReservationStatus(status) {
  const statusLabels = {
    pending: "Pending",
    confirmed: "Confirmed",
    assigned: "Assigned",
    in_progress: "In Progress",
    completed: "Completed",
    cancelled: "Cancelled"
  };
  return statusLabels[status];
}
function formatCurrency(amount, currency = "USD", locale = "en-US") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  }).format(amount);
}
function centsToDollars(cents) {
  return cents / 100;
}
function dollarsToCents(dollars) {
  return Math.round(dollars * 100);
}
function hasRole(userRoles, ...requiredRoles) {
  return requiredRoles.some((role) => userRoles.includes(role));
}
function getPlatformRolePermissions(role) {
  const rolePermissions = {
    super_admin: [
      "view_all_data",
      "edit_users",
      "edit_companies",
      "verify_companies",
      "edit_reservations",
      "manage_subscriptions",
      "manage_platform_admins",
      "view_analytics",
      "access_logs"
    ],
    support: [
      "view_all_data",
      "edit_users",
      "edit_companies",
      "verify_companies",
      "edit_reservations"
    ],
    verifier: ["view_all_data", "verify_companies"],
    developer: ["view_all_data", "view_analytics", "access_logs"],
    read_only: ["view_all_data"]
  };
  const lookup = rolePermissions;
  return lookup[role] ?? [];
}
function hasPlatformPermission(admin, permission) {
  if (admin.permissions.includes(permission)) {
    return true;
  }
  const rolePermissions = getPlatformRolePermissions(admin.role);
  return rolePermissions.includes(permission);
}
function addressPart(source, key) {
  const value = source[key];
  return typeof value === "string" ? value.trim() : "";
}
function formatCompanyAddress(address) {
  if (typeof address === "string") {
    return address.trim();
  }
  if (address === null || typeof address !== "object" || Array.isArray(address)) {
    return "";
  }
  const source = address;
  const parts = [];
  const line1 = addressPart(source, "addressLine1");
  const line2 = addressPart(source, "addressLine2");
  if (line1) parts.push(line1);
  if (line2) parts.push(line2);
  const city = addressPart(source, "city");
  const stateOrRegion = addressPart(source, "stateOrRegion");
  const postalCode = addressPart(source, "postalCode");
  const stateZip = [stateOrRegion, postalCode].filter(Boolean).join(" ");
  const locality = [city, stateZip].filter(Boolean).join(", ");
  if (locality) parts.push(locality);
  const country = addressPart(source, "country");
  if (country) parts.push(country);
  return parts.join(", ");
}
var STRUCTURED_ADDRESS_KEYS = [
  "addressLine1",
  "addressLine2",
  "city",
  "stateOrRegion",
  "postalCode",
  "country"
];
function toStructuredCompanyAddress(address) {
  if (address === null || typeof address !== "object" || Array.isArray(address)) {
    return null;
  }
  const source = address;
  const structured = {};
  for (const key of STRUCTURED_ADDRESS_KEYS) {
    const value = addressPart(source, key);
    if (value) structured[key] = value;
  }
  return Object.keys(structured).length > 0 ? structured : null;
}

export { AIRPORT_STATUS_LEVEL_LABEL, AIRPORT_STATUS_STALE_MS, AUTH_TRANSPORT_ERROR_MESSAGE, CAP_DEFAULTS, COMPLETE_WITHOUT_START_WARNING, DEFAULT_PHONE_COUNTRY, DRIVER_START_LEAD_MINUTES, EARLY_START_NOTATION_THRESHOLD_MINUTES, EMPTY_RESERVATION_LOCATION_LABEL, FLAG_DEFAULTS, FLIGHT_ARRIVAL_LOOKUP_BUFFER_MS, FLIGHT_BOARD_STALE_THRESHOLD_MINUTES, FLIGHT_BOARD_STATUS_ROLE_CLASS, FLIGHT_DEPARTURE_LOOKUP_BUFFER_MS, FLIGHT_LOOKUP_MISMATCH_THRESHOLD_MS, FLIGHT_PICKUP_ADJUST_THRESHOLD_MINUTES, LATE_START_NOTATION_THRESHOLD_MINUTES, MATERIAL_RESERVATION_EDIT_FIELDS, NONEXISTENT_WALL_TIME_MESSAGE, NON_MATERIAL_RESERVATION_EDIT_FIELDS, OPERATIONAL_RESERVATION_EDIT_FIELDS, PHONE_CODE_RESEND_COOLDOWN_SECONDS, PHONE_CODE_RESENT_NOTICE, PHONE_CODE_SENT_NOTICE, PHONE_COUNTRY_NAMES, PHONE_COUNTRY_OPTIONS, PHONE_COUNTRY_SEARCH_ALIASES, QUOTE_EXPIRY_URGENT_HOURS, RESERVATION_ACTION_CAPABILITY_KEYS, RESERVATION_ACTION_FALLBACK_DURATION_MINUTES, RESERVATION_ACTION_OPERATOR_REVIEW_HOURS, RESERVATION_ACTION_REASON_CODES, RESERVATION_ACTION_START_PROMINENCE_HOURS, RESERVATION_ACTION_VEHICLE_ADVISORIES, RESERVATION_EDIT_FIELD_MATERIALITY, RESERVATION_ISSUE_ATTENTION_BADGE_CAP, RESERVATION_ISSUE_CATEGORY_LABELS, RESERVATION_ISSUE_SEVERITY_LABELS, RESERVATION_ISSUE_STATUS_LABELS, RESERVATION_UPDATE_FIELD_SCOPE, RESERVED_SLUGS, STALE_DISPATCH_PREVIEW_REASON, STALE_RESERVATION_MESSAGE, TIER_FEATURE_GROUPS, UPCOMING_GRACE_MS, VEHICLE_CAPABILITY_STATUS_OPTIONS, VEHICLE_CAPABILITY_UNKNOWN_HINT, airportStatusLevelRole, airportStatusUpdatedAgoLabel, associatedPickupCoords, authCodeVerifyErrorMessage, authPasswordUpdateErrorMessage, authReauthErrorMessage, authSendErrorMessage, authSignInErrorMessage, authSignUpErrorMessage, authThrottleRetrySeconds, bookingPagePath, buildBookingUrl, buildTier, canResubmitReviewedRow, capacityWarning, capitalize, centsToDollars, classifyReservationEditMateriality, cn, computeFlightPickupAdjustment, computeHasAirportEndpoint, correlateWindows, csvFilename, deriveFlightTrackedPickupAt, deriveLocationsFromStops, deriveOperationalEditPickupAnchor, deriveReservationActionPresentation, deriveReservationCancellationOutcome, deriveReservationIssueSeverity, deriveReservationIssueState, deriveSoloVehicleEvidenceNudge, detectPhoneInputCountry, dispatchPlanBlockerLabel, dispatchPlanWarningLabel, displayReservationLocation, dollarsToCents, driverStatusLabel, effectivePickupDate, effectivePickupInstant, endOfDayInZone, escapeCsvCell, estimateDriveTimeMinutes, evaluateBookingWindow, evaluateReservationActionCapabilities, evaluateReservationActionTiming, evaluateReservationIssueCategoryEligibility, expandRecurringAvailability, expandWeeklyAvailabilityInZone, filterPhoneCountryOptions, flightBoardGateLabel, flightBoardStatusLabel, flightBoardStatusRole, flightGlanceStatusPresentation, foldCountrySearchText, formatCompanyAddress, formatCurrency, formatDriverStartAvailability, formatEarlyStartContext, formatFixAge, formatFlightBoardTime, formatFlightDelayLabel, formatInZone, formatIssueAttentionBadge, formatLateCancellationPolicyLine, formatLateStartContext, formatPhone, formatPhoneForDisplay, formatPhoneInput, formatPickupDay, formatPickupLocalInstant, formatPickupTime, formatReservationRoute, formatReservationStatus, friendlyDriverError, generateGeocodingSessionToken, getDriverDisplayState, getDriverStartEligibility, getEarlyStartContext, getLateStartContext, getPhoneCountryFlag, getPhoneCountryOption, getPhoneInputPlaceholder, getPlatformRolePermissions, getQuoteExpiryStatus, hasNoFlightWarning, hasPlatformPermission, hasReservationLocation, hasRole, haversineDistanceMiles, initialsFor, instantToWallTime, isActiveUpcoming, isAirportStatusStale, isAuthThrottleError, isDispatchSuggestable, isExpiredRideOffer, isFlightInstantConsistentWithPickup, isLateCancellationPolicyAlwaysFree, isNearAirport, isNonexistentWallTime, isOverdueUnstarted, isPhonePunctuationOnly, isPossiblePhoneInput, isReservedSlug, isStaleDispatchPreviewFailure, isStaleReservationConflict, isWindowWithinWeeklyAvailability, isWithinLateCancelWindow, lateCancelBasisPickupAt, lateCancelFeePolicyAppliesToMode, normalizePhoneToE164, oldestFlightBoardUpdateMinutesAgo, orderWaypointsForPricing, parseExactNonNegativeMoney, preparePhoneInputDraft, projectWallWindowsToDaySegments, projectWeeklyAvailabilityDate, projectWindowToDaySegments, readVehicleCapabilityStatus, reconcileOverdueWithFlight, reportableIssueCategories, requirementChips, requiresLateCancelAcknowledgement, reservationVehicleAdvisoryPresentation, resolveEffectivePickupAt, resolveExpectedFlightReference, rowsToCsv, safePhotoUrl, sanitizeOperationalEditPatch, slugify, soloVehicleEvidenceNudgeMessage, sortByPickup, startOfDayInZone, stripLeadingArticle, suggestionEtaLabel, suggestionWarning, tilePhotoUrl, toFlightChipStatus, toISOString, toStructuredCompanyAddress, truncate, utcToLocal, validateCompanySlug, vehicleCapabilityEvidence, vehicleCapabilityMarks, vehicleFitWarnings, visibleSpecialNotes, wallTimeToInstant, weekdayKeyForDate, withExpectedUpdatedAt, zoneOffsetMinutes };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map